import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, RefreshCw, Loader2, ArrowLeftRight, ArrowUpDown, Lock, Unlock, Ban, Undo2, ClipboardList, Calculator, Printer, ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { api, getToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useSocket } from '../../lib/socket';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { useToast } from '../../components/Toast';
import { useAviso } from '../../components/Aviso';
import { useDialog } from '../../components/DialogProvider';
import { Modal } from '../../components/Modal';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import { formatMoeda, formatMoedaSemSimbolo, formatDataSP, formatDataHoraSP, formatHoraSP, parseSPDate, parseValorBusca, podeDestravarPedido, motivoTravaValido, mensagemPixVinculadoSoAdmin, confirmacaoSoltarPixAoVoltar, pixEsperadoDoPedido, formatCpfCnpj, TOLERANCIA_CENTAVO_CALIBRACAO as TOL_CENTAVO } from '@fiodecobre/shared';
import { ModalFinalizarVenda } from '../../components/caixa/ModalFinalizarVenda';
import { ModalResultadoEmissao, type ResultadoEmissaoData } from '../../components/orcamento/ModalResultadoEmissao';
import { TurnoModal } from './TurnoModal';
import { ConciliacaoBancariaTab } from './ConciliacaoBancariaTab';
import { ConfirmacaoPendenteTab } from './ConfirmacaoPendenteTab';
import { ConciliacaoPagamentosTab } from './ConciliacaoPagamentosTab';
import { ModalReceberAvulso } from '../../components/caixa/ModalReceberAvulso';
import { ModalMovimentacaoCaixa, ModalTransferenciaCaixa } from '../../components/caixa/ModaisMovimentacaoCaixa';
import { ModalMoverPagamento, type PagamentoMovivel } from '../../components/caixa/ModalMoverPagamento';
import { ModalReciboTroca, type ReciboTrocaInfo } from '../../components/trocas/ModalReciboTroca';
import { ModalResultadoDevolucao, type ResultadoDevolucao } from '../../components/notas-fiscais/ModalResultadoDevolucao';
import { FotoProduto } from '../../components/FotoProduto';
import { esperarNotaAutorizada, mapearNotaAutorizada, type NotaDoOrcamento } from './nota-do-cerco';
import { marcarNotaPendente, lerNotaPendente, limparNotaPendente } from './nota-pendente';

interface PedidoCaixa {
  id: number;
  numero: string;
  cliente_id: number;
  cliente_nome: string;
  cliente_codigo: string | null;
  cliente_doc: string | null;
  cliente_whatsapp: string | null;
  // Soma dos titulos de crediario em aberto do cliente (mesma conta do extrato do cliente).
  cliente_em_aberto?: string | number | null;
  total: string;
  subtotal: string;
  subtotal_bruto: string;
  qtd_itens: string;
  desconto_total_valor: string;
  desconto_total_percentual: string;
  tipo_documento: 'nfce' | 'nfe' | 'troca' | null;
  cpf_nota: string | null;
  vendedor_id: number | null;
  vendedor_nome: string | null;
  forma_pagamento_nome: string | null;
  forma_pagamento_id: number | null;
  comprador: string | null;
  atualizado_em: string;
  // EMISSAO do orcamento (quando o pedido nasceu). Diferente do `atualizado_em` acima, que
  // e o que a coluna DATA exibe e se move a cada edicao.
  criado_em: string;
  // 🔑 Quando o pedido foi PRO CAIXA — a ancora do vencimento do crediario desde 27/08/2026
  // ([DA-631]): *"ao finalizar, os 15 dias devem contar a partir dela"*. Era `criado_em`
  // ([DA-335]). ⚠️ `null` em pedido enviado ao caixa ANTES da migration 0257 (o carimbo nao
  // existia e nao foi inventado) — quem calcula cai no `criado_em`.
  enviado_caixa_em: string | null;
  // null = cliente sem whatsapp; true = mensagem inbound recebida nas ultimas 24h
  // (janela aberta pro envio livre); false = janela fechada (so templates)
  whatsapp_janela_aberta: boolean | null;
  // Pix: valor declarado em orcamento_pagamentos (forma tipo='pix'), valor já vinculado a mp_recebimentos
  valor_pix_declarado: string | null;
  valor_pix_vinculado: string | null;
  // Credito da Loja abatido neste pedido (soma das formas tipo='credito_loja' em orcamento_pagamentos).
  // O total cheio menos esse credito = dinheiro que o cliente ainda paga no caixa.
  valor_credito_loja: string | null;
  // Formas do pedido com o valor de cada uma — so vem com 2+ formas (venda mista). Alimenta a
  // coluna Forma de Pagamento, que antes nomeava so a forma principal.
  composicao?: { forma: string; tipo: string; valor: string | number }[] | null;
  pix_validado_admin: boolean;
  // Pedido calibrado (DA-233): o total congelou com centavo != 0 pro Pix casar sozinho. Quando a
  // venda fecha SEM Pix (dinheiro/cartao), o ModalFinalizarVenda descalibra ao vivo pra gaveta nao
  // esperar um valor impossivel de contar em especie (ponta solta #1 do caixa).
  requer_valor_unico: boolean;
  // Valor da LINHA de Pix congelada (DA-320). Num pedido que voltou de venda cancelada e a unica
  // memoria de quanto era o Pix — o cancelamento apaga `orcamento_pagamentos` e sobra o vinculo.
  valor_pix_unico: string | null;
  // Trava manual do pedido (DA-399, cartao RDS caixa #12): pedido que depende de um ajuste ainda
  // nao resolvido. Travado nao finaliza, nao volta pro orcamento e nao e excluido. O login de quem
  // travou vem junto porque a regra de quem destrava depende dele (trava do dono e pessoal).
  travado: boolean;
  travado_motivo: string | null;
  travado_em: string | null;
  travado_por: number | null;
  travado_por_nome: string | null;
  travado_por_login: string | null;
  // Numero da venda CANCELADA que devolveu este pedido pra fila (null = pedido novo). Cancelar
  // reabre o pedido (DA-280) e ele volta indistinguivel de um novo — este campo e' o que deixa a
  // fila avisar. Refinalizar passa a ser so-admin; o backend barra, isto aqui e' pra ninguem
  // chegar no 403 sem entender.
  venda_cancelada_numero: string | null;
  // [DA-764] Voltou ao orcamento e foi reenviado DEPOIS do cancelamento: a tarja vira so aviso e a
  // operadora finaliza. Sem isso (voltou direto pela porta do cancelar) segue so-admin.
  venda_cancelada_reenviada?: boolean;
  // Selo "Cliente de obra" do cadastro (DA-415, cartao RDS 12.4). HABILITA o botao "Venda CM" no
  // ModalFinalizarVenda — nao determina: o mesmo cliente as vezes paga a vista. Vem das DUAS rotas
  // que servem esta interface (`GET /caixa/pedidos` e `/pedidos/:id`).
  cliente_consignacao: boolean | null;
  // Trava do modelo da nota (migration 0263): cliente que so aceita NF-e A4. Diferente do selo
  // acima, esta DETERMINA — o botao "Nota Consumidor" nem aparece. Vem das DUAS rotas tambem.
  cliente_travar_nota_a4: boolean | null;
  // Programa do Prestador (DA-435): quem GANHA o credito desta venda — pode nao ser quem paga.
  // Preenchido => o caixa esconde Crediario e Credito da Loja e mostra a previa do premio.
  // Vem das DUAS rotas que servem esta interface, igual ao selo acima.
  prestador_id: number | null;
  prestador_nome: string | null;
}

// Cotacao da maquininha pro pedido de cartao PARCELADO (DA-419). `venda` e o preco da venda (o
// cheio, ja com o rebase do cartao) e `total_cartao` e o que o cliente paga em N parcelas — a
// diferenca entre os dois e o juros + imposto do parcelamento (DA-140).
interface ValorCartaoPedido {
  id: number;
  parcelas: number;
  venda: number;
  total_cartao: number;
}

interface CaixaConta {
  id: number;
  nome: string;
  tipo: string;
  exige_conferencia: boolean;
  ordem: number;
  // Dono da gaveta (DA-126). Preenchido = gaveta de gente (fecha SEMPRE contando); NULL = caixa
  // compartilhado da loja (Movimento de Caixa), que pode nao ter cedula pra contar. E o unico
  // campo que separa os dois — `exige_conferencia` nao serve, o Movimento tambem nasce TRUE
  // no seed da 0014. Ja vinha do backend (`/contas` faz SELECT *); faltava declarar aqui.
  usuario_id: number | null;
  // Saldo pela regua da TRAVA ([DA-552]) — gaveta pelo turno, conta de banco pelo acumulado.
  // Mesmo numero do /financeiro e da trava, pela mesma funcao no servidor.
  saldo: number;
  formas_pagamento: Array<{ id: number; tipo: string; nome: string; permite_desconto: boolean }>;
}

interface SessaoHoje {
  id: number;
  caixa_id: number;
  caixa_nome: string;
  caixa_tipo: string;
  exige_conferencia: boolean;
  data: string;
  valor_abertura: string;
  valor_fechamento: string | null;
  status: string;
  aberto_em: string;
  fechado_em: string | null;
  operador_abertura_nome: string | null;
  operador_fechamento_nome: string | null;
  valor_esperado_fechamento: string | null;
  divergencia: string | null;
  sucessor_nome: string | null;
  operador_abertura_id: number | null;
  abertura_confirmada_em: string | null;
  abertura_confirmada_por_nome: string | null;
  divergencia_abertura: string | null;
  divergencia_abertura_resolvida_em: string | null;
  // Turno de dinheiro ABERTO: recebido no turno + esperado na gaveta agora (ao vivo,
  // mesma conta do fechamento). Vem preenchido so pra dinheiro aberto.
  recebido_turno?: number | null;
  esperado_atual?: number | null;
}

interface VendaHoje {
  id: number;
  numero: string;
  cliente_nome: string;
  cliente_codigo: string | null;
  total: string;
  desconto_total: string | null;
  status: string;
  finalizada_em: string;
  tipo_documento: 'nfce' | 'nfe' | 'troca' | null;
  operador_nome: string | null;
  vendedor_nome: string | null;
  // Orcamento que originou a venda (ORC-NNNNNN) — exibido sob o cliente pra rastrear VND -> ORC.
  orcamento_numero?: string | null;
  // `pagamento_id`/`forma_pagamento_id`/`caixa_id` sao a identidade da LINHA de pagamento — e ela
  // que a porta "Corrigir caixa" move (DA-351). Sem isso, venda mista nao diz qual linha errou.
  pagamentos: Array<{ forma: string; valor: string; tipo: string; parcelas?: number | null; acrescimo?: string | null; conta?: string | null; pagamento_id?: number; forma_pagamento_id?: number; caixa_id?: number | null }> | null;
  pix_declarado: string | null;
  pix_vinculado: string | null;
  pix_validado_admin: boolean | null;
  pix_conciliado: string | null;
  // Cartao (debito/credito): valor declarado vs. valor ja conciliado manualmente (conciliacoes_manuais).
  cartao_declarado: string | null;
  cartao_conciliado: string | null;
  tipo_principal: string | null;
  // Cobranças Pix PAGAS desta venda (via orcamento) — habilitam reimprimir o comprovante da
  // transação na lista. O QR estático cai sempre na conta Mercado Pago. null/[] = sem comprovante.
  comprovantes_pix: Array<{ cobranca_id: number; valor: string }> | null;
  // Nota autorizada + idade CALCULADA NO SERVIDOR ([DA-442]). Decide se o modal de desfazer
  // oferece cancelar (dentro do prazo da SEFAZ) ou a devolucao (fora). null = venda sem nota.
  nota_desfazer: {
    modelo: 'nfce' | 'nfe';
    nota_id: number;
    numero: number;
    emitido_em: string;
    minutos: number;
    limite_minutos: number;
    expirado: boolean;
    ja_devolvida: boolean;
  } | null;
}

// "1h52" / "45 min" — a idade da nota escrita como gente fala. O numero vem pronto do servidor
// (nunca de Date.now(): o relogio do PC da loja diverge do da VPS e a conta sai negativa).
function idadeLegivel(minutos: number): string {
  if (minutos < 60) return `${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

// Credito da Loja vira vDesc na nota (DA-049): o valor que importa — o da NOTA e o dinheiro real
// recebido — e total - credito, NAO o total cheio. Ex.: venda 48 paga com 44 de credito + 4 em
// dinheiro sai na nota por 4. As telas de caixa exibem esse liquido (ModalFinalizarVenda ja fazia).
function creditoLojaDa(v: VendaHoje): number {
  return (v.pagamentos || []).reduce((s, p) => (p.tipo === 'credito_loja' ? s + Number(p.valor || 0) : s), 0);
}
function totalLiquidoVenda(v: VendaHoje): number {
  return Number(v.total || 0) - creditoLojaDa(v);
}
// Acrescimo do parcelamento no cartao (juros+imposto, DA-140): fica fora da venda/comissao
// e so entra no valor REAL da nota (vOutro). Somado entre os pagamentos da venda.
function acrescimoVenda(v: VendaHoje): number {
  return (v.pagamentos || []).reduce((s, p) => s + Number(p.acrescimo || 0), 0);
}
// Valor REAL cobrado / da nota = liquido da venda + acrescimo do parcelamento.
// Bate com o vNF do fiscal (totalEfetivo + acrescimoTotal). E o valor PRINCIPAL de exibicao;
// o liquido (base pix) so serve de registro pra comissao.
function valorRealVenda(v: VendaHoje): number {
  return totalLiquidoVenda(v) + acrescimoVenda(v);
}

interface ResumoDia {
  data: string;
  vendas: { total: string; qtd: string };
  por_caixa: Array<{ id: number; nome: string; total: string }>;
  por_forma_pagamento: Array<{ id: number; nome: string; tipo: string; total: string }>;
  contas_receber_pendentes: { qtd: string; total: string };
}

type Aba = 'pedidos' | 'vendas' | 'sessoes' | 'transferencias' | 'fluxo' | 'recebimentos' | 'resumo' | 'turnos' | 'conciliacao' | 'conciliacao_pgto' | 'confirmacao';

interface TransferenciaPendente {
  id: number;
  valor: string;
  motivo: string | null;
  status: 'pendente' | 'confirmada';
  criado_em: string;
  confirmada_em: string | null;
  operador_id: number | null;
  caixa_origem_id: number;
  caixa_destino_id: number;
  origem_nome: string;
  destino_nome: string;
  operador_nome: string | null;
  confirmada_por_nome: string | null;
}

interface TurnoRel {
  id: number;
  caixa_id: number;
  caixa_nome: string;
  caixa_tipo: string;
  data: string;
  status: string;
  valor_abertura: string;
  valor_fechamento: string | null;
  valor_esperado_fechamento: string | null;
  divergencia: string | null;
  aberto_em: string;
  fechado_em: string | null;
  operador_abertura_nome: string | null;
  operador_fechamento_nome: string | null;
  sucessor_nome: string | null;
  abertura_confirmada_em: string | null;
  divergencia_abertura: string | null;
}
interface TurnosResp {
  totais: { qtd: number; qtd_com_divergencia: number; faltou: number; sobrou: number };
  turnos: TurnoRel[];
}

interface FluxoLinha {
  documento: string;
  historico: string;
  cliente_codigo: string | null;
  // So nas linhas de VENDA: o orcamento que a originou, exibido sob o cliente.
  orcamento_numero?: string | null;
  data: string;
  especie: string;
  caixa_nome: string | null;
  responsavel: string | null;
  vendedor: string | null;
  entrada: string | number;
  saida: string | number;
  ordem_ts: string;
  tipo_nf: string | null;
  // De onde a linha veio ([DA-466]). `venda`/`cr` sao RECEBIMENTO (dinheiro que o cliente pagou);
  // `mov` e sangria/suprimento e `cp` e conta paga — nenhum dos dois e faturamento.
  origem?: 'venda' | 'cr' | 'cp' | 'mov' | null;
  parcelas?: number | null;
  acrescimo?: string | number | null;
  // Taxa e liquido REAIS da maquininha ([DA-452]) — Point (Mercado Pago) ou PagBank. null = conta
  // sem captura (InfinitePay/BB): a tela nao afirma taxa que nao mediu.
  taxa_maquininha?: string | number | null;
  liquido_maquininha?: string | number | null;
  // So nas linhas de venda: quantos recebimentos a venda teve NO TODO e o total dela.
  // Buscando por valor volta so a perna que casou — e por aqui que a tela sabe que ela e
  // pedaco de uma venda maior.
  venda_total?: string | number | null;
  pernas_venda?: number | null;
  // Composicao da venda ([DA-459]): TODAS as pernas, inclusive credito da loja e crediario, que
  // o Fluxo nao soma ([DA-451]). Vem so quando a venda tem 2+ pagamentos; null nos demais casos.
  composicao?: PernaComposicao[] | null;
}
interface PernaComposicao {
  forma: string;
  tipo: string;
  conta: string | null;
  valor: string | number;
}
interface FluxoResp {
  totalEntradas: number; totalSaidas: number; saldo: number; linhas: FluxoLinha[];
  // Presentes so na busca por valor (ver GET /api/caixa/fluxo).
  buscando?: boolean; truncado?: boolean; encontrados?: number;
}

export function CaixaPainel() {
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario } = useAuth();
  const toast = useToast();
  const aviso = useAviso();
  const dialog = useDialog();
  const sticky = useStickyHeader();
  // Espelha exigirOperaCaixa do backend (DA-384): admin/gerente/caixa OU opera_caixa OU
  // vende_sem_turno ("Ajuda no caixa"). Sem o vende_sem_turno a tela travava o duplo-clique
  // de quem o servidor ja deixava finalizar (JOAO tomava tooltip "Sem permissao" indevida).
  const podeOperar = ['admin', 'gerente', 'caixa'].includes(usuario?.perfil ?? '')
    || usuario?.opera_caixa === true || usuario?.vende_sem_turno === true;
  // Cancelar venda = só-ADMIN (DA-280): cancela NF na SEFAZ + reabre o pedido, operação pesada e
  // sensível. Sem 'gerente' (papel inexistente hoje) pra não abrir brecha de privilégio futura.
  const podeCancelar = usuario?.perfil === 'admin';
  // Porta de "desfazer" no Caixa ([DA-442]). O 🚫 e do CANCELAR, que e so admin — mas a devolucao
  // aceita admin/gerente, e sem isto a gerente nao alcancava a unica saida da venda fora do prazo
  // pelo Caixa (so por /notas-fiscais), justo ela que opera o caso. Entao a gerente ve o botao
  // APENAS quando a linha ja esta em modo devolucao; deixar ela abrir o modo cancelar so trocaria
  // "sem botao" por "botao que estoura 403" (trava de front nao pode prometer mais que o backend).
  const podeDesfazerLinha = useCallback((v: VendaHoje): boolean => {
    if (v.status !== 'finalizada') return false;
    if (podeCancelar) return true;
    if (usuario?.perfil !== 'gerente') return false;
    return !!v.nota_desfazer?.expirado && !v.nota_desfazer.ja_devolvida;
  }, [podeCancelar, usuario?.perfil]);
  const podeVerTurnos = ['admin', 'gerente'].includes(usuario?.perfil ?? '');

  // Abre numa aba especifica quando navegado com state (ex.: faixa global de conciliacao
  // leva pra 'conciliacao'). Default: 'pedidos'. Compat: state antigo recebimentos-pix/cartao
  // (banner/links antigos) cai na aba unificada.
  const abaRaw = (location.state as { aba?: string } | null)?.aba;
  const abaInicial: Aba = (abaRaw === 'recebimentos-pix' || abaRaw === 'recebimentos-cartao')
    ? 'conciliacao'
    : ((abaRaw as Aba) ?? 'pedidos');
  const [aba, setAba] = useState<Aba>(abaInicial);
  // ...e OBEDECE o state a cada chegada, CONSUMINDO ele em seguida (DA-420, receita da DA-377).
  // Lido so na inicializacao, o mesmo state produzia dois defeitos opostos: (a) clicar a faixa de
  // conciliacao ESTANDO no /caixa nao fazia nada — a tela nao remonta, entao ninguem relia o state
  // (vale tambem pro "Vincular na Conciliacao", que nasce dentro da propria tela); e (b) depois
  // daquele clique, TODO F5 do /caixa abria na Conciliacao Bancaria pra sempre, porque o state
  // GRUDA no history entry e sobrevive ao reload. Limpar na hora fecha os dois.
  useEffect(() => {
    const raw = (location.state as { aba?: string } | null)?.aba;
    if (!raw) return;
    setAba((raw === 'recebimentos-pix' || raw === 'recebimentos-cartao') ? 'conciliacao' : (raw as Aba));
    navigate(location.pathname, { replace: true, state: null });
  }, [location.key, location.state, location.pathname, navigate]);
  // "Receber Avulso" (na aba Pedidos pra finalizar): recebe um valor livre amarrado ao cliente, FORA
  // de uma venda, em qualquer das 4 formas (dinheiro/Pix/crédito/débito). O dinheiro cai livre na conta
  // certa e é abatido depois nos títulos em Contas a Receber. Dinheiro está sempre disponível; o modal
  // sozinho desabilita Pix/cartão quando indisponíveis. Generaliza o antigo "Cobrar Pix Avulso".
  const [receberAvulso, setReceberAvulso] = useState(false);
  const [pedidos, setPedidos] = useState<PedidoCaixa[]>([]);
  // Valor que a maquininha vai cobrar num pedido de cartao PARCELADO (DA-419). Vem numa 2a chamada,
  // depois da lista, porque cotar na PagBank e I/O externa — a lista nao pode esperar por ela. Falhou,
  // o mapa fica sem aquele pedido e a coluna Total continua mostrando o preco da venda.
  const [valorCartao, setValorCartao] = useState<Record<number, ValorCartaoPedido>>({});
  const [contas, setContas] = useState<CaixaConta[]>([]);
  const [sessoes, setSessoes] = useState<SessaoHoje[]>([]);
  const [vendas, setVendas] = useState<VendaHoje[]>([]);
  const [resumo, setResumo] = useState<ResumoDia | null>(null);
  const [transferencias, setTransferencias] = useState<TransferenciaPendente[]>([]);
  // Filtro da aba Transferencias mora no PAI: a Tab e desmontada sempre que o "Carregando..." entra
  // (troca de aba, botao Atualizar) e levaria o filtro junto.
  const [transfFiltro, setTransfFiltro] = useState({ de: '', ate: '', origem: '' });
  const [loading, setLoading] = useState(true);
  // Caixa de dinheiro com turno aberto hoje para a operadora logada (null = sem turno aberto).
  // undefined = ainda carregando. Quando null, operadora pode abrir turno em qualquer caixa de dinheiro.
  const [meuCaixa, setMeuCaixa] = useState<{ id: number; nome: string } | null | undefined>(undefined);

  // Fluxo de Caixa
  const hojeStr = new Date().toISOString().slice(0, 10);
  const [fluxoInicio, setFluxoInicio] = useState<string>(hojeStr);
  const [fluxoFim, setFluxoFim] = useState<string>(hojeStr);
  const [fluxoCaixaId, setFluxoCaixaId] = useState<number | ''>('');
  const [fluxo, setFluxo] = useState<FluxoResp | null>(null);
  const [fluxoLoading, setFluxoLoading] = useState(false);
  const fluxoReqId = useRef(0);
  // Busca por valor: o termo digitado so vira consulta 400ms depois de parar de digitar.
  // Sem o debounce cada tecla dispararia a varredura do historico inteiro (a busca ignora
  // o periodo de proposito). O `carregarFluxo` le o APLICADO, nunca o que esta sendo digitado.
  const [fluxoValor, setFluxoValor] = useState<string>('');
  const [fluxoValorAplicado, setFluxoValorAplicado] = useState<string>('');
  useEffect(() => {
    const t = setTimeout(() => setFluxoValorAplicado(fluxoValor.trim()), 400);
    return () => clearTimeout(t);
  }, [fluxoValor]);

  // Relatorio de turnos (Onda 3)
  const [turnosInicio, setTurnosInicio] = useState<string>(hojeStr);
  const [turnosFim, setTurnosFim] = useState<string>(hojeStr);
  const [turnosCaixaId, setTurnosCaixaId] = useState<number | ''>('');
  const [turnosRel, setTurnosRel] = useState<TurnosResp | null>(null);
  const [turnosLoading, setTurnosLoading] = useState(false);

  // Expand de vendas por caixa (aba Sessoes)
  const [sessoesExpandidas, setSessoesExpandidas] = useState<Set<number>>(() => new Set());
  const [vendasPorCaixa, setVendasPorCaixa] = useState<Record<number, VendaHoje[] | null>>({});

  async function toggleSessaoExpand(caixaId: number) {
    setSessoesExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(caixaId)) {
        next.delete(caixaId);
      } else {
        next.add(caixaId);
        if (vendasPorCaixa[caixaId] === undefined) {
          setVendasPorCaixa((p) => ({ ...p, [caixaId]: null }));
          api.get<VendaHoje[]>('/api/caixa/vendas-hoje', { query: { caixa_id: caixaId } })
            .then((r) => setVendasPorCaixa((p) => ({ ...p, [caixaId]: r })))
            .catch(() => setVendasPorCaixa((p) => ({ ...p, [caixaId]: [] })));
        }
      }
      return next;
    });
  }

  // Modais
  const [pedidoFinalizar, setPedidoFinalizar] = useState<PedidoCaixa | null>(null);
  const [resultadoEmissao, setResultadoEmissao] = useState<{ tipo: 'nfce' | 'nfe'; orcamentoId: number; data: ResultadoEmissaoData; clienteWhatsapp?: string | null; clienteNome?: string | null; clienteId?: number | null; valorTotal?: string | number | null } | null>(null);
  // A venda cuja nota esta saindo AGORA. [DA-599] Enquanto isto nao e' null a tela mostra um aviso
  // por cima da fila: o modal de finalizar ja fechou, e sem este sinal a emissao acontece com a
  // tela IDENTICA a "nao aconteceu nada" — foi o que produziu o relato de 21/08/2026, quando uma
  // emissao levou 11 segundos em vez dos 0,4s de rotina.
  const [emitindoNota, setEmitindoNota] = useState<string | null>(null);
  // Porta unica de entrada/saida — os botoes separados Sangria/Suprimento viraram um so, e a
  // direcao e escolhida DENTRO do modal (15/08/2026).
  const [showMovimentacao, setShowMovimentacao] = useState(false);
  const [showTransferencia, setShowTransferencia] = useState(false);
  const [turno, setTurno] = useState<{ modo: 'abrir' | 'fechar' | 'confirmar' | 'conferir'; caixa: { id: number; nome: string }; sessaoId?: number } | null>(null);
  // A trava anti-negativo (modal de saldo insuficiente) agora e tratada DENTRO de
  // ModaisMovimentacaoCaixa, junto das portas que a disparam.
  const [conferirRecebimento, setConferirRecebimento] = useState<{ sessaoId: number; caixaNome: string } | null>(null);
  const [extratoTurno, setExtratoTurno] = useState<{ sessaoId: number; caixaNome: string } | null>(null);
  const [cancelarVenda, setCancelarVenda] = useState<VendaHoje | null>(null);
  const [reciboTroca, setReciboTroca] = useState<ReciboTrocaInfo | null>(null);
  const [motivoCancelar, setMotivoCancelar] = useState('');
  // Trava o botao durante a ida a SEFAZ ([DA-442]). A devolucao leva segundos (SOAP + assinatura)
  // e sem isso o botao segue clicavel sem sinal — dois cliques = duas notas de entrada.
  const [desfazendo, setDesfazendo] = useState(false);
  const [resultadoDevolucao, setResultadoDevolucao] = useState<
    { resultado: ResultadoDevolucao; origemRotulo: 'NFC-e' | 'NF-e'; origemNumero: number } | null
  >(null);
  // Cancelamento de venda paga no cartao exige senha gerencial (estorna R$ no MP, DA-251). O backend
  // responde 403 requer_senha_gerencial; abrimos o modal e reenviamos o cancelamento com a senha.
  const [reabrirAlvo, setReabrirAlvo] = useState<{ sessaoId: number; caixaNome: string; operador: string | null; divergencia: string | null } | null>(null);
  const [reabrindo, setReabrindo] = useState(false);
  // Porta "Corrigir o caixa da venda" (DA-351). Mora no PAI porque abre de dois lugares — a coluna
  // ACOES da "Vendas hoje" e a aba Turnos — e porque o spinner de recarga desmonta as Tabs.
  const [moverPagamento, setMoverPagamento] = useState<PagamentoMovivel[] | null>(null);

  // `silencioso` = recarga de fundo (Socket.IO): NAO acende o "Carregando..." porque ele TROCA o
  // conteudo da aba e DESMONTA a Tab — o que zera todo estado local dela (filtros, selecao do grid).
  // O evento `caixa:pedidos_atualizados` e broadcast global (qualquer venda na loja dispara), entao
  // sem isso o filtro do usuario sumia sozinho segundos depois de ser posto.
  const carregarPedidos = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try { setPedidos(await api.get<PedidoCaixa[]>('/api/caixa/pedidos')); }
    catch (e: any) { aviso.erro(e.message); } finally { if (!silencioso) setLoading(false); }
  }, [toast]);

  // Assinatura id:total da lista — a cotacao so precisa refazer quando entra/sai pedido ou quando um
  // total muda. A referencia de `pedidos` troca a cada recarga de fundo (Socket.IO, broadcast global
  // a cada venda da loja), e depender dela sozinha pediria a cotacao de novo a cada venda de outro
  // operador. O backend ainda tem cache de 5 min por cima.
  const pedidosSig = useMemo(() => pedidos.map((p) => `${p.id}:${p.total}`).join('|'), [pedidos]);
  useEffect(() => {
    const ids = pedidos.map((p) => p.id);
    if (ids.length === 0) { setValorCartao({}); return; }
    let cancelado = false;
    (async () => {
      try {
        const r = await api.get<ValorCartaoPedido[]>(`/api/caixa/pedidos-valor-cartao?ids=${ids.join(',')}`);
        if (!cancelado) setValorCartao(Object.fromEntries(r.map((x) => [x.id, x])));
      } catch {
        // Silencioso de proposito: sem cotacao a coluna Total mostra o preco da venda, que nao e
        // errado — so nao antecipa o juros. Erro de PagBank nao vira toast pro operador.
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidosSig]);

  async function voltarOrcamento(p: PedidoCaixa) {
    // [DA-560]/[DA-662] A tela nao oferece o que o servidor vai negar — deixar a pergunta aparecer
    // primeiro faz a operadora CONFIRMAR uma acao que nao vai acontecer, e a recusa chega depois do
    // "OK", com cara de erro em vez de regra. O que mudou na DA-662: com Pix vinculado o servidor
    // nao recusa mais pra TODO MUNDO, so pra quem nao e admin — entao a tela tem dois caminhos, e
    // os textos dos dois saem do shared (funcao unica), sem a contagem de recebimentos, que a fila
    // nao carrega.
    const pixVinculado = Number(p.valor_pix_vinculado ?? 0);
    if (pixVinculado > 0.005) {
      if (!podeCancelar) {
        await dialog.alerta(mensagemPixVinculadoSoAdmin(pixVinculado, null));
        return;
      }
      // ⚠️ Confirmacao PROPRIA, nao a generica: devolver um pedido com Pix vinculado solta dinheiro,
      // e a frase precisa dizer o valor e onde ele vai parar ANTES do clique. `perigoso` porque o
      // efeito nao se desfaz sozinho — refazer o vinculo depois e trabalho manual na Conciliacao.
      const ok = await dialog.confirmar(
        confirmacaoSoltarPixAoVoltar(pixVinculado, null, p.numero),
        { titulo: 'Devolver e soltar o Pix?', textoConfirmar: 'Sim, devolver e soltar', perigoso: true },
      );
      if (!ok) return;
    } else if (!await dialog.confirmar(`Devolver o pedido ${p.numero} ao vendedor para ajustes?`)) {
      // `dialog.confirmar` no lugar do `window.confirm` nativo (regra da casa): o nativo trava a
      // thread e o React nao re-renderiza por baixo dele.
      return;
    }
    try {
      await api.put(`/api/orcamentos/${p.id}/voltar-caixa`, {});
      // [DA-662] Quando solta dinheiro, o aviso diz PRA ONDE ele foi. Toast basta (e nao modal da
      // [DA-443]) porque o conteudo continua recuperavel na tela: o Pix esta na aba ao lado, e a
      // Conciliacao ja se atualizou sozinha pelo `mp:pix_atualizado`.
      toast.sucesso(pixVinculado > 0.005
        ? 'Pedido devolvido — o Pix foi solto e esta na Conciliacao Bancaria'
        : 'Pedido devolvido ao vendedor');
      await carregarPedidos();
    } catch (e: any) {
      aviso.erro(e?.message ?? 'Erro ao devolver pedido');
    }
  }

  const carregarContas = useCallback(async () => {
    try { setContas(await api.get<CaixaConta[]>('/api/caixa/contas')); } catch (e: any) { aviso.erro(e.message); }
  }, [toast]);

  const carregarSessoes = useCallback(async () => {
    setLoading(true);
    try { setSessoes(await api.get<SessaoHoje[]>('/api/caixa/sessoes-hoje')); }
    catch (e: any) { aviso.erro(e.message); } finally { setLoading(false); }
  }, [toast]);

  /**
   * Recarrega o que uma acao de DINHEIRO muda na aba Sessoes: as sessoes (turno) **e** o SALDO das
   * contas ([DA-553]).
   *
   * ⚠️ As contas eram carregadas UMA VEZ, na montagem — o que bastava enquanto elas so davam nome,
   * tipo e formas de pagamento. Com a coluna Saldo da [DA-552] elas passaram a carregar um numero
   * que MUDA, e recarregar so as sessoes deixava a coluna afirmando um valor que ja tinha deixado
   * de ser verdade. Aconteceu no 1o fechamento depois da coluna nascer: o Ruan fechou o turno de
   * 30/07 contando zero, o banco gravou certo (`valor_fechamento = 0,00`, divergencia −993,00) e a
   * coluna seguiu mostrando R$ 993,00 — ele leu, com razao, que "o saldo nao zerou".
   *
   * Licao que generaliza: **ao pendurar um dado VIVO numa consulta que so rodava na montagem,
   * varra quem a recarrega** — o defeito nao aparece em quem escreve o numero, aparece em quem
   * esqueceu de reler.
   */
  const recarregarSaldos = useCallback(async () => {
    await Promise.all([carregarSessoes(), carregarContas()]);
  }, [carregarSessoes, carregarContas]);

  const carregarVendas = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try { setVendas(await api.get<VendaHoje[]>('/api/caixa/vendas-hoje')); }
    catch (e: any) { aviso.erro(e.message); } finally { if (!silencioso) setLoading(false); }
  }, [toast]);

  const carregarResumo = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try { setResumo(await api.get<ResumoDia>('/api/caixa/resumo-dia')); }
    catch (e: any) { aviso.erro(e.message); } finally { if (!silencioso) setLoading(false); }
  }, [toast]);

  // Fila de transferencias em transito (dois pares de olhos). Operadora ve so as proprias; o backend
  // ja filtra por perfil/dono — aqui so consome.
  const carregarTransferencias = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try { setTransferencias(await api.get<TransferenciaPendente[]>('/api/caixa/transferencias-pendentes')); }
    catch (e: any) { aviso.erro(e.message); } finally { if (!silencioso) setLoading(false); }
  }, [toast]);

  const carregarFluxo = useCallback(async () => {
    // Guarda anti-corrida: quem manda e a consulta MAIS NOVA, nao a que responder por ultimo.
    // O campo Valor e debounced e os outros filtros disparam na hora, entao mexer na data com
    // uma busca pendente lanca duas consultas — e a sem filtro (mais pesada, logo mais lenta)
    // chegava depois e sobrescrevia o resultado da busca: a tela mostrava o periodo inteiro
    // com o valor ainda escrito no campo. Resposta atrasada agora e descartada.
    const req = ++fluxoReqId.current;
    setFluxoLoading(true);
    try {
      const query: Record<string, string | number> = { inicio: fluxoInicio, fim: fluxoFim };
      if (fluxoCaixaId !== '' && fluxoCaixaId != null) query.caixa_id = Number(fluxoCaixaId);
      if (fluxoValorAplicado) query.valor = fluxoValorAplicado;
      const resp = await api.get<FluxoResp>('/api/caixa/fluxo', { query });
      if (req === fluxoReqId.current) setFluxo(resp);
    } catch (e: any) { if (req === fluxoReqId.current) aviso.erro(e.message); }
    finally { if (req === fluxoReqId.current) setFluxoLoading(false); }
  }, [fluxoInicio, fluxoFim, fluxoCaixaId, fluxoValorAplicado, toast]);

  const carregarTurnos = useCallback(async () => {
    setTurnosLoading(true);
    try {
      const query: Record<string, string | number> = { inicio: turnosInicio, fim: turnosFim };
      if (turnosCaixaId !== '' && turnosCaixaId != null) query.caixa_id = Number(turnosCaixaId);
      setTurnosRel(await api.get<TurnosResp>('/api/caixa/turnos', { query }));
    } catch (e: any) { aviso.erro(e.message); }
    finally { setTurnosLoading(false); }
  }, [turnosInicio, turnosFim, turnosCaixaId, toast]);

  useEffect(() => { void carregarContas(); }, [carregarContas]);
  // Carrega o caixa pessoal da operadora (turno aberto hoje) — usado para filtrar a aba Sessoes.
  useEffect(() => {
    const isAdminOuGerente = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
    if (isAdminOuGerente) { setMeuCaixa(null); return; }
    api.get<{ id: number; nome: string } | null>('/api/caixa/meu-caixa')
      .then((r) => setMeuCaixa(r))
      .catch(() => setMeuCaixa(null));
  }, [usuario?.perfil]);
  // Alimenta o banner de confirmacao de passagem pendente (independe da aba ativa).
  useEffect(() => {
    if (!podeOperar) return;
    api.get<SessaoHoje[]>('/api/caixa/sessoes-hoje').then(setSessoes).catch(() => { /* banner e opcional */ });
  }, [podeOperar]);
  useEffect(() => {
    if (aba === 'pedidos') void carregarPedidos();
    else if (aba === 'sessoes') void recarregarSaldos();
    else if (aba === 'vendas') void carregarVendas();
    else if (aba === 'transferencias') void carregarTransferencias();
    // Recebimentos s/ Taxas le as MESMAS linhas do Fluxo ([DA-498]) — um recorte, nao uma consulta
    // propria: dois numeros que respondem "quanto o cliente pagou" tem que sair da mesma fonte.
    else if (aba === 'fluxo' || aba === 'recebimentos') void carregarFluxo();
    else if (aba === 'resumo') void carregarResumo();
    else if (aba === 'turnos') void carregarTurnos();
  }, [aba, carregarPedidos, recarregarSaldos, carregarVendas, carregarTransferencias, carregarFluxo, carregarResumo, carregarTurnos]);

  const { socket } = useSocket();
  useEffect(() => {
    if (!socket) return;
    function recarregarAbaAtiva() {
      if (aba === 'pedidos') void carregarPedidos(true);
      else if (aba === 'vendas') void carregarVendas(true);
      else if (aba === 'transferencias') void carregarTransferencias(true);
      else if (aba === 'fluxo' || aba === 'recebimentos') void carregarFluxo();
      else if (aba === 'resumo') void carregarResumo(true);
    }
    socket.on('caixa:pedidos_atualizados', recarregarAbaAtiva);
    return () => { socket.off('caixa:pedidos_atualizados', recarregarAbaAtiva); };
  }, [socket, aba, carregarPedidos, carregarVendas, carregarTransferencias, carregarFluxo, carregarResumo]);

  // Nota que o SERVIDOR emitiu sozinho depois de a venda finalizar (DA-584).
  //
  // Cerco do Pix ([DA-248]): quando a venda fecha `aguardando_nota`, o backend ainda tenta emitir
  // UMA vez logo apos responder ao front (`dispararEmissaoSeConciliada`, fora da transacao, em
  // `caixa.routes.ts`) — e quando ele acerta, a nota nasce AUTORIZADA sem que a tela saiba de nada:
  // a vendedora fica com o toast "a nota sai quando o pagamento for confirmado" e SEM o painel de
  // impressao, com o cliente esperando o cupom. Medido em 19/08 no ORC-006880: venda finalizada
  // 14:56:39,07, `[cerco-pix] nfce AUTORIZADA n1704` em 14:56:39,46 — 0,4 segundo depois.
  //
  // Por isso a tela ESPERA um pouco por essa nota antes de desistir: le a nota do orcamento (rota
  // que ja existia) por alguns segundos e, se ela apareceu autorizada, abre o painel normalmente.
  // ⚠️ E' proposital NAO esperar mais que isso: quando o pagamento falta DE VERDADE, a nota so sai
  // quando a conciliacao bater (minutos ou horas), a vendedora ja esta no proximo atendimento, e um
  // painel que abrisse sozinho la na frente cairia por cima de outra venda.
  const esperarNotaEmitidaPeloServidor = (orcId: number, tipo: 'nfce' | 'nfe') =>
    esperarNotaAutorizada(() => api.get<NotaDoOrcamento[]>(`/api/${tipo}/orcamento/${orcId}`));

  async function emitirNotaFiscal(orcId: number, tipo: 'nfce' | 'nfe', clienteWhatsapp?: string | null, clienteNome?: string | null, valorTotal?: string | number | null, clienteId?: number | null, vendaNumero?: string) {
    // A marca vai ANTES do POST, porque o que se perde e' a VOLTA ([DA-599]): se a tela remontar ou
    // recarregar durante a emissao, o `200` chega pra um componente que nao existe mais e a nota
    // some do fluxo do caixa sem deixar rastro no servidor. Com a marca, a proxima montagem
    // pergunta ao servidor se a nota saiu e reabre o painel.
    marcarNotaPendente({ orcamentoId: orcId, tipo, clienteWhatsapp, clienteNome, clienteId, valorTotal });
    setEmitindoNota(vendaNumero ?? null);
    try {
      const data = await api.post<ResultadoEmissaoData>(`/api/${tipo}/emitir/${orcId}`);
      setResultadoEmissao({ tipo, orcamentoId: orcId, data, clienteWhatsapp, clienteNome, clienteId, valorTotal });
    } catch (err: any) {
      // Falhou de verdade: nao ha painel pra resgatar depois, e deixar a marca faria a proxima
      // montagem consultar uma nota que nunca existiu.
      limparNotaPendente();
      setResultadoEmissao({
        tipo,
        orcamentoId: orcId,
        data: { erro: err?.message || 'Erro ao emitir', xMotivo: err?.message },
        clienteWhatsapp,
        clienteNome,
        clienteId,
        valorTotal,
      });
    } finally {
      setEmitindoNota(null);
    }
  }

  // Resgate da nota cujo painel a tela perdeu. [DA-599]
  //
  // Roda uma vez por montagem do caixa: se ha marca de emissao em aberto (ver `nota-pendente`), a
  // nota daquele orcamento e' consultada e, se estiver AUTORIZADA, o painel abre normalmente.
  // ⚠️ So a nota autorizada reabre: rejeitada/cancelada nao tem cupom pra entregar, e um painel de
  // erro ressuscitando a cada F5 seria barulho em cima de uma venda ja resolvida.
  useEffect(() => {
    const pendente = lerNotaPendente();
    if (!pendente) return;
    let vivo = true;
    api.get<NotaDoOrcamento[]>(`/api/${pendente.tipo}/orcamento/${pendente.orcamentoId}`)
      .then((notas) => {
        const nota = mapearNotaAutorizada(notas);
        // Sem nota autorizada a marca morre aqui: ou a emissao falhou, ou a venda caiu no cerco do
        // Pix — e nesse caso quem avisa e' a aba "Confirmacao Pendente", nao um painel.
        if (!nota) { limparNotaPendente(); return; }
        if (vivo) setResultadoEmissao({
          tipo: pendente.tipo,
          orcamentoId: pendente.orcamentoId,
          data: nota,
          clienteWhatsapp: pendente.clienteWhatsapp,
          clienteNome: pendente.clienteNome,
          clienteId: pendente.clienteId,
          valorTotal: pendente.valorTotal,
        });
      })
      .catch(() => { /* rede oscilou — a marca fica, a proxima montagem tenta de novo */ });
    return () => { vivo = false; };
  }, []);

  async function confirmarReabrir() {
    if (!reabrirAlvo) return;
    setReabrindo(true);
    try {
      await api.post(`/api/caixa/reabrir-turno/${reabrirAlvo.sessaoId}`, {});
      toast.sucesso('Turno reaberto — refaça o fechamento contando a gaveta');
      setReabrirAlvo(null);
      void recarregarSaldos();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setReabrindo(false); }
  }

  // Fora do prazo de cancelamento da SEFAZ, a porta do 🚫 emite a DEVOLUCAO ([DA-442]).
  // Mesma acao do botao ⤺ de /notas-fiscais — o que muda e o lugar de onde se chega nela.
  async function confirmarDevolverVenda() {
    if (!cancelarVenda?.nota_desfazer) return;
    if (motivoCancelar.trim().length < 3) { aviso.erro('Motivo min. 3 chars'); return; }
    const nf = cancelarVenda.nota_desfazer;
    const url = nf.modelo === 'nfce'
      ? `/api/nfe/devolucao-de-nfce/${nf.nota_id}`
      : `/api/nfe/${nf.nota_id}/devolucao`;
    setDesfazendo(true);
    try {
      const r = await api.post<ResultadoDevolucao & { autorizada?: boolean; xMotivo?: string }>(
        url, { motivo: motivoCancelar.trim() },
      );
      if (!r.autorizada) {
        // Rejeicao NAO fecha o modal: o motivo digitado se perde e o operador nao le o porque.
        aviso.erro(`SEFAZ nao autorizou: ${r.xMotivo || 'erro desconhecido'}${r.cStat ? ` (cStat ${r.cStat})` : ''}`);
        return;
      }
      // Resultado em MODAL, nao toast ([DA-443]): o desfecho de uma acao irreversivel que falou
      // com a SEFAZ tem que esperar um clique, senao some antes de ser lido.
      setResultadoDevolucao({ resultado: r, origemRotulo: nf.modelo === 'nfce' ? 'NFC-e' : 'NF-e', origemNumero: nf.numero });
      setCancelarVenda(null);
      setMotivoCancelar('');
      void carregarVendas();
    } catch (e: any) {
      aviso.erro(e.message);
    } finally {
      setDesfazendo(false);
    }
  }

  async function confirmarCancelarVenda() {
    if (!cancelarVenda) return;
    if (motivoCancelar.trim().length < 3) { aviso.erro('Motivo min. 3 chars'); return; }
    setDesfazendo(true);
    try {
      // DA-280: cancelar NÃO mexe em dinheiro. Se a venda foi paga no cartão, o valor fica PENDENTE de
      // estorno (fila na Conciliação) — o backend devolve a mensagem explicando. Nenhuma senha aqui.
      const r = await api.post(`/api/caixa/cancelar-venda/${cancelarVenda.id}`, {
        motivo: motivoCancelar.trim(),
      });
      toast.sucesso((r as any)?.message || 'Venda cancelada');
      setCancelarVenda(null);
      setMotivoCancelar('');
      void carregarVendas();
    } catch (e: any) {
      aviso.erro(e.message);
    } finally {
      setDesfazendo(false);
    }
  }

  // Passagem de bastao aguardando MINHA confirmacao (dupla assinatura, Onda 3): turno aberto,
  // ainda nao confirmado, aberto em meu nome. Banner cobra a reconferencia sem travar a venda.
  // SO gaveta de dinheiro: contas tipo banco (Cofre, Pagbank) nao tem troco fisico pra contar —
  // uma sessao de banco aberta sem confirmar (ex.: correcao manual de saldo) nao deve pedir contagem.
  const minhasPendentes = sessoes.filter(
    (s) => s.caixa_tipo === 'dinheiro' && s.status === 'aberto' && !s.abertura_confirmada_em && s.operador_abertura_id === (usuario?.id ?? -1),
  );

  return (
    <>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <ShoppingCart size={20} /> Caixa
          </h2>
          <div className="pagina-acoes">
            {aba === 'pedidos' && podeOperar && (
              <button type="button" className="btn-primario" onClick={() => setReceberAvulso(true)}
                title="Receber um valor livre do cliente, fora de uma venda (dinheiro/Pix/cartão) — abate na mão no Contas a Receber">
                Receber Avulso
              </button>
            )}
            <button type="button" className="btn-secundario" onClick={() => {
              if (aba === 'pedidos') void carregarPedidos();
              else if (aba === 'sessoes') void recarregarSaldos();
              else if (aba === 'vendas') void carregarVendas();
              else if (aba === 'transferencias') void carregarTransferencias();
              else if (aba === 'fluxo' || aba === 'recebimentos') void carregarFluxo();
              else if (aba === 'turnos') void carregarTurnos();
              else void carregarResumo();
            }}>
              <RefreshCw size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Atualizar
            </button>
            {/* Par do "Ir para o Caixa" de Orcamentos (pedido do Ruan, 01/10/2026): antes era "Voltar" pra tela inicial. */}
            <button type="button" className="btn-secundario" onClick={() => navigate('/orcamentos')}>Orçamentos</button>
          </div>
        </div>
        <div className="abas" style={{ marginTop: 12 }}>
          <button type="button" className={`aba-btn ${aba === 'pedidos' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('pedidos')}>
            Pedidos pra finalizar {pedidos.length > 0 && `(${pedidos.length})`}
          </button>
          <button type="button" className={`aba-btn ${aba === 'vendas' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('vendas')}>
            Vendas hoje
          </button>
          <button type="button" className={`aba-btn ${aba === 'confirmacao' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('confirmacao')}>
            Confirmação Pendente
          </button>
          <button type="button" className={`aba-btn ${aba === 'conciliacao' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('conciliacao')}>
            Conciliação Bancária
          </button>
          {podeVerTurnos && (
            <button type="button" className={`aba-btn ${aba === 'conciliacao_pgto' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('conciliacao_pgto')}>
              Conciliação Pgto
            </button>
          )}
          <button type="button" className={`aba-btn ${aba === 'sessoes' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('sessoes')}>
            Sessoes
          </button>
          <button type="button" className={`aba-btn ${aba === 'transferencias' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('transferencias')}>
            Transferências{(() => { const p = transferencias.filter((t) => t.status === 'pendente').length; return p > 0 ? ` (${p})` : ''; })()}
          </button>
          <button type="button" className={`aba-btn ${aba === 'fluxo' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('fluxo')}>
            Fluxo
          </button>
          <button type="button" className={`aba-btn ${aba === 'recebimentos' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('recebimentos')}>
            Recebimentos s Taxas
          </button>
          <button type="button" className={`aba-btn ${aba === 'resumo' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('resumo')}>
            Resumo do dia
          </button>
          {podeVerTurnos && (
            <button type="button" className={`aba-btn ${aba === 'turnos' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('turnos')}>
              Turnos
            </button>
          )}
        </div>
      </div>

      {minhasPendentes.length > 0 && (
        <div style={{
          margin: '12px 0', padding: '12px 16px', borderRadius: 8,
          backgroundColor: '#fde68a', color: '#000', borderLeft: '6px solid #ca8a04',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 700 }}>
            Você assumiu {minhasPendentes.length === 1 ? 'o caixa' : `${minhasPendentes.length} caixas`} —
            confirme o troco recebido contando {minhasPendentes.length === 1 ? `(${minhasPendentes[0]!.caixa_nome})` : ''}.
          </span>
          <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {minhasPendentes.map((s) => (
              <button key={s.id} type="button" className="btn-primario"
                onClick={() => setTurno({ modo: 'confirmar', caixa: { id: s.caixa_id, nome: s.caixa_nome }, sessaoId: s.id })}>
                Conferir e assumir{minhasPendentes.length > 1 ? ` — ${s.caixa_nome}` : ''}
              </button>
            ))}
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : aba === 'pedidos' ? (
        <PedidosTab pedidos={pedidos} valorCartao={valorCartao} onFinalizar={setPedidoFinalizar} onVoltar={voltarOrcamento} podeOperar={podeOperar} podeAdmin={podeCancelar} usuarioId={usuario?.id ?? null} usuarioPerfil={usuario?.perfil ?? null} onRecarregar={carregarPedidos} modalFinalizarAberto={!!pedidoFinalizar} />
      ) : aba === 'vendas' ? (
        <VendasTab vendas={vendas} podeCancelar={podeCancelar} podeDesfazerLinha={podeDesfazerLinha} onCancelar={setCancelarVenda} onCorrigirCaixa={setMoverPagamento} />
      ) : aba === 'confirmacao' ? (
        <ConfirmacaoPendenteTab podeConferir={usuario?.perfil === 'admin'} />
      ) : aba === 'conciliacao' ? (
        <ConciliacaoBancariaTab podeVincular podeConferir={usuario?.perfil === 'admin'} onIrAoPedido={() => setAba('pedidos')} />
      ) : aba === 'conciliacao_pgto' ? (
        <ConciliacaoPagamentosTab podeBaixar={['admin', 'gerente'].includes(usuario?.perfil ?? '')} />
      ) : aba === 'sessoes' ? (
        <SessoesTab
          sessoes={sessoes}
          contas={(() => {
            const isAdminOuGerente = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
            if (isAdminOuGerente) return contas;
            // Operadora comum: caixas não-dinheiro (bancos/digitais) + SÓ a própria gaveta de
            // dinheiro. "Movimento de Caixa" (e gavetas de colegas) só aparecem pra admin/gerente
            // — assim ela não abre/usa a Movimento por engano (o dinheiro dela é da gaveta dela).
            return contas.filter((c) => (c.tipo !== 'dinheiro' || c.id === meuCaixa?.id) && c.nome !== 'Cofre');
          })()}
          podeOperar={podeOperar}
          podeConferir={['admin', 'gerente'].includes(usuario?.perfil ?? '')}
          usuarioId={usuario?.id ?? null}
          expandidos={sessoesExpandidas}
          vendasPorCaixa={vendasPorCaixa}
          onToggleExpand={(caixaId) => void toggleSessaoExpand(caixaId)}
          onMovimentacao={() => setShowMovimentacao(true)}
          onTransferencia={() => setShowTransferencia(true)}
          onAbrirTurno={(c) => setTurno({ modo: 'abrir', caixa: { id: c.id, nome: c.nome } })}
          onFecharTurno={(c, s) => setTurno({ modo: 'fechar', caixa: { id: c.id, nome: c.nome }, sessaoId: s.id })}
          onConfirmarTurno={(c, s) => setTurno({ modo: 'confirmar', caixa: { id: c.id, nome: c.nome }, sessaoId: s.id })}
          onConferirGaveta={(c, s) => setTurno({ modo: 'conferir', caixa: { id: c.id, nome: c.nome }, sessaoId: s.id })}
          onConferirRecebimento={(c, s) => setConferirRecebimento({ sessaoId: s.id, caixaNome: c.nome })}
          onReabrirTurno={(c, s) => setReabrirAlvo({ sessaoId: s.id, caixaNome: c.nome, operador: s.operador_fechamento_nome, divergencia: s.divergencia })}
          onResolverDivergencia={async (c, s) => {
            const ok = await dialog.confirmar(
              `Dar o de-acordo na diferença de abertura da ${c.nome}? Some do aviso e fica registrado que você conferiu — não altera nenhum valor.`,
              { titulo: 'Resolver diferença', textoConfirmar: 'Sim, resolvi', textoCancelar: 'Cancelar' },
            );
            if (!ok) return;
            try { await api.post(`/api/caixa/resolver-divergencia/${s.id}`, {}); toast.sucesso('Diferença resolvida'); void recarregarSaldos(); }
            catch (e: any) { aviso.erro(e.message); }
          }}
        />
      ) : aba === 'transferencias' ? (
        <TransferenciasTab
          transferencias={transferencias}
          filtro={transfFiltro}
          onFiltro={setTransfFiltro}
          podeBaixar={['admin', 'gerente'].includes(usuario?.perfil ?? '')}
          onBaixar={async (t) => {
            const ok = await dialog.confirmar(
              `Confirmar o recebimento de ${formatMoeda(t.valor)} em ${t.destino_nome} (vindo de ${t.origem_nome})? Só depois disso o valor entra no saldo do destino.`,
              { titulo: 'Confirmar recebimento', textoConfirmar: 'Sim, recebi', textoCancelar: 'Cancelar' },
            );
            if (!ok) return;
            try { await api.post(`/api/caixa/baixar-transferencia/${t.id}`, {}); toast.sucesso('Recebimento confirmado'); void carregarTransferencias(); }
            catch (e: any) { aviso.erro(e.message); }
          }}
        />
      ) : aba === 'fluxo' ? (
        <FluxoTab
          inicio={fluxoInicio}
          fim={fluxoFim}
          caixaId={fluxoCaixaId}
          caixas={contas}
          fluxo={fluxo}
          loading={fluxoLoading}
          valor={fluxoValor}
          onInicio={setFluxoInicio}
          onFim={setFluxoFim}
          onCaixaId={setFluxoCaixaId}
          onValor={setFluxoValor}
          onAtualizar={() => void carregarFluxo()}
          onHoje={() => {
            const h = new Date().toISOString().slice(0, 10);
            setFluxoInicio(h);
            setFluxoFim(h);
          }}
        />
      ) : aba === 'recebimentos' ? (
        <RecebimentosSemTaxasTab
          inicio={fluxoInicio}
          fim={fluxoFim}
          caixaId={fluxoCaixaId}
          caixas={contas}
          fluxo={fluxo}
          loading={fluxoLoading}
          onInicio={setFluxoInicio}
          onFim={setFluxoFim}
          onCaixaId={setFluxoCaixaId}
          onAtualizar={() => void carregarFluxo()}
          onHoje={() => {
            const h = new Date().toISOString().slice(0, 10);
            setFluxoInicio(h);
            setFluxoFim(h);
          }}
        />
      ) : aba === 'resumo' ? (
        <ResumoTab resumo={resumo} />
      ) : aba === 'turnos' ? (
        <TurnosTab
          inicio={turnosInicio}
          fim={turnosFim}
          caixaId={turnosCaixaId}
          caixas={contas}
          rel={turnosRel}
          loading={turnosLoading}
          onInicio={setTurnosInicio}
          onFim={setTurnosFim}
          onCaixaId={setTurnosCaixaId}
          onAtualizar={() => void carregarTurnos()}
          onHoje={() => {
            const h = new Date().toISOString().slice(0, 10);
            setTurnosInicio(h);
            setTurnosFim(h);
          }}
          podeCorrigir={usuario?.perfil === 'admin'}
          onCorrigirCaixa={setMoverPagamento}
        />
      ) : null}

      {/* Porta de correcao de venda no caixa errado (DA-351) — peca unica, dois gatilhos. */}
      <ModalMoverPagamento
        pagamentos={moverPagamento}
        contas={contas}
        onClose={() => setMoverPagamento(null)}
        onMovido={() => {
          // O caixa de origem E o de destino mudaram de saldo: recarrega o que estiver na tela.
          void carregarVendas(true);
          void recarregarSaldos();
          if (aba === 'turnos') void carregarTurnos();
        }}
      />

      {receberAvulso && (
        <ModalReceberAvulso onClose={() => setReceberAvulso(false)} onRecebido={() => { void carregarPedidos(); }} />
      )}
      {pedidoFinalizar && (
        <ModalFinalizarVenda
          pedido={pedidoFinalizar}
          contas={contas}
          onClose={() => setPedidoFinalizar(null)}
          // Venda CM (DA-415): NAO passa pelo `onSuccess` — nao ha venda, nota nem CR pra tratar.
          // So fecha e recarrega a fila (o pedido virou tipo 'venda_cm' e saiu dela).
          onVendaCm={(numero) => {
            setPedidoFinalizar(null);
            void carregarPedidos();
            toast.sucesso(`${numero} liberado como Venda CM. A nota sai no Fechar o mês.`);
          }}
          onSuccess={async (vendaResp) => {
            const tipo = vendaResp.tipo_documento ?? pedidoFinalizar.tipo_documento;
            const orcId = pedidoFinalizar.id;
            const whatsapp = pedidoFinalizar.cliente_whatsapp;
            const nome = pedidoFinalizar.cliente_nome;
            const cliId = pedidoFinalizar.cliente_id;
            setPedidoFinalizar(null);
            void carregarPedidos();
            if (tipo === 'troca' && vendaResp.recibo) {
              setReciboTroca(vendaResp.recibo);
              return;
            }
            // Cerco do Pix (DA-248): venda finalizada esperando a conciliacao bater NAO emite nota
            // agora — a emissao dispara quando o pagamento for confirmado (aba "Confirmacao Pendente").
            if (vendaResp.aguardando_nota) {
              // O servidor ainda tenta emitir logo depois de responder (DA-584). Espera essa nota
              // antes de mandar a vendedora pra fila — quando ela sai, sai em menos de 1 segundo.
              if (tipo === 'nfce' || tipo === 'nfe') {
                // Mesma marca do caminho normal ([DA-599]): esta espera tambem termina num
                // `setResultadoEmissao` que a remontagem da tela engoliria — e aqui o risco e' maior,
                // porque sao 8 segundos de espera em vez de meio.
                marcarNotaPendente({ orcamentoId: orcId, tipo, clienteWhatsapp: whatsapp, clienteNome: nome, clienteId: cliId, valorTotal: pedidoFinalizar.total });
                toast.sucesso(`Venda ${vendaResp.numero} finalizada — conferindo se a nota saiu...`);
                const nota = await esperarNotaEmitidaPeloServidor(orcId, tipo);
                if (nota) {
                  setResultadoEmissao({ tipo, orcamentoId: orcId, data: nota, clienteWhatsapp: whatsapp, clienteNome: nome, clienteId: cliId, valorTotal: pedidoFinalizar.total });
                  return;
                }
                // A nota nao saiu nos 8 segundos. A marca FICA: se ela sair logo depois (o cerco
                // emite fora da transacao), a proxima montagem do caixa a resgata. Quando a venda
                // fica mesmo esperando conciliacao, a consulta nao acha nada e a marca se apaga la.
              }
              toast.sucesso(`Venda ${vendaResp.numero} finalizada. A nota sai quando o pagamento for confirmado (Confirmação Pendente).`);
            } else if (tipo === 'nfce' || tipo === 'nfe') {
              await emitirNotaFiscal(orcId, tipo, whatsapp, nome, pedidoFinalizar.total, cliId, vendaResp.numero);
            } else {
              toast.sucesso(`Venda ${vendaResp.numero} finalizada`);
            }
          }}
        />
      )}

      {reciboTroca && (
        <ModalReciboTroca recibo={reciboTroca} onFechar={() => setReciboTroca(null)} />
      )}

      {resultadoEmissao && (
        <ModalResultadoEmissao
          tipo={resultadoEmissao.tipo}
          resultado={resultadoEmissao.data}
          orcamentoId={resultadoEmissao.orcamentoId}
          clienteWhatsapp={resultadoEmissao.clienteWhatsapp}
          clienteNome={resultadoEmissao.clienteNome}
          clienteId={resultadoEmissao.clienteId}
          valorTotal={resultadoEmissao.valorTotal}
          onConcluir={() => {
            // Fechamento DELIBERADO: quem clicou aqui ja viu a nota. So agora a marca morre — se
            // ela morresse ao ABRIR, um F5 no meio da impressao levaria o painel de novo. [DA-599]
            limparNotaPendente();
            setResultadoEmissao(null);
          }}
        />
      )}

      {/* A nota esta saindo. [DA-599] O modal de finalizar ja fechou, entao sem este aviso a tela
          fica IDENTICA a "nao aconteceu nada" durante toda a emissao — e a rotina de 0,4s esconde
          isso ate o dia em que a emissao leva 11 segundos e a vendedora vira as costas.
          Fica em z-[65]: acima do painel de nota (z-[60], que so aparece depois) e abaixo dos
          modais da casa (z-index 1000), como manda a convencao de z-index do CLAUDE.md. */}
      {emitindoNota && !resultadoEmissao && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50">
          <div
            className="rounded-xl shadow-2xl px-6 py-5 mx-4 flex items-center gap-3"
            style={{ backgroundColor: 'var(--panel-bg)', color: 'var(--text-color)' }}
          >
            <Loader2 size={22} className="animate-spin" style={{ color: 'var(--primary)' }} />
            <div>
              <div style={{ fontWeight: 700 }}>Emitindo a nota fiscal…</div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Venda {emitindoNota} — aguarde o painel de impressão abrir.
              </div>
            </div>
          </div>
        </div>
      )}

      {showMovimentacao && (
        <ModalMovimentacaoCaixa
          contas={contas}
          onFechar={() => setShowMovimentacao(false)}
          onConcluido={() => void recarregarSaldos()}
        />
      )}

      {showTransferencia && (
        <ModalTransferenciaCaixa
          contas={contas}
          origemFixa={['admin', 'gerente'].includes(usuario?.perfil ?? '') ? null : meuCaixa}
          onFechar={() => setShowTransferencia(false)}
          onConcluido={() => void recarregarSaldos()}
        />
      )}

      {conferirRecebimento && (
        <ConferirRecebimentoModal
          sessaoId={conferirRecebimento.sessaoId}
          caixaNome={conferirRecebimento.caixaNome}
          onFechar={() => setConferirRecebimento(null)}
          onConcluido={() => void recarregarSaldos()}
        />
      )}

      {turno && (
        <TurnoModal
          // Remonta ao trocar de modo: "Fechar turno agora" leva de conferir -> fechar na MESMA
          // sessao, e sem key o componente guardaria o `resultadoConferencia` e continuaria
          // renderizando a tela de conferencia em vez do formulario de fechamento.
          key={`${turno.modo}-${turno.sessaoId ?? 'novo'}`}
          modo={turno.modo}
          caixa={turno.caixa}
          sessaoId={turno.sessaoId}
          usuarioId={usuario?.id ?? null}
          destinosTransferencia={contas
            .filter((c) => (c.tipo === 'dinheiro' || c.nome === 'Cofre') && c.id !== turno.caixa.id)
            .map((c) => ({ id: c.id, nome: c.nome }))}
          onFechar={() => setTurno(null)}
          onConcluido={() => void recarregarSaldos()}
          onAbrirExtrato={turno.sessaoId ? () => setExtratoTurno({ sessaoId: turno.sessaoId!, caixaNome: turno.caixa.nome }) : undefined}
          onFecharTurnoAgora={turno.sessaoId
            ? () => setTurno({ modo: 'fechar', caixa: turno.caixa, sessaoId: turno.sessaoId })
            : undefined}
        />
      )}

      {extratoTurno && (
        <ExtratoGavetaModal
          sessaoId={extratoTurno.sessaoId}
          caixaNome={extratoTurno.caixaNome}
          onFechar={() => setExtratoTurno(null)}
        />
      )}

      {/* Uma porta so pra "desfazer esta venda" ([DA-442]). Quem sabe QUAL mecanismo cabe e o
          sistema, porque a diferenca e o relogio da SEFAZ — nao uma escolha do operador. Dentro
          do prazo: cancela (some a nota, o pedido volta pro caixa). Fora: a devolucao, que e
          outro efeito — por isso o titulo e o rotulo mudam junto, nunca so a acao por tras. */}
      {cancelarVenda && (() => {
        const nf = cancelarVenda.nota_desfazer;
        const jaDevolvida = !!nf?.ja_devolvida;
        const modoDevolucao = !!nf?.expirado && !jaDevolvida;
        // Cancelar segue ADMIN (backend: exigirPerfil('admin')). A gerente so chega aqui em modo
        // devolucao; se o dado estiver velho e ela cair no ramo de cancelar, o modal mostra so
        // "Voltar" — melhor nao ter botao do que ter um que estoura 403.
        const podeAgir = jaDevolvida ? false : (modoDevolucao || podeCancelar);
        const rotuloNota = nf?.modelo === 'nfe' ? 'NF-e' : 'NFC-e';
        const limiteTexto = nf ? (nf.limite_minutos >= 1440 ? '24 horas' : `${nf.limite_minutos} minutos`) : '';
        return (
        <Modal aberto onFechar={() => setCancelarVenda(null)}
          titulo={`${modoDevolucao || jaDevolvida ? 'Desfazer' : 'Cancelar'} venda ${cancelarVenda.numero}`}
          largura={460}
          rodape={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-secundario" onClick={() => setCancelarVenda(null)}>Voltar</button>
              {!podeAgir ? null : modoDevolucao ? (
                <button type="button" className="btn-primario" style={{ background: '#7c3aed' }}
                  disabled={desfazendo} onClick={() => void confirmarDevolverVenda()}>
                  {desfazendo ? 'Emitindo...' : '↺ Registrar devolução'}
                </button>
              ) : (
                <button type="button" className="btn-primario" style={{ background: '#dc2626' }}
                  disabled={desfazendo} onClick={() => void confirmarCancelarVenda()}>
                  {desfazendo ? 'Cancelando...' : 'Cancelar venda'}
                </button>
              )}
            </div>
          }
        >
          <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>
            <div>
              <strong>{cancelarVenda.cliente_nome}</strong> · <span className="cel-num">{formatMoeda(cancelarVenda.total)}</span>
            </div>

            {jaDevolvida ? (
              <div style={{ background: '#f97316', color: '#000', border: '1px solid #c2410c', borderRadius: 6, padding: 10, fontSize: 12.5, lineHeight: 1.5 }}>
                Esta venda <strong>já foi desfeita</strong> por uma devolução. A {rotuloNota} {String(nf!.numero).padStart(6, '0')} não conta mais no faturamento nem na comissão. Não há nada a fazer aqui.
              </div>
            ) : modoDevolucao ? (
              <>
                {/* Laranja solido + preto: prazo vencido e ATENCAO, nao erro (vermelho fica pro que
                    quebrou). Traz o horario e a idade em vez de falar em tese — sem isso o operador
                    nao entende por que o botao mudou. */}
                <div style={{ background: '#f97316', color: '#000', border: '1px solid #c2410c', borderRadius: 6, padding: 10, fontSize: 12.5, lineHeight: 1.5 }}>
                  <strong>Cancelar não é mais possível.</strong> A {rotuloNota} {String(nf!.numero).padStart(6, '0')} foi autorizada às{' '}
                  {formatHoraSP(nf!.emitido_em)}, há <strong>{idadeLegivel(nf!.minutos)}</strong> — a SEFAZ aceita cancelamento
                  de {rotuloNota} só até <strong>{limiteTexto}</strong>. O que dá pra fazer agora é registrar a devolução.
                </div>
                {/* As duas acoes NAO tem o mesmo efeito. Sem dizer isso, "Registrar devolucao" leria
                    como cancelar com outro nome — e o operador pararia de agir achando que acabou. */}
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', gap: 7, padding: '7px 10px', fontSize: 12, lineHeight: 1.5, background: 'var(--primary-soft)' }}>
                    <span style={{ fontWeight: 700, color: '#15803d', flex: '0 0 auto' }}>Faz</span>
                    <span>Emite <strong>NF-e de entrada</strong> referenciando a {rotuloNota} (desfaz o faturamento na SEFAZ), <strong>devolve os produtos ao estoque</strong> e tira a venda da <strong>comissão</strong>.</span>
                  </div>
                  <div style={{ display: 'flex', gap: 7, padding: '7px 10px', fontSize: 12, lineHeight: 1.5, borderTop: '1px solid var(--border)', background: 'var(--panel-bg)' }}>
                    <span style={{ fontWeight: 700, color: '#92400e', flex: '0 0 auto' }}>Não faz</span>
                    <span>A venda <strong>continua no histórico</strong> e não volta pro caixa. Ficam <strong>duas notas</strong>: a de venda e a de entrada. O <strong>dinheiro não volta sozinho</strong> — estorno do cartão é ação separada.</span>
                  </div>
                </div>
              </>
            ) : cancelarVenda.tipo_documento === 'troca' ? (
              <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 4, padding: 8, fontSize: 12, color: '#854d0e' }}>
                Venda finalizada com <strong>100% Crédito da Loja</strong> — sem nota fiscal emitida. Ao cancelar, <strong>o crédito consumido é devolvido automaticamente</strong> ao saldo do cliente. O orçamento volta para o caixa. Cancelamento permitido apenas no mesmo dia.
              </div>
            ) : (
              /* A frase "se o prazo passou, registre uma devolucao" SAIU daqui: ela mandava o
                 operador procurar noutra tela. Quando o prazo passa, o modal vira o de cima. */
              <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 4, padding: 8, fontSize: 12, color: '#854d0e' }}>
                Cancelamento permitido apenas no mesmo dia. Estorna estoque e CRs pendentes. <strong>Se houver NFC-e/NF-e autorizada, ela é cancelada na SEFAZ junto.</strong> <strong>Se a venda foi paga no cartão, o valor NÃO é devolvido automaticamente</strong> — fica pendente de estorno na Conciliação. Para devolver ao cliente, use "Estornar no cartão" (exige senha gerencial); para refazer a venda, o pagamento é reaproveitado.
              </div>
            )}

            {/* Cancelar NAO solta o Pix: o vinculo mora no pedido, que volta pra fila com ele (DA-431) —
                em 12 de 13 cancelamentos medidos (26/10/2026) a venda foi refeita e o Pix entrou sozinho.
                Por isso aviso e nao trava: o que faltava era lembrar a desistencia dos passos seguintes. */}
            {!modoDevolucao && !jaDevolvida && Number(cancelarVenda.pix_vinculado ?? 0) > 0.005 && (
              <div style={{ background: '#fde68a', color: '#000', border: '1px solid #b45309', borderRadius: 4, padding: 8, fontSize: 12, lineHeight: 1.5 }}>
                O Pix de <strong className="cel-num">{formatMoeda(cancelarVenda.pix_vinculado)}</strong> continua no pedido{' '}
                <strong>{cancelarVenda.orcamento_numero ?? ''}</strong>, que volta para a fila do caixa. <strong>Se o cliente desistiu:</strong>{' '}
                depois de cancelar, desvincule o Pix na Conciliação Bancária, devolva o dinheiro e exclua o pedido.
              </div>
            )}

            {podeAgir && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Motivo (min. 3 caracteres)</label>
                <textarea
                  value={motivoCancelar}
                  onChange={(e) => setMotivoCancelar(e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: '6px 8px', borderRadius: 4, fontSize: 13, resize: 'vertical' }}
                  placeholder="Ex: cliente desistiu, valor errado, duplicada"
                  autoFocus
                />
              </div>
            )}
          </div>
        </Modal>
        );
      })()}

      {resultadoDevolucao && (
        <ModalResultadoDevolucao
          resultado={resultadoDevolucao.resultado}
          origemRotulo={resultadoDevolucao.origemRotulo}
          origemNumero={resultadoDevolucao.origemNumero}
          onFechar={() => setResultadoDevolucao(null)}
        />
      )}


      {reabrirAlvo && (
        <Modal aberto onFechar={() => { if (!reabrindo) setReabrirAlvo(null); }}
          titulo={`Reabrir turno — ${reabrirAlvo.caixaNome}`}
          largura={460}
          compacto
          rodape={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-secundario" onClick={() => setReabrirAlvo(null)} disabled={reabrindo}>Voltar</button>
              <button type="button" className="btn-primario" onClick={confirmarReabrir} disabled={reabrindo}>
                {reabrindo ? 'Reabrindo...' : 'Reabrir turno'}
              </button>
            </div>
          }
        >
          <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>
            <div>
              Fechado por <strong>{reabrirAlvo.operador || '—'}</strong>
              {reabrirAlvo.divergencia != null && Math.abs(Number(reabrirAlvo.divergencia)) >= 0.005 && (
                <> · diferença atual <span className="cel-num" style={{ backgroundColor: '#f59e0b', color: '#000', fontWeight: 700, padding: '1px 6px', borderRadius: 4 }}>
                  {Number(reabrirAlvo.divergencia) > 0 ? '+' : '−'}{formatMoeda(Math.abs(Number(reabrirAlvo.divergencia)))}
                </span></>
              )}
            </div>
            <div style={{ background: '#fde68a', color: '#000', borderRadius: 4, padding: 10, fontSize: 12.5, lineHeight: 1.5 }}>
              O fechamento atual (contagem e diferença) será <strong>apagado</strong> e o turno volta a ficar <strong>aberto</strong>. A abertura (troco e quem abriu) é mantida. Depois é só usar <strong>Fechar turno</strong> de novo: contar a gaveta inteira e separar o que vai pro Cofre.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Obs.: o alerta de diferença que já tiver saído no WhatsApp não é desfeito — só o registro do turno é corrigido.
            </div>
          </div>
        </Modal>
      )}

    </>
  );
}

interface ItemPedido {
  id: number;
  produto_descricao: string | null;
  codigo_interno: string | null;
  foto_url: string | null;
  unidade_sigla: string | null;
  quantidade: string | number;
  preco_unitario: string | number;
  subtotal: string | number;
}

// Recebimento ja casado com o pedido (Pix do MP ou cartao Point). Aparece na expansao do "+".
interface RecebimentoVinculado {
  origem: 'pix' | 'cartao';
  valor_alocado: string;     // quanto desse recebimento cobre ESTE pedido
  pagador: string | null;    // so Pix
  valor_recebimento: string; // valor total do recebimento na origem
  quando: string | null;
  status: string | null;
  conferido: boolean | null;
  // [DA-569] Quanto do Pix sobrou sem dono, ja descontado TUDO que foi alocado (a este pedido e a
  // outros) e o devolvido. NULL no cartao. Nao confundir com `valor_recebimento - valor_alocado`,
  // que conta como sobra o dinheiro que esta amarrado noutro pedido.
  sobra_recebimento: string | null;
  referencia: string | null; // mp_payment_id (Pix) ou NSU (cartao)
  bandeira: string | null;   // so cartao
  parcelas: number | null;   // so cartao
}

function PedidosTab({ pedidos, valorCartao, onFinalizar, onVoltar, podeOperar, podeAdmin, usuarioId, usuarioPerfil, onRecarregar, modalFinalizarAberto }: { pedidos: PedidoCaixa[]; valorCartao: Record<number, ValorCartaoPedido>; onFinalizar: (p: PedidoCaixa) => void; onVoltar: (p: PedidoCaixa) => void; podeOperar: boolean; podeAdmin: boolean; usuarioId: number | null; usuarioPerfil: string | null; onRecarregar: () => Promise<void>; modalFinalizarAberto: boolean }) {
  const toast = useToast();
  const aviso = useAviso();
  // Trava do pedido (DA-399): motivo obrigatorio, entao travar passa por um modal com o campo —
  // o dialog.confirmar global tem so 2 botoes fixos e nao coleta texto.
  const [travando, setTravando] = useState<PedidoCaixa | null>(null);
  const [motivoTrava, setMotivoTrava] = useState('');
  const [salvandoTrava, setSalvandoTrava] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  // null = carregando, [] = sem itens, undefined = ainda nao buscou
  const [itensPorPedido, setItensPorPedido] = useState<Record<number, ItemPedido[] | null>>({});
  // Recebimentos casados ao pedido (vem no mesmo fetch dos itens). [] = nenhum (esconde o bloco).
  const [recebPorPedido, setRecebPorPedido] = useState<Record<number, RecebimentoVinculado[]>>({});
  const [validandoPix, setValidandoPix] = useState<number | null>(null);

  // Grid navegavel: clicar marca a linha (amarelo), ↑/↓ navegam. Finalizar e
  // IRREVERSIVEL, entao ativarPorEnter:false — a acao fica so no duplo-clique
  // (com o gate de permissao), nunca dispara por Enter acidental.
  const grid = useGridNavegavel<PedidoCaixa>({
    itens: pedidos,
    getId: (p) => p.id,
    ativarPorEnter: false,
    // O modal de Finalizar Venda fica montado por CIMA desta aba (nao a substitui) — sem desligar
    // aqui, o listener global de ↑/↓ desta lista continuaria vivo por baixo e brigaria com o grid
    // do painel "Ver itens" do modal (as duas selecoes andando juntas, scroll pulando sozinho).
    ativo: !modalFinalizarAberto,
    onAtivar: (p) => {
      // Trava do pedido (DA-399): o backend recusa de qualquer jeito, mas deixar o modal de
      // fechamento abrir pra morrer no Finalizar faria a operadora digitar tudo à toa.
      if (p.travado) { aviso.erro(`Pedido TRAVADO${p.travado_por_nome ? ` por ${p.travado_por_nome}` : ''}: ${p.travado_motivo ?? 'sem motivo registrado'} — destrave antes de finalizar.`); return; }
      // Mesma logica da trava: o backend recusa de qualquer jeito, mas morrer so no Finalizar faria
      // a operadora digitar a venda inteira a toa.
      if (p.venda_cancelada_numero && !p.venda_cancelada_reenviada && !podeAdmin) {
        aviso.erro(`Este pedido já virou a venda ${p.venda_cancelada_numero}, que foi CANCELADA. Só o admin pode finalizar de novo — chame o Ruan.`);
        return;
      }
      if (podeOperar) { onFinalizar(p); }
      else { aviso.erro('Sem permissão para finalizar venda — peça para ativarem "Opera o caixa" no seu cadastro'); }
    },
  });

  async function confirmarTrava() {
    if (!travando) return;
    if (!motivoTravaValido(motivoTrava)) { aviso.erro('Descreva o motivo — é o que a operadora vai ler pra saber o que falta.'); return; }
    setSalvandoTrava(true);
    try {
      await api.post(`/api/caixa/pedidos/${travando.id}/travar`, { motivo: motivoTrava.trim() });
      toast.sucesso(`Pedido ${travando.numero} travado`);
      setTravando(null); setMotivoTrava('');
      await onRecarregar();
    } catch (e: any) {
      aviso.erro(e?.message ?? 'Erro ao travar pedido');
    } finally {
      setSalvandoTrava(false);
    }
  }

  async function destravar(p: PedidoCaixa) {
    try {
      await api.post(`/api/caixa/pedidos/${p.id}/destravar`, {});
      toast.sucesso(`Pedido ${p.numero} destravado`);
      await onRecarregar();
    } catch (e: any) {
      aviso.erro(e?.message ?? 'Erro ao destravar pedido');
    }
  }

  async function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (itensPorPedido[id] === undefined) {
          // Lazy load — marca como "carregando" e dispara fetch (itens + recebimentos casados)
          setItensPorPedido((p) => ({ ...p, [id]: null }));
          api.get<{ itens?: ItemPedido[]; recebimentos_vinculados?: RecebimentoVinculado[] }>(`/api/caixa/pedidos/${id}`)
            .then((r) => {
              setItensPorPedido((p) => ({ ...p, [id]: r.itens ?? [] }));
              setRecebPorPedido((p) => ({ ...p, [id]: r.recebimentos_vinculados ?? [] }));
            })
            .catch(() => setItensPorPedido((p) => ({ ...p, [id]: [] })));
        }
      }
      return next;
    });
  }

  async function validarPix(p: PedidoCaixa) {
    const novoEstado = !p.pix_validado_admin;
    setValidandoPix(p.id);
    try {
      await api.post(`/api/caixa/pedidos/${p.id}/validar-pix`, { validado: novoEstado });
      // Atualiza localmente sem recarregar a lista toda
      p.pix_validado_admin = novoEstado;
      toast.sucesso(novoEstado ? 'Pix marcado como validado' : 'Validação removida');
    } catch (e: any) {
      aviso.erro(e.message);
    } finally {
      setValidandoPix(null);
    }
  }

  if (pedidos.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum pedido aguardando finalizacao</div>;
  }
  return (
    <>
    <table className="tabela" style={{ width: '100%' }}>
      <thead>
        <tr>
          <th style={{ width: 36, textAlign: 'center' }}>#</th>
          <th>Pedido</th>
          <th style={{ width: 56 }}>Cod.</th>
          <th>Cliente</th>
          <th>Data</th>
          <th>Forma de Pagamento</th>
          <th>Tipo NF</th>
          <th style={{ textAlign: 'right' }}>Total</th>
          <th>Vendedor</th>
          <th style={{ textAlign: 'right', width: 80 }}>Desconto</th>
          {/* 118px: cabe "30,00 ✓ Pix" / "30,00 Parcial" numa linha so (DA-529). O grupo
              valor+selo e nowrap, entao a coluna PRECISA pedir a largura — celula que empilha
              nao pede espaco nenhum. Medido na tela publicada, nao estimado. */}
          <th style={{ width: 118 }}>Pix</th>
          {/* 88px: cabem os DOIS botoes (cadeado + voltar) lado a lado. Celula de tabela com
              largura apertada empilha os botoes em vez de pedir espaco. */}
          <th style={{ textAlign: 'right', width: 88 }}>Acoes</th>
        </tr>
      </thead>
      <tbody>
        {pedidos.map((p) => {
          // subtotal_bruto = soma de preco_unitario * quantidade dos itens (antes de qualquer desconto).
          // total = valor liquido a pagar. Diferenca = desconto efetivo (unitario + total + credito).
          const subtotalBruto = Number(p.subtotal_bruto || p.subtotal || 0);
          const total = Number(p.total || 0);
          const descPerc = subtotalBruto > 0 ? (1 - total / subtotalBruto) * 100 : 0;
          const tipoLabel = p.tipo_documento === 'nfe' ? 'A4' : p.tipo_documento === 'nfce' ? 'Consumidor' : '—';
          // Cotacao da maquininha (DA-419) — so existe em pedido de cartao PARCELADO; ausente, a
          // coluna Total segue mostrando o preco da venda.
          const vc = valorCartao[p.id];
          const expandido = expandedIds.has(p.id);
          const itens = itensPorPedido[p.id];
          const recebs = recebPorPedido[p.id] ?? [];
          // Quanto do Pix ainda nao casou — explica o selo "Parcial"/"Aguarda" ali mesmo. A base vem
          // da regra unica (DA-431), que cobre o pedido devolvido por venda cancelada: ali nao ha
          // declaracao (o cancelar apaga `orcamento_pagamentos`) e quem responde e a linha de Pix
          // congelada. Calculado UMA vez por linha e reusado pelo selo da coluna PIX abaixo.
          const pixDeclaradoExp = Number(p.valor_pix_declarado ?? 0);
          const pixVinculadoExp = Number(p.valor_pix_vinculado ?? 0);
          const pixEsperado = pixEsperadoDoPedido({
            declarado: pixDeclaradoExp,
            vinculado: pixVinculadoExp,
            valorPixUnico: p.valor_pix_unico != null ? Number(p.valor_pix_unico) : null,
            total: Number(p.total ?? 0),
          });
          const faltaPix = pixEsperado - pixVinculadoExp;
          // Recebimento PARCIAL EM RELACAO AO PEDIDO (DA-529): o Pix casou, mas nao cobre o total —
          // venda mista (R$ 30 Pix + R$ 20 dinheiro) ou Pix pago pela metade. Ai a coluna mostra o
          // valor CONFIRMADO a esquerda do selo, porque "✓ Pix" ao lado de um Total de R$ 50,00 le
          // como "os R$ 50,00 entraram" — foi exatamente a leitura do Ruan em 13/08. Pedido coberto
          // 100% pelo Pix segue so com o selo: o numero seria uma copia do Total da linha ao lado.
          const pixConfirmadoParcial = pixVinculadoExp > 0.005
            && pixVinculadoExp < Number(p.total ?? 0) - TOL_CENTAVO;
          // FALTA RECEBER (DA-561). Parte do dinheiro ja entrou (Pix vinculado + Credito da Loja
          // abatido) e a coluna Total seguia exibindo o preco cheio: a fila obrigava o operador a
          // subtrair de cabeca — R$ 375,01 ao lado de "2,99 Parcial", com o 372,02 escondido no
          // hover do selo, que no tablet nao existe ([DA-529] p.2). O numero que ainda esta em jogo
          // passa ao destaque e o preco da venda recua pro subtitulo (convencao da casa, pedida em
          // 11/08 pro modal e pra coluna Valor da Conciliacao). A palavra "falta" vai colada nele:
          // numero grande sem rotulo, sob um cabecalho que diz "Total", le como "a venda vale
          // 372,02". Cartao PARCELADO fica de fora — ali o destaque ja pertence ao valor da
          // maquininha ([DA-419]), que e o que o cliente paga.
          const creditoLojaExp = Number(p.valor_credito_loja ?? 0);
          const jaEntrouExp = pixVinculadoExp + creditoLojaExp;
          const faltaReceber = Math.round((total - jaEntrouExp) * 100) / 100;
          const mostraFaltaReceber = !vc && pixVinculadoExp > 0.005 && faltaReceber > TOL_CENTAVO;
          // Trava do pedido (DA-399). Dias inteiros parados: o skew VPS<->PC (~1min) nao afeta
          // contagem em DIAS, e so aparece a partir do 1o dia — trava do dia nao vira alarme.
          const travadoEm = parseSPDate(p.travado_em);
          const diasTravado = travadoEm ? Math.max(0, Math.floor((Date.now() - travadoEm.getTime()) / 86400000)) : 0;
          const podeTirarTrava = podeDestravarPedido(
            { travado_por: p.travado_por, travado_por_login: p.travado_por_login },
            { id: usuarioId ?? -1, perfil: usuarioPerfil },
          );
          return (
            <Fragment key={p.id}>
              {(() => { const rp = grid.rowProps(p); return (
              <tr
                {...rp}
                style={{
                  ...rp.style,
                  cursor: p.travado ? 'not-allowed' : podeOperar ? 'pointer' : 'not-allowed',
                  // Faixa laranja na borda: marca a linha travada sem brigar com o amarelo da
                  // selecao do grid (principio #3 — cada cor com um significado so).
                  ...(p.travado ? { boxShadow: 'inset 4px 0 0 0 #f97316' } : null),
                }}
                title={p.travado
                  ? `Travado${p.travado_por_nome ? ` por ${p.travado_por_nome}` : ''}: ${p.travado_motivo ?? ''}`
                  : podeOperar ? 'Clique duplo pra finalizar venda' : 'Sem permissão — "Opera o caixa" precisa estar ativo no seu cadastro'}
              >
                <td style={{ textAlign: 'center' }} onDoubleClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => void toggleExpand(p.id)}
                    title={expandido ? 'Ocultar itens' : 'Ver itens'}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 22, height: 22, borderRadius: 4, padding: 0,
                      border: '1px solid var(--input-border, #d1d5db)',
                      background: expandido ? 'var(--accent, #2563eb)' : 'transparent',
                      color: expandido ? '#fff' : 'var(--accent, #2563eb)',
                      cursor: 'pointer',
                    }}
                  >
                    {expandido ? <Minus size={12} /> : <Plus size={12} />}
                  </button>
                </td>
                <td style={{ fontWeight: 600 }}>
                  {p.numero}
                  {p.travado && (
                    <span
                      title="Pedido travado — nao finaliza, nao volta e nao e excluido"
                      style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#000', backgroundColor: '#f97316', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}
                    >
                      🔒 TRAVADO
                    </span>
                  )}
                </td>
                <td className="text-gray-500 font-bold no-underline">{p.cliente_codigo || (p.cliente_id && p.cliente_id !== 1 ? p.cliente_id : '—')}</td>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {p.whatsapp_janela_aberta !== null && (
                      <span
                        title={p.whatsapp_janela_aberta
                          ? 'WhatsApp: janela 24h aberta — pode enviar mensagem livre'
                          : 'WhatsApp: janela 24h fechada — so templates'}
                        style={{
                          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                          background: p.whatsapp_janela_aberta ? '#3b82f6' : '#9ca3af',
                          boxShadow: '0 0 0 1px rgba(255,255,255,0.6)',
                        }}
                      />
                    )}
                    {p.cliente_nome}
                  </span>
                  {p.cliente_doc && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap' }}>
                      {formatCpfCnpj(p.cliente_doc)}
                    </div>
                  )}
                  {Number(p.cliente_em_aberto ?? 0) > 0 && (
                    // O <div> em bloco garante a linha propria embaixo do nome (inline-block subia
                    // pro lado dele); o fundo fica so no selo.
                    <div style={{ marginTop: 3 }}>
                      <span
                        title="Total dos titulos de crediario em aberto deste cliente (o mesmo do extrato do cliente)"
                        style={{
                          display: 'inline-block', fontSize: 11, whiteSpace: 'nowrap',
                          // Amarelo mais claro que o #fde68a da linha selecionada, com contorno: na
                          // linha marcada o selo nao some.
                          backgroundColor: '#fef3c7', color: '#000', border: '1px solid #f59e0b',
                          padding: '1px 7px', borderRadius: 4,
                        }}
                      >
                        Em aberto{' '}
                        <span className="cel-num" style={{ fontWeight: 700 }}>{formatMoeda(p.cliente_em_aberto)}</span>
                      </span>
                    </div>
                  )}
                </td>
                <td>{formatDataHoraSP(p.atualizado_em)}</td>
                <td style={{ fontWeight: 600 }}>
                  {/* Venda mista: cada forma com o seu valor. Antes a celula dizia so "Credito da
                      Loja" e o Pix de R$ 9,00 do mesmo pedido so aparecia na coluna PIX. */}
                  {p.composicao && p.composicao.length > 1 ? (
                    p.composicao.map((f, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', whiteSpace: 'nowrap' }}>
                        <span style={{ flex: '1 1 auto' }}>{f.forma}</span>
                        <span className="cel-num">{formatMoedaSemSimbolo(f.valor)}</span>
                      </div>
                    ))
                  ) : (p.forma_pagamento_nome || '—')}
                </td>
                <td style={{ color: 'var(--text-muted)' }}>{tipoLabel}</td>
                <td className="cel-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                  {/* Cartao PARCELADO (DA-419): o numero grande e o que o cliente PAGA na maquininha
                      — o preco da venda desce pro subtitulo. Ate aqui a coluna mostrava a venda e o
                      juros so aparecia dentro do modal de finalizar. Sem cotacao (a vista, misto ou
                      PagBank fora do ar), cai no comportamento de sempre. */}
                  {vc ? formatMoeda(vc.total_cartao) : mostraFaltaReceber ? (
                    <span title={`Total da venda ${formatMoeda(total)} — ja entrou ${formatMoeda(jaEntrouExp)}`}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginRight: 4 }}>falta</span>
                      {formatMoeda(faltaReceber)}
                    </span>
                  ) : formatMoeda(p.total)}
                  {mostraFaltaReceber && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap' }}>
                      de {formatMoeda(total)}
                    </div>
                  )}
                  {vc && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap' }}
                         title={`${vc.parcelas}x de ${formatMoeda(vc.total_cartao / vc.parcelas)} — juros + imposto do parcelamento`}>
                      venda {formatMoedaSemSimbolo(vc.venda)} + cartão {formatMoedaSemSimbolo(vc.total_cartao - vc.venda)}
                    </div>
                  )}
                  {(() => {
                    const credito = creditoLojaExp;
                    // Com o Pix parcial em cena, a linha de falta acima ja desconta o credito —
                    // manter as duas poria dois "a pagar" diferentes na mesma celula (DA-561).
                    if (credito <= 0.005 || mostraFaltaReceber) return null;
                    // O credito vira desconto na nota ([DA-049]), entao total - credito e' o valor
                    // da NOTA — e o rotulo diz isso. "a pagar" confundia depois que o Pix ja tinha
                    // caido: nao havia mais nada a pagar e o numero seguia ali.
                    const valorNota = total - credito;
                    return (
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap' }}>
                        <span title={`Credito da Loja ${formatMoeda(credito)} entra como desconto na nota`}>
                          nota {formatMoeda(valorNota)}
                        </span>
                      </div>
                    );
                  })()}
                </td>
                <td>{p.vendedor_nome || '—'}</td>
                <td className="cel-num" style={{ textAlign: 'right', color: descPerc > 0.005 ? 'var(--danger)' : 'var(--text-muted)', fontWeight: descPerc > 0.005 ? 600 : 400 }}>
                  {descPerc > 0.005 ? `${descPerc.toFixed(2)}%` : '—'}
                </td>
                <td onDoubleClick={(e) => e.stopPropagation()}>
                  {(() => {
                    // `pixEsperado` (regra unica, DA-431) ja veio calculado no escopo da linha e
                    // cobre o pedido devolvido por venda cancelada, que nao tem declaracao nenhuma.
                    const pixDeclarado = pixDeclaradoExp;
                    const pixVinculado = pixVinculadoExp;
                    if (pixEsperado <= 0.005) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>;
                    // Valor confirmado, a ESQUERDA do selo (DA-529). 12px pra alinhar com a pilula
                    // ao lado — nao e escolha de estilo solta, e o tamanho do vizinho. Sem simbolo:
                    // a coluna e estreita e o "R$" ja esta na coluna Total, na mesma linha.
                    const valorConfirmado = pixConfirmadoParcial ? (
                      <span
                        className="cel-num"
                        title={`Confirmado ${formatMoeda(pixVinculado)} dos ${formatMoeda(Number(p.total ?? 0))} do pedido`}
                        style={{ fontSize: 12, fontWeight: 700 }}
                      >
                        {formatMoedaSemSimbolo(pixVinculado)}
                      </span>
                    ) : null;
                    // O par valor+selo nao se separa: `flex-wrap` numa celula de tabela NAO pede
                    // espaco (a coluna encolhe e os itens empilham), entao o grupo vai `nowrap` e a
                    // largura da coluna foi medida pra ele. O "Validar" pode cair pra linha de baixo.
                    const comValor = (selo: ReactNode) => (
                      valorConfirmado
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>{valorConfirmado}{selo}</span>
                        : selo
                    );
                    if (p.pix_validado_admin) {
                      return comValor(
                        <span title="Pix validado manualmente pelo admin" style={{ fontSize: 12, fontWeight: 700, color: '#000', backgroundColor: '#fde68a', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                          ✓ Admin
                        </span>,
                      );
                    }
                    // Tolerancia de centavos ±0,05 (mesma da finalizacao/emissao): o QR estatico nao
                    // embute valor, entao o cliente digita e pode arredondar ate 5 centavos. Gap <=0,05
                    // conta como Pix casado; acima disso e recebimento PARCIAL de verdade (fica "Parcial").
                    if (pixVinculado >= pixEsperado - TOL_CENTAVO) {
                      return comValor(
                        <span title={pixDeclarado > 0.005
                          ? `Pix vinculado: ${formatMoeda(pixVinculado)}`
                          : `Pix vinculado: ${formatMoeda(pixVinculado)} — recebido antes de a venda ser cancelada. O dinheiro entrou e continua com a loja; nao cobre o cliente de novo.`}
                          style={{ fontSize: 12, fontWeight: 700, color: '#000', backgroundColor: '#fde68a', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                          ✓ Pix
                        </span>,
                      );
                    }
                    // Parcial ou pendente
                    const falta = pixEsperado - pixVinculado;
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        {comValor(
                          <span title={`Pix do pedido: ${formatMoeda(pixEsperado)} · vinculado: ${formatMoeda(pixVinculado)} · falta: ${formatMoeda(falta)}`} style={{ fontSize: 12, fontWeight: 700, color: '#000', backgroundColor: '#f59e0b', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                            {pixVinculado > 0.005 ? 'Parcial' : 'Aguarda'}
                          </span>,
                        )}
                        {podeAdmin && (
                          <button
                            type="button"
                            title="Validar Pix manualmente (admin)"
                            disabled={validandoPix === p.id}
                            onClick={() => void validarPix(p)}
                            style={{ fontSize: 11, padding: '1px 6px', border: '1px solid var(--input-border,#d1d5db)', borderRadius: 4, background: 'var(--panel-bg,#fff)', cursor: 'pointer', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}
                          >
                            Validar
                          </button>
                        )}
                      </span>
                    );
                  })()}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onDoubleClick={(e) => e.stopPropagation()}>
                  {/* Cadeado (DA-399). Travar: qualquer um — quem descobre a pendencia e quem esta
                      com o pedido na mao. Destravar: so quem travou ou admin (trava do dono e
                      pessoal), e o cadeado aberto some pra quem nao pode. */}
                  {!p.travado ? (
                    <button
                      type="button"
                      onClick={() => { setTravando(p); setMotivoTrava(''); }}
                      title="Travar pedido (impede finalizar, voltar e excluir)"
                      style={{ padding: 6, border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', borderRadius: 4 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(249, 115, 22, 0.12)'; e.currentTarget.style.color = '#f97316'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                    >
                      <Lock size={16} />
                    </button>
                  ) : podeTirarTrava ? (
                    <button
                      type="button"
                      onClick={() => void destravar(p)}
                      title={`Destravar (travado${p.travado_por_nome ? ` por ${p.travado_por_nome}` : ''})`}
                      style={{ padding: 6, border: 0, background: 'transparent', color: '#f97316', cursor: 'pointer', borderRadius: 4 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(249, 115, 22, 0.12)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Unlock size={16} />
                    </button>
                  ) : null}
                  {/* Travado nao volta pro orcamento: o backend recusa, e deixar o botao vivo so
                      geraria erro depois do window.confirm.
                      [DA-560]/[DA-662] Pix vinculado NAO impede mais a volta: pra admin, devolver
                      solta o dinheiro junto (ele cai na Conciliacao como recebimento a conciliar);
                      pra quem nao e admin, o botao FICA e explica no clique. A diferenca pro travado
                      e proposital: o pedido travado carrega a tarja laranja com o motivo escrito na
                      propria linha, entao sumir com o botao nao esconde nada; o pedido com Pix so
                      mostra o selo "✓ Pix", que diz que o dinheiro chegou e NAO diz que por isso o
                      pedido esta preso. Sumir com o botao ali deixaria a vendedora procurando um
                      botao que ela usou ontem ([DA-468]: ausencia nao se distingue de inexistencia).
                      ⚠️ O tooltip conta a verdade de HOJE e ela depende de QUEM esta olhando — dizer
                      "um admin precisa desvincular antes" virou texto vencido no instante em que
                      esta porta parou de recusar ([DA-639]). */}
                  {!p.travado && (podeOperar || (usuarioId !== null && p.vendedor_id === usuarioId)) && (
                    <button
                      type="button"
                      onClick={() => onVoltar(p)}
                      title={Number(p.valor_pix_vinculado ?? 0) > 0.005
                        ? (podeAdmin
                            ? 'Devolver ao vendedor — o Pix vinculado sera solto e volta pra Conciliacao Bancaria'
                            : 'Pix ja vinculado — devolver solta esse dinheiro, e so um admin pode fazer isso')
                        : podeOperar ? 'Devolver ao vendedor para ajustes' : 'Voltar para orcamento (seu pedido)'}
                      style={{ padding: 6, border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', borderRadius: 4 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(234, 88, 12, 0.1)'; e.currentTarget.style.color = '#ea580c'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                    >
                      <Undo2 size={16} />
                    </button>
                  )}
                </td>
              </tr>
              ); })()}
              {/* Tarja da trava (DA-399): o motivo escrito e o que impede a trava de virar cadeado
                  mudo — sem ele o proximo admin destrava so pra tocar a fila. Laranja solido +
                  letra preta (pendencia, nao erro; e daltonismo-safe). */}
              {p.travado && (
                <tr>
                  <td colSpan={12} style={{ padding: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px 6px 40px', background: '#f97316', color: '#000', fontSize: 13 }}>
                      <Lock size={14} style={{ flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        Travado{p.travado_por_nome ? ` por ${p.travado_por_nome}` : ''}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>{p.travado_motivo || 'sem motivo registrado'}</span>
                      <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {diasTravado >= 1 ? `há ${diasTravado} dia${diasTravado > 1 ? 's' : ''}` : p.travado_em ? formatHoraSP(p.travado_em) : ''}
                      </span>
                    </div>
                  </td>
                </tr>
              )}
              {/* Pedido que voltou por cancelamento. Amarelo forte + preto pra nao confundir com o
                  laranja da trava (e daltonismo-safe). O numero da venda vai escrito: sem ele o
                  aviso vira "alguma coisa aconteceu aqui" e a operadora chama o admin sem saber o
                  que dizer. */}
              {p.venda_cancelada_numero && (
                <tr>
                  <td colSpan={12} style={{ padding: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px 6px 40px', background: '#fbbf24', color: '#000', fontSize: 13 }}>
                      <Undo2 size={14} style={{ flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        Venda {p.venda_cancelada_numero} cancelada
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {p.venda_cancelada_reenviada
                          ? 'Este pedido já foi vendido uma vez, voltou ao orçamento e foi reenviado — pode finalizar normalmente.'
                          : 'Este pedido já foi vendido uma vez. Só o admin pode finalizar de novo — confira se o dinheiro do cliente voltou antes.'}
                      </span>
                    </div>
                  </td>
                </tr>
              )}
              {expandido && (
                <tr style={{ background: 'rgba(59, 130, 246, 0.05)' }}>
                  <td colSpan={12} style={{ padding: 0 }}>
                    <div style={{ padding: '12px 32px' }}>
                      {(recebs.length > 0 || faltaPix > 0.005) && (
                        <div style={{ marginBottom: 12, background: 'var(--panel-bg, #fff)', borderRadius: 6, border: '1px solid var(--input-border, #e5e7eb)', overflow: 'hidden' }}>
                          {recebs.map((rec, i) => {
                            const valorAloc = Number(rec.valor_alocado);
                            const valorTot = Number(rec.valor_recebimento);
                            const parcial = valorAloc < valorTot - 0.005; // o recebimento cobre mais de um pedido
                            // [DA-569] A sobra vem PRONTA do servidor — `valorTot - valorAloc` daria
                            // como sobra o que esta amarrado noutro pedido (o Pix de R$ 608,00 do
                            // ORC-006797 tem R$ 605,01 assim, e sobra zero de verdade).
                            const sobra = rec.sobra_recebimento == null ? 0 : Number(rec.sobra_recebimento);
                            const temSobra = sobra > 0.005;
                            const noutros = valorTot - valorAloc - Math.max(0, sobra);
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderTop: i === 0 ? 'none' : '1px solid var(--input-border, #f3f4f6)', fontSize: 13 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#000', backgroundColor: rec.origem === 'pix' ? '#a7f3d0' : '#bfdbfe', padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                                  {rec.origem === 'pix' ? 'Pix' : 'Cartão'}
                                </span>
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {rec.origem === 'pix'
                                    ? (rec.pagador || 'Pagador não identificado')
                                    : `${rec.bandeira || 'Cartão'}${rec.parcelas && rec.parcelas > 1 ? ` ${rec.parcelas}x` : ''}`}
                                  {rec.referencia && (
                                    <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 11 }}>#{rec.referencia}</span>
                                  )}
                                </span>
                                <span style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                                  {rec.quando ? formatDataHoraSP(rec.quando) : '—'}
                                </span>
                                {/* [DA-569] O selo fala da PARCELA deste pedido, nao do recebimento inteiro.
                                    No Pix quem prova e' o proprio `mp_vinculo`: os R$ 372,02 amarrados
                                    aqui estao conferidos ainda que o Pix de R$ 376,00 tenha sobra solta.
                                    Lendo `mr.conferido` (do recebimento) a linha dizia "a conferir" sobre
                                    dinheiro ja conferido e calava a pendencia real, que e' de OUTRO objeto
                                    — ela vai na pilula ao lado. Cartao segue no `conferido` da cobranca:
                                    ela pertence a um pedido so, e ali o campo mede a chegada do liquido. */}
                                {rec.origem === 'pix' || rec.conferido
                                  ? <span title={rec.origem === 'pix'
                                      ? `${formatMoeda(valorAloc)} deste Pix estão amarrados a este pedido`
                                      : 'Conferido na Conciliação'} style={{ fontSize: 11, fontWeight: 700, color: '#000', backgroundColor: '#fde68a', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>✓ Conferido</span>
                                  : <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>a conferir</span>}
                                {temSobra && (
                                  <span title={`O cliente pagou ${formatMoeda(valorTot)} e este pedido consumiu ${formatMoeda(valorAloc)}${noutros > 0.005 ? ` (outros pedidos, ${formatMoeda(noutros)})` : ''}. Sobram ${formatMoeda(sobra)} sem dono — resolve na Conciliação Bancária.`}
                                        style={{ fontSize: 11, fontWeight: 700, color: '#000', backgroundColor: '#f97316', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                                    sobra {formatMoeda(sobra)}
                                  </span>
                                )}
                                {/* [DA-569] Sobra NEGATIVA = o liquido ja nao cobre o que este pedido
                                    tem amarrado, ou seja o Pix foi devolvido DEPOIS do vinculo. Zero
                                    casos na base em 18/08, mas devolver Pix e' recurso vivo ([DA-206]) e
                                    sem isto o "✓ Conferido" que passei a afirmar viraria mentira calada
                                    no dia em que acontecesse — o selo prova o vinculo, nao o dinheiro. */}
                                {sobra < -0.005 && (
                                  <span title={`Este pedido tem ${formatMoeda(valorAloc)} amarrados, mas o Pix já devolveu parte: faltam ${formatMoeda(Math.abs(sobra))} de lastro. Confira na Conciliação Bancária.`}
                                        style={{ fontSize: 11, fontWeight: 700, color: '#000', backgroundColor: '#ef4444', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                                    devolvido {formatMoeda(Math.abs(sobra))}
                                  </span>
                                )}
                                <span className="cel-num" style={{ fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'right', minWidth: 90 }}>
                                  {formatMoeda(valorAloc)}
                                  {parcial && (
                                    /* [DA-569] O texto deriva do dado: o mesmo "de R$ X" cobre agora dois
                                       mundos — recebimento repartido entre pedidos e recebimento com sobra
                                       —, e a frase fixa "dividido entre pedidos" descrevia so o primeiro. */
                                    <span title={noutros > 0.005
                                        ? `Recebimento total de ${formatMoeda(valorTot)}, dividido entre pedidos`
                                        : `Recebimento total de ${formatMoeda(valorTot)}; o resto ainda não tem pedido`} style={{ display: 'block', fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>
                                      de {formatMoeda(valorTot)}
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                          {faltaPix > 0.005 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 12px', borderTop: recebs.length > 0 ? '1px solid var(--input-border, #f3f4f6)' : 'none', fontSize: 13, background: '#fef3c7', color: '#000' }}>
                              <span style={{ fontWeight: 600 }}>
                                {pixVinculadoExp > 0.005 ? 'Falta casar do Pix do pedido' : 'Pix declarado, nenhum recebimento casado ainda'}
                              </span>
                              <span className="cel-num" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>falta {formatMoeda(faltaPix)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {itens === undefined || itens === null ? (
                        <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Carregando itens...</div>
                      ) : itens.length === 0 ? (
                        <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Nenhum item</div>
                      ) : (
                        <table style={{ width: '100%', fontSize: 13, background: 'var(--panel-bg, #fff)', borderRadius: 6, border: '1px solid var(--input-border, #e5e7eb)' }}>
                          <thead>
                            <tr style={{ background: 'var(--grid-header-bg, #f3f4f6)', color: 'var(--grid-header-fg, #4b5563)' }}>
                              <th style={{ textAlign: 'center', padding: '6px 8px', width: 32, fontSize: 11, fontWeight: 600 }}>#</th>
                              <th style={{ padding: '6px 8px', width: 44, fontSize: 11, fontWeight: 600 }}></th>
                              <th style={{ textAlign: 'left', padding: '6px 12px', width: 80, fontSize: 11, fontWeight: 600 }}>Cod.</th>
                              <th style={{ textAlign: 'left', padding: '6px 12px', fontSize: 11, fontWeight: 600 }}>
                                Descricao <span style={{ marginLeft: 8, opacity: 0.6, fontWeight: 400 }}>({p.numero})</span>
                              </th>
                              <th style={{ textAlign: 'right', padding: '6px 12px', width: 110, fontSize: 11, fontWeight: 600 }}>Qtd</th>
                              <th style={{ textAlign: 'right', padding: '6px 12px', width: 110, fontSize: 11, fontWeight: 600 }}>Unit.</th>
                              <th style={{ textAlign: 'right', padding: '6px 12px', width: 130, fontSize: 11, fontWeight: 600 }}>Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itens.map((it, idx) => (
                              <tr key={it.id} style={{ borderTop: '1px solid var(--input-border, #f3f4f6)' }}>
                                <td style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                                <td style={{ padding: '4px 8px' }}>
                                  {it.foto_url ? (
                                    <FotoProduto url={it.foto_url} tam={36} />
                                  ) : (
                                    <div style={{ width: 36, height: 36, borderRadius: 4, background: '#f3f4f6', border: '1px solid var(--input-border, #e5e7eb)' }} />
                                  )}
                                </td>
                                <td style={{ padding: '6px 12px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{it.codigo_interno || '—'}</td>
                                <td style={{ padding: '6px 12px' }}>{it.produto_descricao || '—'}</td>
                                <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right' }}>
                                  {Number(it.quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                                  {it.unidade_sigla ? ` ${it.unidade_sigla}` : ''}
                                </td>
                                <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right' }}>{formatMoeda(it.preco_unitario)}</td>
                                <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>{formatMoeda(it.subtotal)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
    {travando && (
      <Modal
        aberto
        titulo={`Travar pedido ${travando.numero}`}
        onFechar={() => { if (!salvandoTrava) { setTravando(null); setMotivoTrava(''); } }}
        largura={520}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => { setTravando(null); setMotivoTrava(''); }} disabled={salvandoTrava}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={() => void confirmarTrava()} disabled={salvandoTrava || !motivoTravaValido(motivoTrava)}>
              {salvandoTrava ? 'Travando...' : 'Travar pedido'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Enquanto estiver travado, este pedido <strong>não finaliza, não volta pro orçamento e não pode ser excluído</strong>.
            Quem destrava: você ou um admin.
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 }}>
            O que falta resolver? <span style={{ color: 'var(--danger)' }}>*</span>
            <textarea
              value={motivoTrava}
              onChange={(e) => setMotivoTrava(e.target.value)}
              rows={3}
              autoFocus
              placeholder="Ex.: falta cortar 15m do cabo 4mm · cliente vai confirmar a bitola"
              style={{ resize: 'vertical', fontWeight: 400 }}
            />
          </label>
        </div>
      </Modal>
    )}
    </>
  );
}

// Linhas de pagamento de uma venda que podem trocar de caixa: as que TEM caixa (crediario e
// credito da loja nao entram em caixa nenhum) e so em venda finalizada.
function pagamentosMoviveis(v: VendaHoje): PagamentoMovivel[] {
  if (v.status !== 'finalizada') return [];
  return (v.pagamentos || [])
    .filter((p) => p.pagamento_id != null && p.forma_pagamento_id != null && p.caixa_id != null)
    .map((p) => ({
      id: p.pagamento_id!,
      venda_numero: v.numero,
      cliente_nome: v.cliente_nome,
      forma_nome: p.forma,
      forma_pagamento_id: p.forma_pagamento_id!,
      valor: Number(p.valor || 0),
      caixa_id: p.caixa_id ?? null,
      caixa_nome: p.conta ?? null,
      finalizada_em: v.finalizada_em,
    }));
}

// Rotulo curto da forma na coluna de pagamentos do Vendas hoje — mesmo vocabulario do
// extrato de comissoes (Pix, Din, Cartão Créd, Cartão Déb); o resto mostra o nome do cadastro.
function formaAbreviada(tipo: string, nome: string): string {
  switch (tipo) {
    case 'pix': return 'Pix';
    case 'dinheiro': return 'Din';
    case 'cartao_credito': return 'Cartão Créd';
    case 'cartao_debito': return 'Cartão Déb';
    default: return nome;
  }
}

function VendasTab({ vendas, podeCancelar, podeDesfazerLinha, onCancelar, onCorrigirCaixa }: { vendas: VendaHoje[]; podeCancelar: boolean; podeDesfazerLinha: (v: VendaHoje) => boolean; onCancelar: (v: VendaHoje) => void; onCorrigirCaixa: (p: PagamentoMovivel[]) => void }) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  // null = carregando, [] = sem itens, undefined = ainda nao buscou
  const [itensPorVenda, setItensPorVenda] = useState<Record<number, ItemPedido[] | null>>({});
  // linha selecionada para navegacao por teclado (clique seleciona, setas movem)
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filtroVendedor, setFiltroVendedor] = useState<string>('');
  // Comprovante Pix sendo impresso (spinner) e menu de escolha quando a venda tem mais de um Pix.
  const [imprimindoCob, setImprimindoCob] = useState<number | null>(null);
  const [menuPix, setMenuPix] = useState<{ vendaId: number; x: number; y: number; comps: Array<{ cobranca_id: number; valor: string }> } | null>(null);

  // Imprime o comprovante (NAO fiscal) de uma transação Pix já paga. Abre a janela ANTES do await
  // (senão o browser bloqueia o popup pós-promise). Mesmo PDF 55mm da finalização da venda.
  async function imprimirComprovantePix(cobrancaId: number) {
    setMenuPix(null);
    const win = window.open('', '_blank');
    setImprimindoCob(cobrancaId);
    try {
      const token = getToken();
      const r = await fetch(`/api/pix/cobranca/${cobrancaId}/comprovante-pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(String(r.status));
      const blob = await r.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      if (win) win.location.href = url;
      else window.open(url, '_blank');
    } catch (e: any) {
      if (win) win.close();
      alert('Erro ao gerar comprovante Pix: ' + (e?.message ?? e));
    } finally {
      setImprimindoCob(null);
    }
  }

  // Clique na impressora: 1 Pix imprime direto; 2+ abre um menuzinho pra escolher qual transação.
  function clicarImprimirPix(e: React.MouseEvent, v: VendaHoje) {
    e.stopPropagation();
    const comps = v.comprovantes_pix ?? [];
    if (comps.length === 0) return;
    if (comps.length === 1) { void imprimirComprovantePix(comps[0]!.cobranca_id); return; }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuPix({ vendaId: v.id, x: rect.right, y: rect.bottom, comps });
  }

  // Fecha o menu de escolha ao clicar fora / rolar.
  useEffect(() => {
    if (!menuPix) return;
    const fechar = () => setMenuPix(null);
    window.addEventListener('click', fechar);
    window.addEventListener('scroll', fechar, true);
    return () => { window.removeEventListener('click', fechar); window.removeEventListener('scroll', fechar, true); };
  }, [menuPix]);

  // Vendedores únicos presentes nas vendas do dia, com contador
  const vendedoresUnicos = useMemo(() => {
    const mapa = new Map<string, number>();
    for (const v of vendas) {
      const nome = v.vendedor_nome ?? '—';
      mapa.set(nome, (mapa.get(nome) ?? 0) + 1);
    }
    return Array.from(mapa.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [vendas]);

  // Filtra por vendedor e ORDENA: Desvinculadas no topo, depois cronológico crescente.
  const vendasFiltradas = useMemo(() => {
    const base = filtroVendedor === '' ? vendas : vendas.filter((v) => (v.vendedor_nome ?? '—') === filtroVendedor);
    const isDesvinculada = (v: typeof vendas[0]) => {
      const decl = Number(v.pix_declarado ?? 0);
      return decl > 0.005
        && Number(v.pix_vinculado ?? 0) < decl - TOL_CENTAVO
        && !v.pix_validado_admin
        && Number(v.pix_conciliado ?? 0) < decl - TOL_CENTAVO;
    };
    return [...base].sort((a, b) => {
      const da = isDesvinculada(a) ? 0 : 1;
      const db = isDesvinculada(b) ? 0 : 1;
      if (da !== db) return da - db;
      return a.finalizada_em > b.finalizada_em ? 1 : -1;
    });
  }, [vendas, filtroVendedor]);

  // Rodape da lista: o total do dinheiro que a loja RECEBEU hoje. Crediario fica FORA — e fiado,
  // o dinheiro so entra na baixa do titulo (mesmo recorte que o Fluxo e a comissao ja usam,
  // `fp.tipo <> 'crediario'`). A exclusao e por LINHA de pagamento, nao por venda: venda mista
  // (parte dinheiro, parte fiado) entra pela parte que caiu no caixa. Cancelada nao soma.
  const resumoRodape = useMemo(() => {
    const validas = vendasFiltradas.filter((v) => v.status !== 'cancelada');
    let recebido = 0;
    let crediario = 0;
    for (const v of validas) {
      const cred = (v.pagamentos || []).reduce(
        (s, p) => (p.tipo === 'crediario' ? s + Number(p.valor || 0) : s), 0,
      );
      crediario += cred;
      recebido += totalLiquidoVenda(v) - cred;
    }
    return { qtd: validas.length, recebido, crediario };
  }, [vendasFiltradas]);

  // ↑/↓ movem a selecao; Enter expande/recolhe a linha selecionada
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (selectedId === null) return;
      const idx = vendasFiltradas.findIndex((v) => v.id === selectedId);
      if (idx === -1) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedId(vendasFiltradas[Math.min(idx + 1, vendasFiltradas.length - 1)]!.id);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedId(vendasFiltradas[Math.max(idx - 1, 0)]!.id);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        void toggleExpand(selectedId);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, vendasFiltradas]);

  // mantem a linha selecionada visivel ao navegar com as setas
  useEffect(() => {
    if (selectedId === null) return;
    document.querySelector(`[data-venda-id="${selectedId}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  async function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (itensPorVenda[id] === undefined) {
          setItensPorVenda((p) => ({ ...p, [id]: null }));
          api.get<{ itens?: ItemPedido[] }>(`/api/caixa/vendas/${id}/itens`)
            .then((r) => setItensPorVenda((p) => ({ ...p, [id]: r.itens ?? [] })))
            .catch(() => setItensPorVenda((p) => ({ ...p, [id]: [] })));
        }
      }
      return next;
    });
  }

  return (
    <div>
      {vendedoresUnicos.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Recebido por
          </label>
          <select
            value={filtroVendedor}
            onChange={(e) => { setFiltroVendedor(e.target.value); setSelectedId(null); }}
            style={{ minWidth: 180 }}
          >
            <option value="">Todos ({vendas.length})</option>
            {vendedoresUnicos.map(([nome, qtd]) => (
              <option key={nome} value={nome}>{nome} ({qtd})</option>
            ))}
          </select>
        </div>
      )}

      {vendasFiltradas.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          {filtroVendedor ? `Nenhuma venda de ${filtroVendedor} hoje` : 'Nenhuma venda finalizada hoje'}
        </div>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 36, textAlign: 'center' }}>#</th>
              <th>Numero</th>
              <th>Cliente</th>
              <th>Vendedor</th>
              <th>Operador</th>
              <th>Conta</th>
              <th>Pagamentos</th>
              <th>Pix</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Desconto</th>
              <th>Status</th>
              <th>Hora</th>
              <th style={{ textAlign: 'right', width: 100 }}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {vendasFiltradas.map((v) => {
              const expandido = expandedIds.has(v.id);
              const itens = itensPorVenda[v.id];
              const selecionado = selectedId === v.id;
              const descPerc = Number(v.desconto_total || 0);
              return (
                <Fragment key={v.id}>
                  <tr
                    data-venda-id={v.id}
                    onClick={() => setSelectedId(v.id)}
                    style={{
                      cursor: 'pointer',
                      ...(v.status === 'cancelada' ? { opacity: 0.55 } : {}),
                      ...(selecionado ? { background: 'rgba(37, 99, 235, 0.18)' } : {}),
                    }}
                  >
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => void toggleExpand(v.id)}
                        title={expandido ? 'Ocultar itens' : 'Ver itens'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 22, height: 22, borderRadius: 4, padding: 0,
                          border: '1px solid var(--input-border, #d1d5db)',
                          background: expandido ? 'var(--accent, #2563eb)' : 'transparent',
                          color: expandido ? '#fff' : 'var(--accent, #2563eb)',
                          cursor: 'pointer',
                        }}
                      >
                        {expandido ? <Minus size={12} /> : <Plus size={12} />}
                      </button>
                    </td>
                    <td style={{ fontWeight: 600 }}>{v.numero}</td>
                    <td>
                      {v.cliente_nome}
                      {v.orcamento_numero && (
                        <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{v.orcamento_numero}</div>
                      )}
                    </td>
                    <td>{v.vendedor_nome || '—'}</td>
                    <td>{v.operador_nome || '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {(() => {
                        // Conta/destino de cada recebimento (gaveta, Cofre, Mercado Pago, ...). Quase
                        // sempre uma so; venda com Pix + dinheiro pode ter duas — mostra as distintas.
                        const contas = [...new Set((v.pagamentos || []).map((p) => p.conta).filter(Boolean))] as string[];
                        if (contas.length === 0) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                        return contas.join(', ');
                      })()}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {/* Um pagamento por linha: valor primeiro (alinhado a direita, pra os numeros
                          ficarem um embaixo do outro) e a forma abreviada depois — pedido do Ruan. */}
                      <div style={{ display: 'inline-grid', gridTemplateColumns: 'auto auto', columnGap: 6, alignItems: 'baseline' }}>
                        {v.pagamentos?.map((p, i) => {
                          const parcelas = Number(p.parcelas || 1);
                          // Valor REAL cobrado nessa forma (valor pix + acrescimo do parcelamento) = valor da nota.
                          // Aqui mostra o que o cliente pagou; o pix (faturamento/caixa) fica na coluna Total.
                          const real = Number(p.valor || 0) + Number(p.acrescimo || 0);
                          const parceladoTxt = parcelas > 1 ? ` ${parcelas}x de ${formatMoedaSemSimbolo(real / parcelas)}` : '';
                          return (
                            <Fragment key={i}>
                              <strong className="cel-num" style={{ textAlign: 'right' }}>{formatMoedaSemSimbolo(real)}</strong>
                              <span style={{ whiteSpace: 'nowrap' }}>{formaAbreviada(p.tipo, p.forma)}{parceladoTxt}</span>
                            </Fragment>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const pixDecl = Number(v.pix_declarado ?? 0);
                        // Pix tem prioridade no selo (Vinculada/Desvinculada).
                        if (pixDecl > 0.005) {
                          const vinculada = Number(v.pix_vinculado ?? 0) >= pixDecl - TOL_CENTAVO || v.pix_validado_admin;
                          const conferida = !vinculada && Number(v.pix_conciliado ?? 0) >= pixDecl - TOL_CENTAVO;
                          if (vinculada) return (
                            <span style={{ backgroundColor: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                              Vinculada
                            </span>
                          );
                          if (conferida) return (
                            <span style={{ backgroundColor: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }} title="Pix conferido manualmente na Conciliação Bancária">
                              Conferida
                            </span>
                          );
                          return (
                            <span style={{ backgroundColor: '#dc2626', color: '#fff', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }} title="Venda em Pix sem recebimento bancário vinculado — confira a origem do dinheiro">
                              Desvinculada
                            </span>
                          );
                        }
                        // Cartao (debito/credito): conciliado pela maquininha MP (automatico, DA-238) OU
                        // pelo Conferir manual. O backend soma as duas coberturas em `cartao_conciliado`
                        // (DA-410) — contando so a manual, todo cartao da Point ficava vermelho pra sempre.
                        const cartaoDecl = Number(v.cartao_declarado ?? 0);
                        if (cartaoDecl > 0.005) {
                          const conciliado = Number(v.cartao_conciliado ?? 0) >= cartaoDecl - TOL_CENTAVO;
                          return conciliado ? (
                            <span title="Recebimento em cartão conciliado no banco" style={{ backgroundColor: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                              Conciliado
                            </span>
                          ) : (
                            <span title="Recebimento em cartão ainda não conciliado no banco — confira na aba Conciliação Bancária" style={{ backgroundColor: '#dc2626', color: '#fff', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                              A conciliar
                            </span>
                          );
                        }
                        // Demais formas (Dinheiro, Crediario, ...): nada a conciliar — selo neutro com a forma.
                        const formas = [...new Set((v.pagamentos || []).filter((p) => p.tipo !== 'pix').map((p) => p.forma))];
                        if (formas.length === 0) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                        return (
                          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                            {formas.map((f, i) => (
                              <span key={i} title="Forma de pagamento — nada a conciliar" style={{ backgroundColor: '#e5e7eb', color: '#374151', fontWeight: 600, fontSize: 11, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                                {f}
                              </span>
                            ))}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="cel-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {formatMoeda(totalLiquidoVenda(v))}
                      {acrescimoVenda(v) > 0.005 && (
                        <div
                          style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)' }}
                          title="Valor da nota (cobrado no cartão, já com o acréscimo do parcelamento). É só informação — NÃO entra em faturamento, caixa nem comissão."
                        >
                          nota {formatMoeda(valorRealVenda(v))}
                        </div>
                      )}
                    </td>
                    <td className="cel-num" style={{ textAlign: 'right', color: descPerc > 0.005 ? 'var(--danger)' : 'var(--text-muted)', fontWeight: descPerc > 0.005 ? 600 : 400 }}>
                      {descPerc > 0.005 ? `${descPerc.toFixed(2)}%` : '—'}
                    </td>
                    <td>
                      <span className={v.status === 'finalizada' ? 'badge-verde' : 'badge-vermelho'}>{v.status}</span>
                    </td>
                    <td style={{ fontSize: 11 }}>{formatDataHoraSP(v.finalizada_em)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {(v.comprovantes_pix?.length ?? 0) > 0 && (
                        <button
                          type="button"
                          className="btn-icon"
                          title="Imprimir comprovante do Pix (máquina de cartão)"
                          onClick={(e) => clicarImprimirPix(e, v)}
                          disabled={imprimindoCob !== null && v.comprovantes_pix!.some((c) => c.cobranca_id === imprimindoCob)}
                          style={{ color: '#15803d' }}
                        >
                          {imprimindoCob !== null && v.comprovantes_pix!.some((c) => c.cobranca_id === imprimindoCob)
                            ? <Loader2 size={14} className="animate-spin" />
                            : <Printer size={14} />}
                        </button>
                      )}
                      {/* Corrigir o caixa da venda (DA-351) — mesmo gate do cancelar (so admin):
                          mexe em dinheiro ja contabilizado e recarimba turno fechado. */}
                      {podeCancelar && pagamentosMoviveis(v).length > 0 && (
                        <button
                          type="button"
                          className="btn-icon"
                          title="Corrigir o caixa desta venda"
                          onClick={() => onCorrigirCaixa(pagamentosMoviveis(v))}
                          style={{ color: 'var(--primary)' }}
                        >
                          <ArrowLeftRight size={14} />
                        </button>
                      )}
                      {/* O ICONE nao muda ([DA-442]): a porta e o 🚫 de sempre, e quem se adapta e o
                          modal. Trocar o icone por linha faria quase toda a lista virar outra coisa
                          depois de 30 min — mudanca visual que ninguem pediu. So o tooltip conta a
                          verdade de hoje. */}
                      {podeDesfazerLinha(v) && (
                        <button type="button" className="btn-icon"
                          title={v.nota_desfazer?.expirado && !v.nota_desfazer.ja_devolvida
                            ? 'Desfazer venda (fora do prazo de cancelamento — registra devolução)'
                            : 'Cancelar venda'}
                          onClick={() => onCancelar(v)} style={{ color: '#dc2626' }}>
                          <Ban size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandido && (
                    <tr style={{ background: 'rgba(59, 130, 246, 0.05)' }}>
                      <td colSpan={13} style={{ padding: 0 }}>
                        <div style={{ padding: '12px 32px' }}>
                          {acrescimoVenda(v) > 0.005 && (() => {
                            const pagCartao = (v.pagamentos || []).find((p) => Number(p.acrescimo || 0) > 0.005);
                            const parcelas = Number(pagCartao?.parcelas || 1);
                            return (
                              <div style={{
                                marginBottom: 12, maxWidth: 360, fontSize: 13,
                                background: 'var(--panel-bg, #fff)', borderRadius: 6,
                                border: '1px solid var(--input-border, #e5e7eb)', overflow: 'hidden',
                              }}>
                                <div style={{
                                  padding: '6px 12px', fontWeight: 700, fontSize: 12,
                                  background: 'var(--grid-header-bg, #f3f4f6)', color: 'var(--grid-header-fg, #4b5563)',
                                }}>
                                  Cartão Crédito{parcelas > 1 ? ` — ${parcelas}x` : ''}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 4, padding: '8px 12px' }}>
                                  <span style={{ color: 'var(--text-muted)' }}>Valor base (pix, comissão)</span>
                                  <span className="cel-num" style={{ textAlign: 'right' }}>{formatMoeda(totalLiquidoVenda(v))}</span>
                                  <span style={{ color: 'var(--text-muted)' }}>Acréscimo do parcelamento</span>
                                  <span className="cel-num" style={{ textAlign: 'right' }}>+ {formatMoeda(acrescimoVenda(v))}</span>
                                  <span style={{ fontWeight: 700, borderTop: '1px solid var(--input-border, #e5e7eb)', paddingTop: 4, marginTop: 2 }}>Cobrado no cartão (nota)</span>
                                  <span style={{ display: 'inline-flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 10, borderTop: '1px solid var(--input-border, #e5e7eb)', paddingTop: 4, marginTop: 2 }}>
                                    {parcelas > 1 && (
                                      <span className="cel-num" style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-muted)' }}>
                                        {parcelas}x de {formatMoeda(valorRealVenda(v) / parcelas)}
                                      </span>
                                    )}
                                    <strong className="cel-num">{formatMoeda(valorRealVenda(v))}</strong>
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                          {itens === undefined || itens === null ? (
                            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Carregando itens...</div>
                          ) : itens.length === 0 ? (
                            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Nenhum item</div>
                          ) : (
                            <table style={{ width: '100%', fontSize: 13, background: 'var(--panel-bg, #fff)', borderRadius: 6, border: '1px solid var(--input-border, #e5e7eb)' }}>
                              <thead>
                                <tr style={{ background: 'var(--grid-header-bg, #f3f4f6)', color: 'var(--grid-header-fg, #4b5563)' }}>
                                  <th style={{ textAlign: 'center', padding: '6px 8px', width: 32, fontSize: 11, fontWeight: 600 }}>#</th>
                                  <th style={{ padding: '6px 8px', width: 44, fontSize: 11, fontWeight: 600 }}></th>
                                  <th style={{ textAlign: 'left', padding: '6px 12px', width: 80, fontSize: 11, fontWeight: 600 }}>Cod.</th>
                                  <th style={{ textAlign: 'left', padding: '6px 12px', fontSize: 11, fontWeight: 600 }}>
                                    Descricao <span style={{ marginLeft: 8, opacity: 0.6, fontWeight: 400 }}>({v.numero})</span>
                                  </th>
                                  <th style={{ textAlign: 'right', padding: '6px 12px', width: 110, fontSize: 11, fontWeight: 600 }}>Qtd</th>
                                  <th style={{ textAlign: 'right', padding: '6px 12px', width: 110, fontSize: 11, fontWeight: 600 }}>Unit.</th>
                                  <th style={{ textAlign: 'right', padding: '6px 12px', width: 130, fontSize: 11, fontWeight: 600 }}>Subtotal</th>
                                </tr>
                              </thead>
                              <tbody>
                                {itens.map((it, idx) => (
                                  <tr key={it.id} style={{ borderTop: '1px solid var(--input-border, #f3f4f6)' }}>
                                    <td style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                                    <td style={{ padding: '4px 8px' }}>
                                      {it.foto_url ? (
                                        <FotoProduto url={it.foto_url} tam={36} />
                                      ) : (
                                        <div style={{ width: 36, height: 36, borderRadius: 4, background: '#f3f4f6', border: '1px solid var(--input-border, #e5e7eb)' }} />
                                      )}
                                    </td>
                                    <td style={{ padding: '6px 12px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{it.codigo_interno || '—'}</td>
                                    <td style={{ padding: '6px 12px' }}>{it.produto_descricao || '—'}</td>
                                    <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right' }}>
                                      {Number(it.quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                                      {it.unidade_sigla ? ` ${it.unidade_sigla}` : ''}
                                    </td>
                                    <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right' }}>{formatMoeda(it.preco_unitario)}</td>
                                    <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>{formatMoeda(it.subtotal)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          {/* colSpan 8 = as colunas ate "Pix", pra o valor cair EMBAIXO da coluna Total (13 colunas). */}
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
              <td colSpan={8} style={{ textAlign: 'right', fontSize: 13, color: 'var(--text-muted)', paddingRight: 8 }}>
                {filtroVendedor && `${filtroVendedor} — `}
                Total de {resumoRodape.qtd} {resumoRodape.qtd === 1 ? 'venda' : 'vendas'}
                {resumoRodape.crediario > 0.005 && (
                  <span style={{ fontWeight: 400 }}>
                    {' '}· crediario {formatMoeda(resumoRodape.crediario)} fora
                  </span>
                )}
              </td>
              <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoeda(resumoRodape.recebido)}</td>
              <td colSpan={4} />
            </tr>
          </tfoot>
        </table>
      )}

      {/* Menu de escolha quando a venda tem mais de um Pix — fixed (a tabela rola). */}
      {menuPix && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', left: menuPix.x, top: menuPix.y, transform: 'translateX(-100%)',
            zIndex: 1000, background: 'var(--panel-bg, #fff)', border: '1px solid var(--input-border, #d1d5db)',
            borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.18)', padding: 4, minWidth: 180,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', padding: '6px 10px 4px', textTransform: 'uppercase' }}>
            Qual comprovante?
          </div>
          {menuPix.comps.map((c) => (
            <button
              key={c.cobranca_id}
              type="button"
              onClick={() => void imprimirComprovantePix(c.cobranca_id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                padding: '8px 10px', borderRadius: 6, border: 0, background: 'transparent',
                cursor: 'pointer', fontSize: 13, color: 'var(--text-color)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--grid-header-bg, #f3f4f6)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <Printer size={14} style={{ color: '#15803d', flexShrink: 0 }} />
              Pix de <strong className="cel-num">{formatMoeda(c.valor)}</strong>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SessoesTab({ sessoes, contas, podeOperar, podeConferir, usuarioId, expandidos, vendasPorCaixa, onToggleExpand, onMovimentacao, onTransferencia, onAbrirTurno, onFecharTurno, onConfirmarTurno, onConferirGaveta, onConferirRecebimento, onReabrirTurno, onResolverDivergencia }: {
  sessoes: SessaoHoje[]; contas: CaixaConta[]; podeOperar: boolean; podeConferir: boolean; usuarioId: number | null;
  expandidos: Set<number>; vendasPorCaixa: Record<number, VendaHoje[] | null>;
  onToggleExpand: (caixaId: number) => void;
  onMovimentacao: () => void; onTransferencia: () => void;
  onAbrirTurno: (c: CaixaConta) => void;
  onFecharTurno: (c: CaixaConta, s: SessaoHoje) => void;
  onConfirmarTurno: (c: CaixaConta, s: SessaoHoje) => void;
  onConferirGaveta: (c: CaixaConta, s: SessaoHoje) => void;
  onConferirRecebimento: (c: CaixaConta, s: SessaoHoje) => void;
  onReabrirTurno: (c: CaixaConta, s: SessaoHoje) => void;
  onResolverDivergencia: (c: CaixaConta, s: SessaoHoje) => void;
}) {
  // Data de hoje em Sao Paulo no mesmo formato do `data` da sessao (YYYY-MM-DD) — usada pra
  // distinguir turno aberto HOJE de gaveta que amanheceu aberta. 'en-CA' da exatamente esse formato.
  const hojeSP = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  return (
    <div>
      {podeOperar && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button type="button" className="btn-secundario" onClick={onMovimentacao}>
            <ArrowUpDown size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Entrada / Saida
          </button>
          <button type="button" className="btn-secundario" onClick={onTransferencia}>
            <ArrowLeftRight size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Transferir
          </button>
        </div>
      )}

      <table className="tabela" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 36, textAlign: 'center' }}>#</th>
            <th>Caixa</th>
            <th>Tipo</th>
            {/* [DA-552] O saldo vem LOGO APOS o nome, antes das colunas de turno: as 6 contas
                bancarias tem todas as colunas de turno vazias (elas nao tem turno), e era essa
                linha muda que fazia o admin procurar o saldo em outra tela.
                ⚠️ NAO adiantar `width` aqui: eu tentei (112 no Saldo, 76 no Tipo) pra devolver 30px
                e MEDI na release publicada que os dois valores sao ignorados — a tabela e
                `width: 100%` com layout automatico, e o piso e o min-content da coluna ACOES (tres
                botoes empilhados). Largura escrita e inerte e pior que largura ausente, porque
                parece resolvido. Quem quiser estreitar de verdade mexe no conteudo de ACOES. */}
            <th style={{ textAlign: 'right' }}>Saldo</th>
            <th>Status</th>
            <th>Aberto por</th>
            <th>Aberto em</th>
            <th style={{ textAlign: 'right' }}>Abertura</th>
            <th style={{ textAlign: 'right' }}>Fechamento</th>
            <th style={{ textAlign: 'right' }}>A recolher</th>
            <th style={{ textAlign: 'right' }}>Diferenca</th>
            <th style={{ textAlign: 'right', width: 140 }}>Acoes</th>
          </tr>
        </thead>
        <tbody>
          {contas.map((c) => {
            // Turno: prioriza a sessao ABERTA do caixa; senao mostra a mais recente do dia.
            const doCaixa = sessoes.filter((x) => x.caixa_id === c.id);
            const s = doCaixa.find((x) => x.status === 'aberto') ?? doCaixa[doCaixa.length - 1];
            const ehDinheiro = c.tipo === 'dinheiro';
            const div = s?.divergencia != null ? Number(s.divergencia) : null;
            // Divergencia de ABERTURA (contou != troco deixado no fechamento anterior) ainda nao
            // resolvida pelo admin — aparece na faixa de aviso e ganha botao "Resolver" aqui.
            const divAbertura = s?.divergencia_abertura != null ? Number(s.divergencia_abertura) : null;
            const temDivAbertura = divAbertura != null && Math.abs(divAbertura) >= 0.005 && !s?.divergencia_abertura_resolvida_em;
            // So gaveta de dinheiro aguarda contagem do troco; conta tipo banco (Cofre) nunca.
            const aguardando = ehDinheiro && !!s && s.status === 'aberto' && !s.abertura_confirmada_em;
            // Turno que amanheceu aberto (gaveta esquecida — o scheduler nao auto-fecha dinheiro,
            // DA-333). O `sessoes-hoje` agora traz ele mesmo sendo de outra data, entao avisa DE
            // QUE DIA e: "Aberta" pelado faria parecer que alguem abriu hoje de manha.
            const abertoDeOutroDia = !!s && s.status === 'aberto' && String(s.data).slice(0, 10) !== hojeSP;
            const expandido = expandidos.has(c.id);
            const vendasCaixa = vendasPorCaixa[c.id];
            // Caixa de CONTA (DA-127): sem turno. Tem movimento se existe sessao do dia (auto-criada
            // pelo garantirSessao quando passou pagamento). "Conferido" quando ja fechou (status='fechado').
            const contaConferida = !ehDinheiro && !!s && s.status === 'fechado';
            const contaAConferir = !ehDinheiro && !!s && s.status !== 'fechado';
            return (
              <Fragment key={c.id}>
                <tr>
                  <td style={{ textAlign: 'center' }} onDoubleClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => onToggleExpand(c.id)}
                      title={expandido ? 'Ocultar vendas do caixa' : 'Ver vendas finalizadas neste caixa hoje'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 22, height: 22, borderRadius: 4, padding: 0,
                        border: '1px solid var(--input-border, #d1d5db)',
                        background: expandido ? 'var(--accent, #2563eb)' : 'transparent',
                        color: expandido ? '#fff' : 'var(--accent, #2563eb)',
                        cursor: 'pointer',
                      }}
                    >
                      {expandido ? <Minus size={12} /> : <Plus size={12} />}
                    </button>
                  </td>
                  <td style={{ fontWeight: 600 }}>{c.nome}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.tipo}</td>
                  {/* Valor em AZUL NEGRITO, o mesmo dos botoes de acao da linha (pedido dele,
                      15/08/2026). `var(--primary)`, nunca o hex `#1d4ed8` medido na tela: hex
                      cravado quebra o tema escuro e os temas cobre.
                      ⚠️ A conta NEGATIVA fica de fora e segue laranja: ela nao pode ler igual a
                      conta saudavel numa tela de dinheiro, e o azul e' a cor do "esta tudo certo"
                      aqui. Mesmo sinal dos modais e da faixa do topo ([DA-551]). */}
                  <td className="cel-num" style={{ textAlign: 'right' }}>
                    {c.saldo == null ? (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    ) : Number(c.saldo) < 0 ? (
                      <span
                        style={{ background: '#f97316', color: '#000', padding: '1px 5px', borderRadius: 3, fontWeight: 700 }}
                        title="Conta negativa: saiu dinheiro que nunca foi lancado entrando. Nenhuma saida passa ate a entrada ser lancada."
                      >
                        {formatMoeda(Number(c.saldo))}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--primary)', fontWeight: 700 }}>
                        {formatMoeda(Number(c.saldo))}
                      </span>
                    )}
                  </td>
                  <td>
                    {ehDinheiro ? (
                      s ? (
                        aguardando
                          ? <span style={{ backgroundColor: '#fde68a', color: '#000', fontWeight: 700, padding: '1px 8px', borderRadius: 999, fontSize: 12 }}>Aguardando conferência</span>
                          : abertoDeOutroDia
                          ? <span title="Este turno ficou aberto de um dia anterior. Feche ele contando a gaveta antes de abrir o turno de hoje."
                              style={{ backgroundColor: '#f97316', color: '#000', fontWeight: 700, padding: '1px 8px', borderRadius: 999, fontSize: 12 }}>
                              Aberta desde {formatDataSP(s.data)}
                            </span>
                          : s.status === 'aberto' ? <span className="badge-verde">Aberta</span> : <span className="badge-cinza">Fechada</span>
                      ) : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sem movimento hoje</span>
                    ) : (
                      // Caixa de CONTA (DA-127): sem turno, status reflete a conferencia do recebimento.
                      contaConferida
                        ? <span className="badge-cinza">Conferido ✓</span>
                        : contaAConferir
                          ? <span style={{ backgroundColor: '#fde68a', color: '#000', fontWeight: 700, padding: '1px 8px', borderRadius: 999, fontSize: 12 }}>A conferir</span>
                          : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>
                  <td>{ehDinheiro ? (s?.operador_abertura_nome || '—') : '—'}</td>
                  <td style={{ fontSize: 11 }}>{ehDinheiro && s?.aberto_em ? formatDataHoraSP(s.aberto_em) : '—'}</td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>{ehDinheiro && s ? formatMoeda(s.valor_abertura) : '—'}</td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>{s?.valor_fechamento ? formatMoeda(s.valor_fechamento) : '—'}</td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>
                    {ehDinheiro && s?.status === 'fechado' && s.valor_fechamento != null
                      ? formatMoeda(Math.max(0, Number(s.valor_fechamento) - Number(s.valor_abertura)))
                      : ehDinheiro && s?.status === 'aberto' && s.recebido_turno != null
                      ? (
                        <div style={{ lineHeight: 1.35, fontWeight: 400 }}>
                          <div title="Vendas em dinheiro recebidas neste turno — vai pro Cofre ao fechar">
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', marginRight: 4 }}>recebido</span>
                            {formatMoeda(s.recebido_turno)}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }} title="Esperado na gaveta agora = troco da abertura + recebido − sangrias + suprimentos">
                            <span style={{ fontSize: 10, marginRight: 4 }}>esperado</span>
                            {formatMoeda(s.esperado_atual ?? 0)}
                          </div>
                        </div>
                      )
                      : '—'}
                  </td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>
                    {temDivAbertura
                      ? <span title="Diferença na abertura: o que foi contado não bateu com o troco deixado no fechamento anterior"
                          style={{ backgroundColor: '#ef4444', color: '#000', fontWeight: 700, padding: '1px 6px', borderRadius: 4 }}>
                          abertura {divAbertura! > 0 ? '+' : '−'}{formatMoeda(Math.abs(divAbertura!))}
                        </span>
                      : div == null ? '—' : Math.abs(div) < 0.005
                      ? <span style={{ color: 'var(--text-muted)' }}>ok</span>
                      : <span style={{ backgroundColor: '#f59e0b', color: '#000', fontWeight: 700, padding: '1px 6px', borderRadius: 4 }}>
                          {div > 0 ? '+' : '−'}{formatMoeda(Math.abs(div))}
                        </span>}
                  </td>
                  <td>
                    {/* Faixa flex: as acoes ficam lado a lado, do mesmo tamanho, em azul solido
                        (`.caixa-acoes` / `.caixa-acao-btn`). O espacamento vem do `gap`, por isso
                        os botoes nao carregam mais margin/fontSize inline. */}
                    <div className="caixa-acoes">
                    {podeConferir && temDivAbertura && (
                      <button type="button" className="caixa-acao-btn caixa-acao-btn--atencao" onClick={() => onResolverDivergencia(c, s!)}>
                        Resolver diferença
                      </button>
                    )}
                    {podeOperar && aguardando && (
                      <button type="button" className="caixa-acao-btn caixa-acao-btn--atencao" onClick={() => onConfirmarTurno(c, s!)}>
                        Conferir e assumir
                      </button>
                    )}
                    {podeOperar && ehDinheiro && (!s || s.status !== 'aberto') && (
                      <button type="button" className="caixa-acao-btn" onClick={() => onAbrirTurno(c)}>
                        Abrir turno
                      </button>
                    )}
                    {podeOperar && ehDinheiro && s && s.status === 'aberto' && !aguardando && (
                      <button type="button" className="caixa-acao-btn" onClick={() => onConferirGaveta(c, s)}>
                        <Calculator size={12} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Conferir gaveta
                      </button>
                    )}
                    {/* A data no proprio botao (DA-343): o operador precisa saber QUAL dia esta
                        fechando na hora de agir. Antes ela so existia no selo laranja "Aberta desde",
                        do outro lado da linha — e o turno que atravessou o dia fecha por aqui igual. */}
                    {podeOperar && ehDinheiro && s && s.status === 'aberto' && (
                      <button type="button" className="caixa-acao-btn" onClick={() => onFecharTurno(c, s)}>
                        <Lock size={12} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Fechar turno de {formatDataSP(s.data).slice(0, 5)}
                      </button>
                    )}
                    {/* ⛔ O "Fechar sem contagem" foi REMOVIDO em 2026-09-05, a pedido dele. Ele nasceu
                        na [DA-342] D3 pro caixa que "nao TEM cedula pra contar", e a premissa era que o
                        Movimento de Caixa fosse um livro-caixa. Nao e': o dinheiro dele mora no COFRE, e
                        o Ruan guarda ~R$ 400 de troco na loja pra trocar com as vendedoras — resposta
                        dele a pergunta que a [DA-343] deixou aberta ("decidir o que o Movimento e'").
                        Sendo gaveta, fecha por UMA porta, sempre contando, como as gavetas com dono.
                        Fechar pelo esperado sem contar era o atalho que desfazia o controle: foi por ele
                        que o turno de 30/07 fechou com uma falta de R$ 993,00 que quase certamente era
                        dinheiro ja levado ao cofre e nunca transferido no sistema. */}
                    {/* Reabrir turno fechado por engano — so admin/gerente, so gaveta de dinheiro. Desfaz o
                        fechamento (limpa a diferenca falsa) pra refechar certo contando a gaveta + transferir. */}
                    {podeConferir && ehDinheiro && s && s.status === 'fechado' && (
                      <button type="button" className="caixa-acao-btn" onClick={() => onReabrirTurno(c, s)}>
                        <Undo2 size={12} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Reabrir turno
                      </button>
                    )}
                    {/* Caixa de CONTA (DA-127): conferencia de recebimento — so admin/gerente, so com movimento ainda nao conferido. */}
                    {podeConferir && contaAConferir && s && (
                      <button type="button" className="caixa-acao-btn" onClick={() => onConferirRecebimento(c, s)}>
                        <ClipboardList size={12} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Conferir recebimento
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
                {expandido && (
                  <tr style={{ background: 'rgba(59, 130, 246, 0.05)' }}>
                    {/* 12 = 11 + a coluna Saldo da [DA-552]. Mexeu em coluna, mexa aqui. */}
                    <td colSpan={12} style={{ padding: 0 }}>
                      <div style={{ padding: '12px 32px' }}>
                        {vendasCaixa === undefined || vendasCaixa === null ? (
                          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Carregando vendas...</div>
                        ) : vendasCaixa.length === 0 ? (
                          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Nenhuma venda finalizada neste caixa hoje</div>
                        ) : (
                          <table style={{ width: '100%', fontSize: 13, background: 'var(--panel-bg, #fff)', borderRadius: 6, border: '1px solid var(--input-border, #e5e7eb)' }}>
                            <thead>
                              <tr style={{ background: 'var(--grid-header-bg, #f3f4f6)', color: 'var(--grid-header-fg, #4b5563)' }}>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Pedido</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', width: 56, fontWeight: 600, fontSize: 11 }}>Cod.</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Cliente</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Data</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Forma de Pagamento</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Tipo NF</th>
                                <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Total</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Vendedor</th>
                                <th style={{ textAlign: 'right', padding: '6px 12px', width: 80, fontWeight: 600, fontSize: 11 }}>Desconto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const finalizadas = vendasCaixa.filter((v) => v.status === 'finalizada');
                                return finalizadas.map((v, idx) => {
                                const tipoLabel = v.tipo_documento === 'nfe' ? 'A4' : v.tipo_documento === 'nfce' ? 'Consumidor' : '—';
                                const descPerc = Number(v.desconto_total || 0);
                                const formasPag = v.pagamentos?.map((p) => p.forma).join(', ') || '—';
                                return (
                                  <tr key={v.id} style={{ borderTop: idx > 0 ? '1px solid var(--input-border, #f3f4f6)' : undefined }}>
                                    <td style={{ padding: '6px 12px', fontWeight: 600 }}>{v.numero}</td>
                                    <td style={{ padding: '6px 12px' }} className="text-gray-500 font-bold no-underline">{v.cliente_codigo || '—'}</td>
                                    <td style={{ padding: '6px 12px' }}>{v.cliente_nome}</td>
                                    <td style={{ padding: '6px 12px', fontSize: 11 }}>{formatDataHoraSP(v.finalizada_em)}</td>
                                    <td style={{ padding: '6px 12px', fontWeight: 600 }}>{formasPag}</td>
                                    <td style={{ padding: '6px 12px', color: 'var(--text-muted)' }}>{tipoLabel}</td>
                                    <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>{formatMoeda(totalLiquidoVenda(v))}</td>
                                    <td style={{ padding: '6px 12px' }}>{v.vendedor_nome || '—'}</td>
                                    <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right', color: descPerc > 0.005 ? 'var(--danger)' : 'var(--text-muted)', fontWeight: descPerc > 0.005 ? 600 : 400 }}>
                                      {descPerc > 0.005 ? `${descPerc.toFixed(2)}%` : '—'}
                                    </td>
                                  </tr>
                                );
                                });
                              })()}
                            </tbody>
                            <tfoot>
                              <tr style={{ borderTop: '2px solid var(--input-border, #e5e7eb)', fontWeight: 700 }}>
                                <td colSpan={6} style={{ padding: '6px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>
                                  {(() => {
                                    const finalizadas = vendasCaixa.filter((v) => v.status === 'finalizada');
                                    const totalCaixa = finalizadas.reduce((acc, v) => acc + totalLiquidoVenda(v), 0);
                                    return podeConferir
                                      ? `Total: ${formatMoeda(totalCaixa)} (${finalizadas.length} ${finalizadas.length === 1 ? 'venda' : 'vendas'})`
                                      : 'Total suas vendas:';
                                  })()}
                                </td>
                                <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 700 }}>
                                  {formatMoeda(vendasCaixa.filter((v) => v.status === 'finalizada').reduce((acc, v) => acc + totalLiquidoVenda(v), 0))}
                                </td>
                                <td colSpan={2} />
                              </tr>
                            </tfoot>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        {(() => {
          const totalRecolher = contas.reduce((acc, c) => {
            if (c.tipo !== 'dinheiro') return acc;
            const doCaixa = sessoes.filter((x) => x.caixa_id === c.id);
            const s = doCaixa.find((x) => x.status === 'aberto') ?? doCaixa[doCaixa.length - 1];
            if (!s || s.status !== 'fechado' || s.valor_fechamento == null) return acc;
            return acc + Math.max(0, Number(s.valor_fechamento) - Number(s.valor_abertura));
          }, 0);
          if (totalRecolher < 0.005) return null;
          return (
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--input-border, #e5e7eb)', fontWeight: 700 }}>
                <td colSpan={7} style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>
                  Total a recolher hoje
                </td>
                <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }}>
                  {formatMoeda(totalRecolher)}
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          );
        })()}
      </table>
    </div>
  );
}

interface FiltroTransferencias { de: string; ate: string; origem: string }

// Dia (YYYY-MM-DD) no fuso SP — casa com o value do <input type="date">.
const diaISO = (val: string | Date | null | undefined): string | null => {
  const d = parseSPDate(val);
  return d ? new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(d) : null;
};

// Fila de transferencias em transito (dois pares de olhos). A grana SAI da origem na hora; so ENTRA
// no destino quando o admin/gerente baixa. Operadora ve so as proprias (backend ja filtra) como
// "Aguardando validação"; admin/gerente ve todas e tem o botao "Baixar".
function TransferenciasTab({ transferencias, filtro, onFiltro, podeBaixar, onBaixar }: {
  transferencias: TransferenciaPendente[];
  filtro: FiltroTransferencias;
  onFiltro: (f: FiltroTransferencias) => void;
  podeBaixar: boolean;
  onBaixar: (t: TransferenciaPendente) => void;
}) {
  const { de, ate, origem } = filtro;

  const origensUnicas = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of transferencias) m.set(t.origem_nome, (m.get(t.origem_nome) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));
  }, [transferencias]);

  const filtradas = useMemo(() => transferencias.filter((t) => {
    if (origem && t.origem_nome !== origem) return false;
    // Pendente e TAREFA, nao historico: nunca some pelo filtro de data (so as confirmadas sao
    // filtraveis por dia). Era o que escondia a transferencia pendente da Rafaela quando o filtro
    // estava num dia anterior. Ver project_pontas_soltas_do_caixa #2.
    if (t.status !== 'pendente' && (de || ate)) {
      const dia = diaISO(t.criado_em);
      if (!dia) return false;
      if (de && dia < de) return false;
      if (ate && dia > ate) return false;
    }
    return true;
  }), [transferencias, origem, de, ate]);

  const temFiltro = Boolean(de || ate || origem);

  if (transferencias.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhuma transferência registrada</div>;
  }
  return (
    <div>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
        Transferir é entregar dinheiro pra outra conta: ele <strong>sai da origem na hora</strong>, mas
        só <strong>entra oficialmente no destino quando a gerência confirma o recebimento</strong>.
        Até lá fica <em>em trânsito</em>.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          De
          <input type="date" value={de} max={ate || undefined}
            onChange={(e) => onFiltro({ ...filtro, de: e.target.value })} style={{ width: 150 }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Até
          <input type="date" value={ate} min={de || undefined}
            onChange={(e) => onFiltro({ ...filtro, ate: e.target.value })} style={{ width: 150 }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Origem
          <select value={origem} onChange={(e) => onFiltro({ ...filtro, origem: e.target.value })} style={{ minWidth: 180 }}>
            <option value="">Todas ({transferencias.length})</option>
            {origensUnicas.map(([nome, qtd]) => (
              <option key={nome} value={nome}>{nome} ({qtd})</option>
            ))}
          </select>
        </label>
        {temFiltro && (
          <button type="button" className="btn-secundario" style={{ fontSize: 12 }}
            onClick={() => onFiltro({ de: '', ate: '', origem: '' })}>
            Limpar
          </button>
        )}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, marginLeft: 'auto' }}>
          {filtradas.length} {filtradas.length === 1 ? 'transferência' : 'transferências'}
        </span>
      </div>

      {filtradas.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          Nenhuma transferência com esse filtro
        </div>
      ) : (
      <table className="tabela" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Origem</th>
            <th>Destino</th>
            <th style={{ textAlign: 'right' }}>Valor</th>
            <th>Registrou</th>
            <th>Quando</th>
            <th>Status</th>
            <th style={{ textAlign: 'right', width: 120 }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {filtradas.map((t) => {
            const pendente = t.status === 'pendente';
            return (
              <tr key={t.id} style={pendente ? undefined : { opacity: 0.7 }}>
                <td style={{ fontWeight: 600 }}>{t.origem_nome}</td>
                <td style={{ fontWeight: 600 }}>{t.destino_nome}</td>
                <td className="cel-num" style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoeda(t.valor)}</td>
                <td>{t.operador_nome || '—'}</td>
                <td>{formatDataHoraSP(t.criado_em)}</td>
                <td>
                  {pendente ? (
                    // Daltonismo-safe: "aguardando" = destaque amarelo solido + letra preta (nao verde/vermelho).
                    <span style={{ backgroundColor: '#fde68a', color: '#000', fontWeight: 700, padding: '2px 8px', borderRadius: 999, fontSize: 12 }}>
                      Aguardando validação
                    </span>
                  ) : (
                    <span className="status-pill status-pill-ativo" title={t.confirmada_por_nome ? `Confirmado por ${t.confirmada_por_nome}` : undefined}>
                      <span className="status-pill-dot" />
                      Confirmada{t.confirmada_por_nome ? ` · ${t.confirmada_por_nome}` : ''}
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {podeBaixar && pendente && (
                    <button type="button" className="btn-primario" style={{ fontSize: 12 }} onClick={() => onBaixar(t)}>
                      Baixar
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      )}
    </div>
  );
}

// Conferencia de recebimento de um caixa de CONTA (DA-127): admin/gerente informa o saldo visto
// no app/extrato/maquininha (contagem CEGA — o esperado so e revelado DEPOIS de confirmar) e o
// sistema compara, grava a divergencia e fecha a sessao.
function ConferirRecebimentoModal({ sessaoId, caixaNome, onFechar, onConcluido }: {
  sessaoId: number; caixaNome: string; onFechar: () => void; onConcluido: () => void;
}) {
  const aviso = useAviso();
  const [contado, setContado] = useState<number>(0);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<{ esperado: number; contado: number; divergencia: number } | null>(null);

  async function confirmar() {
    setEnviando(true);
    try {
      const r = await api.post<{ ok: boolean; esperado: number; contado: number; divergencia: number }>(
        `/api/caixa/conferir-recebimento/${sessaoId}`, { contado },
      );
      setResultado({ esperado: r.esperado, contado: r.contado, divergencia: r.divergencia });
      onConcluido();
    } catch (e: any) {
      aviso.erro(e.message);
    } finally {
      setEnviando(false);
    }
  }

  if (resultado) {
    const div = resultado.divergencia;
    const confere = Math.abs(div) < 0.005;
    return (
      <Modal aberto onFechar={onFechar} titulo={`Recebimento conferido — ${caixaNome}`} largura={480} compacto
        rodape={<button type="button" className="btn-primario" onClick={onFechar}>Concluir</button>}
      >
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <span>Esperado no sistema</span><span className="cel-num">{formatMoeda(resultado.esperado)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <span>Você informou</span><span className="cel-num">{formatMoeda(resultado.contado)}</span>
          </div>
          <div style={{
            marginTop: 6, padding: '12px 14px', borderRadius: 8,
            backgroundColor: confere ? '#fde68a' : '#f59e0b',
            border: `2px solid ${confere ? '#ca8a04' : '#b45309'}`,
            color: '#000', fontWeight: 700, textAlign: 'center',
          }}>
            {confere
              ? '✓ CONFERIDO — bateu certo'
              : `DIFERENÇA: ${div > 0 ? 'SOBROU' : 'FALTOU'} ${formatMoeda(Math.abs(div))}`}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal aberto onFechar={onFechar} titulo={`Conferir ${caixaNome}`} largura={480}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={onFechar} disabled={enviando}>Cancelar</button>
          <button type="button" className="btn-primario" onClick={confirmar} disabled={enviando}>
            {enviando ? 'Enviando...' : 'Confirmar'}
          </button>
        </>
      }
    >
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
        Confira no app/extrato/comprovante quanto este caixa recebeu hoje e informe o valor.
        A diferença em relação ao esperado aparece depois de confirmar.
      </p>
      <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
        Saldo visto no app/extrato/comprovante
      </label>
      <MoneyInput valor={contado} onChangeValor={setContado} style={{ width: '100%' }} />
    </Modal>
  );
}

// Lupa do turno: lista os movimentos de dinheiro desde a ultima conferencia que bateu (ou desde
// a abertura). E a ferramenta pra investigar uma diferenca numa janela curta — um Pix lancado
// como dinheiro aparece como entrada que nao deveria; um troco errado fica localizado aqui.
interface ExtratoMovimento { ts: string; tipo: string; descricao: string; valor: number }
interface ExtratoConferencia { id: number; contado: string; esperado: string; divergencia: string; criado_em: string; operador_nome: string | null }
function ExtratoGavetaModal({ sessaoId, caixaNome, onFechar }: {
  sessaoId: number; caixaNome: string; onFechar: () => void;
}) {
  const [dados, setDados] = useState<{ desde: string | null; conferencias: ExtratoConferencia[]; movimentos: ExtratoMovimento[] } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ desde: string | null; conferencias: ExtratoConferencia[]; movimentos: ExtratoMovimento[] }>(`/api/caixa/turno/${sessaoId}/extrato`)
      .then(setDados)
      .catch((e) => setErro(e.message));
  }, [sessaoId]);

  const entradas = (dados?.movimentos ?? []).filter((m) => m.valor > 0).reduce((a, m) => a + m.valor, 0);
  const saidas = (dados?.movimentos ?? []).filter((m) => m.valor < 0).reduce((a, m) => a + m.valor, 0);

  return (
    <Modal aberto onFechar={onFechar} titulo={`Extrato da gaveta — ${caixaNome}`} largura={620}
      rodape={<button type="button" className="btn-primario" onClick={onFechar}>Fechar</button>}
    >
      {erro ? (
        <div style={{ padding: 16, color: 'var(--danger)', fontSize: 13 }}>{erro}</div>
      ) : !dados ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Carregando...</div>
      ) : (
        <>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
            {dados.desde
              ? <>Movimentos desde a última conferência que bateu (<strong style={{ color: 'var(--text-color)' }}>{formatDataHoraSP(dados.desde)}</strong>). O erro está nesta janela.</>
              : <>Movimentos desde a abertura do turno (ainda não houve uma conferência que bateu).</>}
          </p>

          {dados.conferencias.length > 0 && (
            <div style={{ marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {dados.conferencias.map((c) => {
                const div = Number(c.divergencia);
                const ok = Math.abs(div) < 0.005;
                return (
                  <span key={c.id} title={c.operador_nome ?? ''} style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 700,
                    backgroundColor: ok ? '#fde68a' : '#f59e0b', color: '#000',
                  }}>
                    {formatDataHoraSP(c.criado_em).slice(-5)} · {ok ? 'bateu' : `${div > 0 ? '+' : '−'}${formatMoeda(Math.abs(div))}`}
                  </span>
                );
              })}
            </div>
          )}

          {dados.movimentos.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Nenhum movimento de dinheiro nesta janela.
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--grid-header-bg, #f3f4f6)', color: 'var(--grid-header-fg, #4b5563)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, fontSize: 11, width: 64 }}>Hora</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, fontSize: 11 }}>Movimento</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600, fontSize: 11, width: 110 }}>Valor</th>
                </tr>
              </thead>
              <tbody>
                {dados.movimentos.map((m, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid var(--input-border, #f3f4f6)' }}>
                    <td style={{ padding: '6px 10px', fontSize: 11, color: 'var(--text-muted)' }}>{formatHoraSP(m.ts)}</td>
                    <td style={{ padding: '6px 10px' }}>{m.descricao}</td>
                    <td className="cel-num" style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: m.valor < 0 ? 'var(--danger)' : 'var(--text-color)' }}>
                      {m.valor < 0 ? '−' : '+'}{formatMoeda(Math.abs(m.valor))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--input-border, #e5e7eb)', fontWeight: 700 }}>
                  <td colSpan={2} style={{ padding: '6px 10px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>
                    Entrou {formatMoeda(entradas)} · Saiu {formatMoeda(Math.abs(saidas))} · Líquido
                  </td>
                  <td className="cel-num" style={{ padding: '6px 10px', textAlign: 'right' }}>{formatMoeda(entradas + saidas)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </>
      )}
    </Modal>
  );
}

function ResumoTab({ resumo }: { resumo: ResumoDia | null }) {
  if (!resumo) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Sem dados</div>;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <Kpi label="Vendas hoje" valor={resumo.vendas.qtd} unidade="qtd" />
        <Kpi label="Total vendido" valor={formatMoeda(resumo.vendas.total)} />
        <Kpi label="CR pendentes" valor={resumo.contas_receber_pendentes.qtd} unidade="qtd" />
        <Kpi label="A receber" valor={formatMoeda(resumo.contas_receber_pendentes.total)} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
        <div>
          <h3 style={{ marginBottom: 8 }}>Por caixa</h3>
          <table className="tabela" style={{ width: '100%' }}>
            <thead><tr><th>Caixa</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
            <tbody>
              {resumo.por_caixa.map((c) => (
                <tr key={c.id}><td>{c.nome}</td><td className="cel-num" style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoeda(c.total)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3 style={{ marginBottom: 8 }}>Por forma de pagamento</h3>
          <table className="tabela" style={{ width: '100%' }}>
            <thead><tr><th>Forma</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
            <tbody>
              {resumo.por_forma_pagamento.map((f) => (
                <tr key={f.id}><td>{f.nome}</td><td className="cel-num" style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoeda(f.total)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, valor, unidade }: { label: string; valor: string | number; unidade?: string }) {
  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      <div className="cel-num" style={{ fontSize: 22, fontWeight: 700 }}>{valor} {unidade && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{unidade}</span>}</div>
    </div>
  );
}

// Formata data plain (YYYY-MM-DD) sem aplicar conversao de timezone do browser.
function fmtDataFluxo(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// Celula "Forma" de uma perna de pagamento do fluxo. Espelha EXATAMENTE a celula de Pagamentos da
// Vendas hoje: forma + "Nx de parcela" (mesmo peso da forma) + ": " + valor da NOTA em negrito. So no
// parcelado; demais formas seguem so com a especie.
// [DA-452]: `entrada` JA e o valor cobrado (base + acrescimo) — somar o acrescimo de novo aqui
// contaria o juro duas vezes. O `acrescimo` sobrou so como sinal de "esta linha e parcelada".
function celulaFormaFluxo(l: FluxoLinha) {
  const acr = Number(l.acrescimo || 0);
  if (acr <= 0.005) return <>{l.especie}</>;
  const parcelas = Number(l.parcelas || 1);
  const real = Number(l.entrada || 0);
  const parceladoTxt = parcelas > 1 ? ` ${parcelas}x de ${formatMoeda(real / parcelas)}` : '';
  return <>{l.especie}{parceladoTxt}: <strong className="cel-num">{formatMoeda(real)}</strong></>;
}

// Por que a perna nao aparece como entrada no Fluxo ([DA-451]). O texto e curto de proposito: ele
// existe pra explicar a diferenca entre o TOTAL DA VENDA e o que entrou em caixa, nao pra ensinar
// a forma de pagamento. Forma nova sem verbete cai no generico, que segue verdadeiro.
const MOTIVO_FORA_DO_CAIXA: Record<string, string> = {
  credito_loja: 'credito do cliente — nao entra no caixa',
  crediario: 'a receber — nao entra no caixa',
};

// COMPOSICAO da venda ([DA-459]): as formas que pagaram a venda e o valor de cada uma, com o total
// embaixo. Substitui o "PIX + DINHEIRO" (que nao dizia quanto foi de cada) e o "de R$ 34,00 em 2
// pagamentos" (que anunciava um pagamento sem nome). Aparece so em venda com 2+ pernas.
function celulaComposicaoFluxo(pernas: PernaComposicao[], total: string | number, corSec: string) {
  // A conta so entra quando a venda usa MAIS DE UMA — com uma so, a coluna Caixa ja diz qual e.
  const contas = new Set(pernas.map((p) => p.conta).filter(Boolean));
  return (
    <div>
      {pernas.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', whiteSpace: 'nowrap' }}>
          <span style={{ flex: '1 1 auto' }}>
            {p.forma}
            {p.conta
              ? (contas.size > 1 && <span style={{ fontSize: 11, color: corSec }}> ({p.conta})</span>)
              : <span style={{ fontSize: 11, color: corSec }}> ({MOTIVO_FORA_DO_CAIXA[p.tipo] ?? 'nao entra no caixa'})</span>}
          </span>
          <span className="cel-num" style={{ fontWeight: 700 }}>{formatMoeda(p.valor)}</span>
        </div>
      ))}
      {/* A regua herda a COR DO PROPRIO TEXTO secundario (branca na linha azul, cinza fora dela):
          `var(--border)` foi calibrada pra fundo branco e sumiria sobre o azul solido ([DA-416]). */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', whiteSpace: 'nowrap', borderTop: `1px solid ${corSec}`, marginTop: 3, paddingTop: 3 }}>
        <span style={{ flex: '1 1 auto', color: corSec }}>TOTAL DA VENDA</span>
        <span className="cel-num" style={{ fontWeight: 700 }}>{formatMoeda(total)}</span>
      </div>
    </div>
  );
}

function FluxoTab({ inicio, fim, caixaId, caixas, valor, fluxo, loading, onInicio, onFim, onCaixaId, onValor, onAtualizar, onHoje }: {
  inicio: string; fim: string; caixaId: number | ''; caixas: CaixaConta[]; valor: string;
  fluxo: FluxoResp | null; loading: boolean;
  onInicio: (v: string) => void; onFim: (v: string) => void; onCaixaId: (v: number | '') => void;
  onValor: (v: string) => void;
  onAtualizar: () => void; onHoje: () => void;
}) {
  // Le o termo DIGITADO (nao o que voltou na resposta) pra tela reagir na hora: o `fluxo.buscando`
  // so chega depois do debounce + ida ao servidor, e ate la os avisos mentiriam sobre o estado.
  const valorBuscado = parseValorBusca(valor);
  const buscando = valorBuscado !== null;
  const termoInvalido = valor.trim() !== '' && !buscando;
  // Data em branco vale como hoje (o backend resolve). O texto do banner diz qual periodo
  // esta valendo — sem isso, campo vazio + resultado do dia parece busca que nao filtrou.
  const dataBR = (d: string) => d.split('-').reverse().join('/');
  const periodoTexto = (!inicio || !fim) ? 'hoje'
    : inicio === fim ? dataBR(inicio)
    : `${dataBR(inicio)} a ${dataBR(fim)}`;
  // A venda é UMA só, ainda que o dinheiro caia em contas diferentes: quem procura uma venda procura
  // pelo TOTAL (185,01), não por "50,01 + 135,00". Agrupa as pernas de pagamento da mesma venda numa
  // linha com o total; as formas/contas viram detalhe (expande no clique). Cada perna continua
  // existindo — é o extrato por conta que faz o caixa fechar; por isso, filtrando por um caixa
  // específico o backend já devolve só a perna daquela conta e o grupo vira linha única sozinho.
  //
  // ⚠️ BUSCANDO, NAO AGRUPA. Ali a pergunta e "como foi paga essa venda?", e a resposta tem que
  // estar na tela — nao atras de um clique nem numa sub-linha "↳" com metade das celulas vazia.
  // Cada pagamento vira uma LINHA INTEIRA (pedido, cliente, data, forma, caixa, vendedor, valor),
  // e o azul, que vem de `pernas_venda`, amarra visualmente as que sao da mesma venda.
  const [expandido, setExpandido] = useState<Set<string>>(new Set());
  const grupos = useMemo(() => {
    const out: Array<{ key: string; linhas: FluxoLinha[] }> = [];
    const idx = new Map<string, number>();
    const vistas = new Map<string, number>();
    (fluxo?.linhas ?? []).forEach((l) => {
      const ehVenda = Number(l.entrada) > 0 && /^VND/i.test(l.documento ?? '');
      if (ehVenda && !buscando) {
        const key = `${l.documento}|${l.data}`;
        const at = idx.get(key);
        if (at != null) { out[at]!.linhas.push(l); return; }
        idx.set(key, out.length);
        out.push({ key, linhas: [l] });
        return;
      }
      // Linha solta: a chave sai do PROPRIO lancamento, nunca da posicao — o Fluxo recarrega
      // sozinho por socket, e chave posicional faria o destaque do grid escorregar pra outra
      // linha assim que algo entrasse acima. O contador so desempata lancamentos de assinatura
      // identica (deterministico: mesma ordem, mesmo numero).
      const assinatura = `solo|${l.documento}|${l.ordem_ts}|${l.especie}|${l.caixa_nome ?? ''}|${l.entrada}|${l.saida}`;
      const n = (vistas.get(assinatura) ?? 0) + 1;
      vistas.set(assinatura, n);
      out.push({ key: `${assinatura}|${n}`, linhas: [l] });
    });
    return out;
  }, [fluxo, buscando]);

  // Grid padrao: clicar marca a linha (amarelo `#fde68a`), ↑/↓ navegam. SEM `onAtivar` — a acao da
  // linha aqui e abrir o detalhe por conta, que ja mora no clique unico e so existe em parte delas
  // (mesmo uso do grid na Revisar margens). O amarelo nao colide com nada nesta grade: o azul e da
  // venda dividida e o verde/vermelho sao dos valores.
  //
  // Sao DUAS grades nesta aba (subtotais por conta e lancamentos) e UM teclado so: sem isso as setas
  // moveriam as duas ao mesmo tempo. Quem recebe o clique fica com o teclado, e a outra apaga o
  // destaque — uma linha corrente por tela, senao "a linha corrente" vira duas e nao significa nada.
  const [focoGrid, setFocoGrid] = useState<'contas' | 'linhas'>('linhas');
  const grid = useGridNavegavel<{ key: string; linhas: FluxoLinha[] }>({
    itens: grupos,
    getId: (g) => g.key,
    ativo: focoGrid === 'linhas',
  });

  function alternar(key: string) {
    setExpandido((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }

  // A legenda so aparece quando ha linha azul na tela — cor sem significado declarado vira
  // enfeite, e legenda de algo que nao esta ali vira ruido.
  const temVendaDividida = grupos.some((g) => Number(g.linhas[0]?.pernas_venda ?? 0) > 1);

  // CONFERENCIA POR CONTA ([DA-452]). Deriva das LINHAS ja na tela (nao de uma consulta nova), pra
  // o subtotal ser sempre a soma exata do que esta abaixo dele — subtotal vindo de outra query
  // divergiria calado do extrato exibido, que e o pecado que esta tela existe pra matar.
  // A taxa so entra onde foi MEDIDA na maquininha; conta sem captura fica com taxa zero e o
  // "caiu na conta" igual a entrada, porque afirmar taxa nao medida seria inventar numero.
  const porConta = useMemo(() => {
    const mapa = new Map<string, { conta: string; entradas: number; recebido: number; saidas: number; taxa: number; medido: boolean }>();
    for (const l of fluxo?.linhas ?? []) {
      const conta = l.caixa_nome || '(sem conta)';
      const at = mapa.get(conta) ?? { conta, entradas: 0, recebido: 0, saidas: 0, taxa: 0, medido: false };
      at.entradas += Number(l.entrada || 0);
      // RECEBIMENTO e so o que veio do cliente ([DA-466]): venda finalizada e crediario baixado.
      // A lista e por INCLUSAO, nao "tudo menos sangria" — fonte nova que aparecer no Fluxo fica
      // de fora ate alguem decidir que ela e faturamento, que e o erro barato.
      if (l.origem === 'venda' || l.origem === 'cr') at.recebido += Number(l.entrada || 0);
      at.saidas += Number(l.saida || 0);
      if (l.liquido_maquininha != null) { at.taxa += Number(l.taxa_maquininha || 0); at.medido = true; }
      mapa.set(conta, at);
    }
    return Array.from(mapa.values())
      .filter((c) => c.entradas > 0.005 || c.saidas > 0.005)
      .sort((a, b) => b.entradas - a.entradas);
  }, [fluxo]);

  // TOTAL DE RECEBIMENTOS SEM TAXAS BANCARIAS ([DA-454]) — o que de fato caiu nas contas.
  // Deriva do MESMO `porConta` da tabela logo abaixo (soma da coluna "Caiu na conta"), nunca de uma
  // conta paralela: dois numeros que respondem a mesma pergunta tem que sair da mesma fonte, senao
  // divergem calados — a licao do [DA-410] e a razao de ser do [DA-452].
  //
  // NAO e o "Saldo do Periodo": o saldo tira TODAS as saidas (fornecedor pago, sangria, estorno);
  // este tira SO a taxa da maquininha. Coincidem num dia em que a unica saida foi taxa, e e por
  // isso que o rotulo precisa dizer "sem taxas bancarias" em vez de "liquido".
  //
  // ⚠️ [DA-466] Soma a coluna `recebido`, NAO `entradas`: o suprimento que recebe uma sangria e
  // dinheiro que ja tinha sido recebido e so mudou de gaveta — contado aqui, o mesmo real entrava
  // duas vezes. Em 06/08 isso inflava o painel em R$ 1.200,43 (3 sangrias que viraram 3
  // suprimentos): dizia R$ 4.788,50 onde a loja recebeu R$ 3.588,07. A tabela por conta segue
  // somando TUDO de proposito — la a pergunta e "bate com o extrato do banco?", e o banco viu a
  // transferencia. Duas perguntas diferentes, dois numeros; por isso o cartao mostra a ponte
  // entre eles na sublinha, senao a diferenca leria como divergencia.
  const totalSemTaxas = useMemo(
    () => porConta.reduce((s, c) => s + c.recebido - c.taxa, 0),
    [porConta],
  );
  const totalTaxas = useMemo(() => porConta.reduce((s, c) => s + c.taxa, 0), [porConta]);
  // Quanto das entradas foi so remanejamento entre contas da loja (o que o painel deixou de fora).
  const totalRemanejado = useMemo(
    () => porConta.reduce((s, c) => s + c.entradas - c.recebido, 0),
    [porConta],
  );

  // Esta e a grade em que ele confere linha a linha contra o app do banco — marcar onde parou e
  // justamente o que ela pedia. Identidade = o nome da conta.
  const gridContas = useGridNavegavel<(typeof porConta)[number]>({
    itens: porConta,
    getId: (c) => c.conta,
    ativo: focoGrid === 'contas',
  });

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>De</label>
          <input type="date" value={inicio} onChange={(e) => onInicio(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>Ate</label>
          <input type="date" value={fim} onChange={(e) => onFim(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>Caixa</label>
          <select value={String(caixaId)} onChange={(e) => onCaixaId(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">Todos</option>
            {caixas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>Valor</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input
              value={valor}
              onChange={(e) => onValor(e.target.value)}
              placeholder="500,00"
              style={{ width: 150, paddingLeft: 30, paddingRight: valor ? 28 : undefined }}
            />
            {valor !== '' && (
              <button
                type="button"
                onClick={() => onValor('')}
                title="Limpar busca"
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, lineHeight: 0 }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <button type="button" className="btn-secundario" onClick={onHoje}>Hoje</button>
        <button type="button" className="btn-primario" onClick={onAtualizar}>
          <RefreshCw size={14} style={{ marginRight: 4, marginBottom: -2 }} /> Atualizar
        </button>
      </div>

      {/* Estado da busca. Banner de aviso = fundo SOLIDO + letra preta (ver sistema/CLAUDE.md § UI). */}
      {termoInvalido && (
        <div style={{ background: '#f97316', color: '#000', borderLeft: '4px solid #c2410c', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          <b>"{valor}"</b> nao e um valor. A busca do Fluxo e so por valor — digite so numeros, ex.: <b>500,00</b>.
        </div>
      )}
      {buscando && (
        <div style={{ background: '#f97316', color: '#000', borderLeft: '4px solid #c2410c', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          Procurando <b>{formatMoeda(valorBuscado)}</b> em <b>{periodoTexto}</b> — a busca respeita o periodo De/Ate.
          Nao achou? Abra o De pra tras.
          {fluxo?.truncado && <> Achei <b>{fluxo.encontrados}</b> lancamentos; mostrando os <b>{fluxo.linhas.length}</b> mais recentes.</>}
        </div>
      )}

      {/* Totais */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: '4px solid #10b981', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Total Entradas</div>
          <div className="cel-num" style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>{formatMoeda(fluxo?.totalEntradas ?? 0)}</div>
        </div>
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: '4px solid #dc2626', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Total Saidas</div>
          <div className="cel-num" style={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}>{formatMoeda(fluxo?.totalSaidas ?? 0)}</div>
        </div>
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: '4px solid var(--primary)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{buscando ? 'Saldo dos Encontrados' : 'Saldo do Periodo'}</div>
          <div className="cel-num" style={{ fontSize: 22, fontWeight: 700, color: (fluxo?.saldo ?? 0) >= 0 ? '#10b981' : '#dc2626' }}>
            {formatMoeda(fluxo?.saldo ?? 0)}
          </div>
        </div>
        {/* [DA-454] Mesma familia semantica de "Total Entradas" (dinheiro que entrou), entao MESMA cor:
            inventar uma cor nova aqui obrigaria a aprender um vocabulario pra um subtotal do que ja
            esta ao lado. O que distingue e o rotulo + a sublinha da taxa. */}
        <div
          style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: '4px solid #10b981', borderRadius: 8, padding: 12 }}
          title="O que os CLIENTES pagaram, menos a taxa das maquininhas: vendas e crediario baixado. Nao entra dinheiro remanejado entre contas da loja (sangria/suprimento/transferencia) — ele ja foi recebido antes. Diferente do Saldo do Periodo, que tambem desconta fornecedor pago e estorno."
        >
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Total de Recebimentos sem taxas bancarias
          </div>
          <div className="cel-num" style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>{formatMoeda(totalSemTaxas)}</div>
          {/* A sublinha e o que impede o painel de virar copia do "Total Entradas" num dia sem taxa:
              ela diz QUANTO foi descontado, que e a informacao que o outro cartao nao tem. E com o
              [DA-466] ela virou tambem a PONTE ate a tabela por conta: sem dizer o quanto saiu por
              remanejamento, a diferenca entre os dois numeros leria como divergencia. */}
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {totalTaxas > 0.005 ? <>− {formatMoeda(totalTaxas)} de taxa</> : 'sem taxa no periodo'}
            {totalRemanejado > 0.005 && <> · fora {formatMoeda(totalRemanejado)} movidos entre contas</>}
          </div>
        </div>
      </div>

      {/* Conferencia por CONTA ([DA-452]) — a razao de ser desta tabela e o Ruan poder somar SOZINHO:
          cada linha tem uma fonte externa que e DELE (app do Mercado Pago, app do PagBank, a gaveta
          contada), nao um numero que so o sistema sabe produzir. Sem ela o total e um numero unico
          que so da pra conferir perguntando pra mim. */}
      {!loading && fluxo && fluxo.linhas.length > 0 && porConta.length > 0 && (
        <div style={{ marginBottom: 12, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', background: 'var(--panel-bg)', fontSize: 12, fontWeight: 600 }}>
            Conferencia por conta
            <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
              {' '}— confira cada linha contra o extrato do banco ou a gaveta contada
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="tabela" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th style={{ textAlign: 'right' }}>Entradas</th>
                  <th style={{ textAlign: 'right' }}>Taxa</th>
                  <th style={{ textAlign: 'right' }}>Caiu na conta</th>
                  <th style={{ textAlign: 'right' }}>Saidas</th>
                </tr>
              </thead>
              <tbody>
                {porConta.map((c) => {
                  const rpc = gridContas.rowProps(c);
                  // Sobre o amarelo, o verde/vermelho dos valores perde contraste — na linha marcada
                  // o numero volta pro preto (a informacao "entrada x saida" segue nas colunas).
                  const sel = gridContas.selectedId === c.conta;
                  return (
                    <tr
                      key={c.conta}
                      {...rpc}
                      onClick={() => { setFocoGrid('contas'); grid.setSelectedId(null); rpc.onClick(); }}
                    >
                      <td style={{ fontWeight: 600 }}>{c.conta}</td>
                      <td className="cel-num" style={{ textAlign: 'right', color: sel ? '#000' : '#10b981' }}>{formatMoeda(c.entradas)}</td>
                      <td className="cel-num" style={{ textAlign: 'right', color: sel ? '#000' : c.taxa > 0.005 ? '#dc2626' : 'var(--text-muted)' }}>
                        {c.taxa > 0.005 ? `− ${formatMoeda(c.taxa)}` : '—'}
                      </td>
                      <td className="cel-num" style={{ textAlign: 'right', fontWeight: 700 }} title={c.medido ? 'Medido na maquininha — tem que bater com o extrato' : 'Esta conta nao captura a taxa; e a propria entrada'}>
                        {formatMoeda(c.entradas - c.taxa)}
                      </td>
                      <td className="cel-num" style={{ textAlign: 'right', color: sel ? '#000' : c.saidas > 0.005 ? '#dc2626' : 'var(--text-muted)' }}>
                        {c.saidas > 0.005 ? formatMoeda(c.saidas) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {fluxo.truncado && (
            <div style={{ padding: '6px 12px', fontSize: 11, color: 'var(--danger)' }}>
              A busca cortou em {fluxo.linhas.length} de {fluxo.encontrados} lancamentos — estes subtotais sao so do que esta na tela.
            </div>
          )}
        </div>
      )}

      {temVendaDividida && !loading && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: '#1d4ed8', flex: '0 0 auto' }} />
          {buscando
            ? 'Linhas azuis = pagamentos de uma venda que teve mais de um recebimento. As do mesmo pedido somam o total da venda.'
            : 'Linha azul = a venda foi paga em mais de um recebimento.'}
        </div>
      )}

      {/* Tabela */}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando fluxo...</div>
      ) : !fluxo || fluxo.linhas.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          {buscando
            ? <>Nenhum lancamento de <b>{formatMoeda(valorBuscado)}</b> em <b>{periodoTexto}</b> — nem venda, nem crediario, nem conta a pagar, nem sangria. Abra o periodo pra procurar mais longe.</>
            : 'Sem movimentacoes no periodo'}
        </div>
      ) : (
        <table className="tabela" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th>Pedido</th>
              <th style={{ width: 70 }}>Cod.</th>
              <th>Cliente / Historico</th>
              <th style={{ width: 100 }}>Data</th>
              <th>Forma</th>
              <th>Caixa</th>
              <th>Vendedor</th>
              <th style={{ textAlign: 'right', width: 110 }}>Entrada</th>
              <th style={{ textAlign: 'right', width: 110 }}>Saida</th>
            </tr>
          </thead>
          <tbody>
            {grupos.map((grupo) => {
              const { key, linhas } = grupo;
              const l0 = linhas[0]!;
              const varias = linhas.length > 1;
              const aberto = expandido.has(key);
              const entradaGrupo = linhas.reduce((s, x) => s + Number(x.entrada || 0), 0);
              const saidaGrupo = linhas.reduce((s, x) => s + Number(x.saida || 0), 0);
              // [DA-452] Taxa/liquido medidos na maquininha. So soma as pernas que TEM captura —
              // venda mista (parte no cartao, parte em dinheiro) mostraria um liquido menor que a
              // entrada sem explicacao. `temLiquido` exige que TODA perna com entrada seja medida.
              const pernasComLiquido = linhas.filter((x) => x.liquido_maquininha != null);
              const temLiquido = pernasComLiquido.length > 0
                && pernasComLiquido.length === linhas.filter((x) => Number(x.entrada || 0) > 0.005).length;
              const taxaGrupo = pernasComLiquido.reduce((s, x) => s + Number(x.taxa_maquininha || 0), 0);
              const liquidoGrupo = pernasComLiquido.reduce((s, x) => s + Number(x.liquido_maquininha || 0), 0);
              // Venda paga em mais de um recebimento: linha AZUL SOLIDA com fonte branca. O sinal
              // vem de `pernas_venda` (a venda toda), nao de `linhas.length` (o que esta na tela) —
              // buscando por valor so a perna que casou volta, e e justamente ai que o operador
              // precisa saber que aquele Pix de 500 e pedaco de uma venda de 700.
              const pernasVenda = Number(l0.pernas_venda ?? 0);
              const vendaDividida = pernasVenda > 1;
              // [DA-459] Composicao completa da venda. Vem do backend so quando ha 2+ pagamentos.
              const composicao = (l0.composicao && l0.composicao.length > 1) ? l0.composicao : null;
              // Pintar so o `background` da <tr> NAO basta: cada celula fixa a propria cor
              // (--text-muted, verde, vermelho) e sumiria no azul. Mesma licao da CotacaoLadoALado.
              // Selecionada, o amarelo do grid cobre o azul — e a letra tem que voltar pro preto,
              // senao o branco da venda dividida fica ilegivel sobre o `#fde68a`.
              const selecionado = grid.selectedId === key;
              const corFg = selecionado ? '#000' : vendaDividida ? '#fff' : undefined;
              const corFgSec = selecionado ? '#6b5a1e' : vendaDividida ? 'rgba(255,255,255,0.85)' : 'var(--text-muted)';
              const rp = grid.rowProps(grupo);
              const tituloLinha = vendaDividida
                ? `Venda ${l0.documento}: ${formatMoeda(l0.venda_total)} em ${pernasVenda} pagamentos`
                  + (varias ? ' — clique para recolher/abrir o detalhe' : ' — esta linha e um deles')
                : (varias ? 'Clique para ver as formas de pagamento' : undefined);
              return (
                <Fragment key={key}>
                  <tr
                    {...rp}
                    onClick={() => { setFocoGrid('linhas'); gridContas.setSelectedId(null); rp.onClick(); if (varias) alternar(key); }}
                    style={{
                      ...(vendaDividida ? { backgroundColor: '#1d4ed8', color: '#fff' } : null),
                      ...rp.style,
                    }}
                    title={tituloLinha}
                  >
                    <td style={{ fontWeight: 600, fontSize: 12, color: corFg }}>{l0.documento}</td>
                    <td className="text-gray-500 font-bold no-underline" style={{ color: corFg }}>{l0.cliente_codigo ? String(l0.cliente_codigo).padStart(4, '0') : '—'}</td>
                    <td style={{ color: corFg }}>
                      {l0.historico}
                      {l0.orcamento_numero && (
                        <div className="text-[11px]" style={{ color: corFgSec }}>{l0.orcamento_numero}</div>
                      )}
                    </td>
                    <td style={{ color: corFg }}>{fmtDataFluxo(l0.data)}</td>
                    <td style={{ color: corFg }}>
                      {/* [DA-459] Com 2+ pagamentos a celula mostra a COMPOSICAO (forma + valor de
                          cada uma + total da venda). O chevron continua abrindo o detalhe por
                          conta, que e outra pergunta ("em que gaveta caiu"). */}
                      {composicao ? (
                        <span style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                          {varias && (aberto ? <ChevronDown size={13} style={{ marginTop: 3, flex: '0 0 auto' }} /> : <ChevronRight size={13} style={{ marginTop: 3, flex: '0 0 auto' }} />)}
                          <span style={{ flex: '1 1 auto' }}>{celulaComposicaoFluxo(composicao, l0.venda_total ?? entradaGrupo, corFgSec)}</span>
                        </span>
                      ) : varias ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {aberto ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          {linhas.map((x) => x.especie).join(' + ')}
                        </span>
                      ) : celulaFormaFluxo(l0)}
                    </td>
                    <td style={{ color: corFgSec }}>
                      {varias
                        ? [...new Set(linhas.map((x) => x.caixa_nome).filter(Boolean))].join(' + ') || '—'
                        : (l0.caixa_nome || '—')}
                    </td>
                    <td style={{ color: corFgSec }}>{l0.vendedor || '—'}</td>
                    <td className="cel-num" style={{ textAlign: 'right', color: corFg ?? (entradaGrupo > 0 ? '#10b981' : 'var(--text-muted)'), fontWeight: entradaGrupo > 0 ? 600 : 400 }}>
                      {entradaGrupo > 0 ? formatMoeda(entradaGrupo) : '—'}
                      {temLiquido && taxaGrupo > 0.005 && (
                        <div
                          style={{ fontSize: 10, fontWeight: 400, color: corFgSec }}
                          title="Taxa cobrada pela maquininha e valor que caiu de fato na conta. Confira o líquido contra o extrato do banco — é o mesmo número."
                        >
                          taxa {formatMoeda(taxaGrupo)} · líq. {formatMoeda(liquidoGrupo)}
                        </div>
                      )}
                      {/* Sai quando a composicao esta na tela: ali o total da venda aparece com
                          NOME e valor de cada pagamento, e repetir "em N pagamentos" custaria
                          atencao sem dizer nada novo ([DA-459]). */}
                      {vendaDividida && !varias && !composicao && (
                        <div style={{ fontSize: 10, fontWeight: 400, color: corFgSec }}>
                          de {formatMoeda(l0.venda_total)} em {pernasVenda} pagamentos
                        </div>
                      )}
                    </td>
                    <td className="cel-num" style={{ textAlign: 'right', color: corFg ?? (saidaGrupo > 0 ? '#dc2626' : 'var(--text-muted)'), fontWeight: saidaGrupo > 0 ? 600 : 400 }}>
                      {saidaGrupo > 0 ? formatMoeda(saidaGrupo) : '—'}
                    </td>
                  </tr>
                  {varias && aberto && linhas.map((l, i) => (
                    <tr key={`${key}-${i}`} style={{ backgroundColor: 'var(--panel-bg)', fontSize: 12 }}>
                      <td />
                      <td />
                      <td style={{ color: 'var(--text-muted)', paddingLeft: 18 }}>↳ forma de pagamento</td>
                      <td />
                      <td>{celulaFormaFluxo(l)}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{l.caixa_nome || '—'}</td>
                      <td />
                      <td className="cel-num" style={{ textAlign: 'right', color: '#10b981' }}>{formatMoeda(l.entrada)}</td>
                      <td />
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
              <td colSpan={7} style={{ textAlign: 'right' }}>Totais</td>
              <td className="cel-num" style={{ textAlign: 'right', color: '#10b981' }}>{formatMoeda(fluxo.totalEntradas)}</td>
              <td className="cel-num" style={{ textAlign: 'right', color: '#dc2626' }}>{formatMoeda(fluxo.totalSaidas)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

// RECEBIMENTOS SEM TAXAS ([DA-498]) — o extrato por tras do cartao "Total de Recebimentos sem taxas
// bancarias" do Fluxo ([DA-454]). Nao tem consulta propria: le as MESMAS `fluxo.linhas` que a aba
// Fluxo ja carregou e aplica o recorte do [DA-466] (origem `venda`|`cr` = dinheiro que o CLIENTE
// pagou). Consulta paralela divergiria calada do numero que ela existe pra explicar.
//
// ⚠️ A coluna Taxa tem TRES estados, nao dois. Enquanto era um numero so no cartao, "taxa zero" e
// "taxa que ninguem mediu" somavam igual e a diferenca nao aparecia; numa tela chamada "sem taxas
// bancarias" a coluna Liquido passa a AFIRMAR o que caiu na conta, e ai o traco vira mentira:
//   medido      -> a maquininha capturou (point_cobrancas/pagbank_transacoes): mostra a taxa
//   sem taxa    -> pix/dinheiro, que taxa nao tem: traco honesto
//   nao medido  -> cartao SEM captura: a taxa existe e o sistema nao sabe quanto
// Hoje todo crediario baixado cai no 3o caso: a consulta de CR do backend nao busca taxa nenhuma
// (so a de vendas faz o join), entao cartao no PagBank baixando crediario entra pelo valor cheio.
// O rodape soma quanto do periodo esta nessa situacao — o liquido REAL e menor que o exibido.
function RecebimentosSemTaxasTab({
  inicio, fim, caixaId, caixas, fluxo, loading, onInicio, onFim, onCaixaId, onAtualizar, onHoje,
}: {
  inicio: string; fim: string; caixaId: number | ''; caixas: CaixaConta[];
  fluxo: FluxoResp | null; loading: boolean;
  onInicio: (v: string) => void; onFim: (v: string) => void; onCaixaId: (v: number | '') => void;
  onAtualizar: () => void; onHoje: () => void;
}) {
  // Recorte + ordem por FORMA (alfabetica), com o mais recente no topo dentro de cada forma. A
  // pergunta desta aba e "quanto entrou por cada forma e quanto a taxa comeu", entao agrupar os
  // iguais e o que deixa a coluna Taxa legivel — cronologico puro embaralha os tres estados dela.
  //
  // ⚠️ TITULOS DE CREDIARIO ENTRAM AGRUPADOS ([DA-498]). O cliente que quita 10 parcelas numa
  // passada de cartao gera 10 linhas em `contas_receber` e UMA transacao no banco; listadas uma a
  // uma, a tela cobra do operador uma conciliacao que o extrato nao tem como responder.
  //
  // ⚠️⚠️ E o vinculo e' DEDUZIDO, nao registrado — isso e' o que precisa ficar escrito. A
  // `contas_receber` NAO tem agrupador (nem `recebimento_id`, nem `lote`): numa baixa em lote o
  // backend faz N UPDATEs independentes, e so `point_cobrancas`/Pix deixam rastro do ato, por
  // tabela de vinculo que o PagBank nao alimenta. Logo o unico amarre possivel e a assinatura
  // (cliente + forma + conta + operador da baixa + dia) — que ACERTA no caso normal e ERRA se o
  // MESMO cliente pagar duas vezes no mesmo dia, na mesma forma, com o mesmo operador: ai dois
  // atos viram uma linha e o banco mostra dois lancamentos. Por isso o grupo (a) diz quantos
  // titulos somou, (b) ABRE nos titulos e (c) carrega no title o aviso de que foi deduzido.
  // Esconder sem poder abrir seria trocar "numero demais" por "numero que ninguem confere".
  const recebimentos = useMemo(() => {
    const vistos = new Map<string, number>();
    const grupos = new Map<string, { chave: string; linhas: FluxoLinha[]; agrupavel: boolean }>();
    for (const l of (fluxo?.linhas ?? [])) {
      if (l.origem !== 'venda' && l.origem !== 'cr') continue;
      let chave: string;
      if (l.origem === 'cr') {
        chave = `cr|${l.cliente_codigo ?? ''}|${l.especie}|${l.caixa_nome ?? ''}|${l.responsavel ?? ''}|${l.data}`;
      } else {
        // Venda segue linha a linha (foi o recorte pedido): chave propria, nunca posicional
        // ([DA-108]) — a aba recarrega por socket e o destaque escorregaria pra outra linha.
        const base = `vnd|${l.documento}|${l.ordem_ts}|${l.especie}|${l.caixa_nome ?? ''}|${l.entrada}`;
        const n = (vistos.get(base) ?? 0) + 1;
        vistos.set(base, n);
        chave = n > 1 ? `${base}#${n}` : base;
      }
      const g = grupos.get(chave) ?? { chave, linhas: [], agrupavel: l.origem === 'cr' };
      g.linhas.push(l);
      grupos.set(chave, g);
    }
    return Array.from(grupos.values()).sort((a, b) =>
      (a.linhas[0]!.especie || '').localeCompare(b.linhas[0]!.especie || '', 'pt-BR')
      || String(b.linhas[0]!.ordem_ts).localeCompare(String(a.linhas[0]!.ordem_ts)));
  }, [fluxo]);

  // Quais grupos de crediario estao abertos. Nasce vazio: o pedido foi justamente nao ver os titulos.
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const alternar = (chave: string) => setAbertos((prev) => {
    const p = new Set(prev);
    if (p.has(chave)) p.delete(chave); else p.add(chave);
    return p;
  });

  // O payload nao traz o `fp.tipo` que o backend usa pra decidir se busca taxa — sobra a especie.
  // Forma de cartao nova com outro nome cairia no traco em vez do "nao medido"; e o erro barato
  // (deixa de avisar), nunca o de afirmar taxa inexistente.
  const ehCartao = (l: FluxoLinha) => /CARTAO/.test(l.especie || '');
  const foiMedido = (l: FluxoLinha) => l.liquido_maquininha != null;
  const semMedicao = (l: FluxoLinha) => ehCartao(l) && !foiMedido(l);

  // Totais varrem as LINHAS, nunca os grupos: agrupar e' decisao de exibicao, e um total que
  // mudasse ao juntar linhas ja seria prova de que o agrupamento mexeu no dinheiro.
  const t = useMemo(() => {
    let bruto = 0, taxa = 0, naoMedido = 0, qtdNaoMedido = 0, qtdLinhas = 0;
    for (const g of recebimentos) for (const l of g.linhas) {
      qtdLinhas++;
      bruto += Number(l.entrada || 0);
      if (foiMedido(l)) taxa += Number(l.taxa_maquininha || 0);
      else if (semMedicao(l)) { naoMedido += Number(l.entrada || 0); qtdNaoMedido++; }
    }
    return { bruto, taxa, liquido: bruto - taxa, naoMedido, qtdNaoMedido, qtdLinhas };
  }, [recebimentos]);

  // Grid padrao da casa: clicar marca a linha, ↑/↓ andam, o destaque acompanha o scroll. Sem
  // `onAtivar` — a linha nao abre nada (e um extrato de conferencia), entao Enter e duplo-clique nao
  // teriam o que ativar; mesmo caso da Revisar margens. Esta aba renderiza SOZINHA, entao nao ha o
  // conflito de dois grids dividindo o teclado que a aba Fluxo precisa resolver.
  const grid = useGridNavegavel<(typeof recebimentos)[number]>({
    itens: recebimentos,
    getId: (r) => r.chave,
  });

  const periodoTexto = inicio === fim ? fmtDataFluxo(inicio) : `${fmtDataFluxo(inicio)} a ${fmtDataFluxo(fim)}`;

  return (
    <div>
      {/* Mesmos filtros do Fluxo, MENOS a busca por valor: aqui a pergunta e "quanto entrou no
          periodo", e um recorte por valor devolveria um total que nao e o do periodo. */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>De</label>
          <input type="date" value={inicio} onChange={(e) => onInicio(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>Ate</label>
          <input type="date" value={fim} onChange={(e) => onFim(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>Caixa</label>
          <select value={String(caixaId)} onChange={(e) => onCaixaId(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">Todos</option>
            {caixas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        <button type="button" className="btn-secundario" onClick={onHoje}>Hoje</button>
        <button type="button" className="btn-primario" onClick={onAtualizar}>
          <RefreshCw size={14} style={{ marginRight: 4, marginBottom: -2 }} /> Atualizar
        </button>
      </div>

      {/* A busca por valor mora na aba Fluxo e as duas COMPARTILHAM o mesmo `fluxo` ([DA-498]). Com
          ela ligada, esta lista e o recorte da busca — e o total abaixo seria lido como o total do
          periodo. Avisar e obrigatorio: o filtro daqui nao tem como desliga-lo. */}
      {fluxo?.buscando && (
        <div style={{ background: '#f97316', color: '#000', borderLeft: '4px solid #c2410c', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          Ha uma <b>busca por valor</b> ativa na aba Fluxo, entao estes numeros sao so dos lancamentos
          que ela achou — <b>nao</b> do periodo inteiro. Limpe a busca no Fluxo pra ver {periodoTexto} completo.
        </div>
      )}

      {/* Totais. O total fecha a fila DE PROPOSITO: os dois primeiros sao os descontos que explicam
          por que ele nao e o bruto (o medido e o que ninguem mediu), entao o olho le a conta na
          ordem em que ela acontece e para no resultado. O ultimo cartao e o MESMO numero do painel
          do Fluxo ([DA-454]), pela mesma conta.
          ⚠️ "3a posicao" e' o FIM da fila, nao a coluna 3: o cartao do meio some no dia sem cartao
          nao medido, e ai o total tem que continuar sendo o ultimo, nao virar um buraco no grid. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: '4px solid var(--primary)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Taxa medida no periodo</div>
          <div className="cel-num" style={{ fontSize: 22, fontWeight: 700, color: t.taxa > 0.005 ? '#dc2626' : 'var(--text-muted)' }}>
            {t.taxa > 0.005 ? <>− {formatMoeda(t.taxa)}</> : '—'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>so onde a maquininha capturou</div>
        </div>
        {/* So aparece quando ha o que avisar — cartao que some no dia limpo em vez de exibir um zero
            que ensina a ignorar a cor. */}
        {t.qtdNaoMedido > 0 && (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: '4px solid #f97316', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Cartao sem taxa medida</div>
            <div className="cel-num" style={{ fontSize: 22, fontWeight: 700, color: '#c2410c' }}>{formatMoeda(t.naoMedido)}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {t.qtdNaoMedido} recebimento{t.qtdNaoMedido === 1 ? '' : 's'} · o liquido real e menor
            </div>
          </div>
        )}
        <div
          style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: '4px solid #10b981', borderRadius: 8, padding: 12 }}
          title="O que os CLIENTES pagaram, menos a taxa das maquininhas: vendas e crediario baixado. Nao entra dinheiro remanejado entre contas da loja (sangria/suprimento/transferencia) — ele ja foi recebido antes."
        >
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Total de Recebimentos sem taxas bancarias
          </div>
          <div className="cel-num" style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>{formatMoeda(t.liquido)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {t.qtdLinhas} recebimento{t.qtdLinhas === 1 ? '' : 's'} · {formatMoeda(t.bruto)} bruto
            {t.taxa > 0.005 && <> − {formatMoeda(t.taxa)} de taxa</>}
          </div>
        </div>
      </div>

      {t.qtdNaoMedido > 0 && (
        <div style={{ background: '#f97316', color: '#000', borderLeft: '4px solid #c2410c', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          <b>{formatMoeda(t.naoMedido)}</b> em cartao entrou <b>sem taxa medida</b> — a maquininha cobrou,
          o sistema nao capturou quanto. A coluna Liquido dessas linhas mostra o valor cheio, entao
          <b> o que caiu na conta e menor que o total acima</b>.
        </div>
      )}

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando recebimentos...</div>
      ) : recebimentos.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          Nenhum recebimento de cliente em {periodoTexto}.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tabela" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ width: 56 }}>Hora</th>
                <th>Pedido</th>
                <th style={{ width: 70 }}>Cod.</th>
                <th>Cliente</th>
                <th>Forma</th>
                <th>Conta</th>
                <th>Vendedor</th>
                <th style={{ textAlign: 'right', width: 110 }}>Bruto</th>
                <th style={{ textAlign: 'right', width: 110 }}>Taxa</th>
                <th style={{ textAlign: 'right', width: 110 }}>Liquido</th>
              </tr>
            </thead>
            <tbody>
              {recebimentos.map((r) => {
                const l = r.linhas[0]!;
                const varios = r.linhas.length > 1;
                const aberto = abertos.has(r.chave);
                const medido = foiMedido(l);
                const semMed = semMedicao(l);
                const bruto = r.linhas.reduce((s, x) => s + Number(x.entrada || 0), 0);
                const taxaGrupo = r.linhas.filter(foiMedido).reduce((s, x) => s + Number(x.taxa_maquininha || 0), 0);
                const liquido = bruto - taxaGrupo;
                // O hook pinta a <tr> de amarelo, mas cada celula abaixo fixa a PROPRIA cor (verde
                // do bruto, vermelho da taxa, cinza do secundario) e elas sumiriam sobre o
                // `#fde68a`. Na linha marcada o numero volta pro preto e o secundario pro tom
                // escuro do amarelo — a informacao segue nas colunas, nao na cor.
                const sel = grid.selectedId === r.chave;
                const fg = sel ? '#000' : undefined;
                const fgSec = sel ? '#6b5a1e' : 'var(--text-muted)';
                const rp = grid.rowProps(r);
                return (
                  <Fragment key={r.chave}>
                    <tr
                      {...rp}
                      onClick={() => { rp.onClick(); if (varios) alternar(r.chave); }}
                      title={varios
                        ? `${r.linhas.length} titulos de crediario baixados no mesmo dia, mesma forma, mesma conta e pelo mesmo operador — somados aqui porque o banco viu um lancamento so. O sistema nao registra quais baixas foram feitas juntas, entao este agrupamento e DEDUZIDO: clique para conferir titulo a titulo.`
                        : undefined}
                    >
                      <td style={{ fontSize: 12, color: fgSec }}>{horaRecebimento(l)}</td>
                      <td style={{ fontWeight: 600, fontSize: 12, color: fg }}>
                        {varios ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {aberto ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            {r.linhas.length} titulos
                          </span>
                        ) : l.documento}
                      </td>
                      <td className="text-gray-500 font-bold no-underline" style={{ color: fg }}>
                        {l.cliente_codigo ? String(l.cliente_codigo).padStart(4, '0') : '—'}
                      </td>
                      <td style={{ color: fg }}>{nomeClienteRecebimento(l)}</td>
                      <td style={{ color: fg }}>{celulaFormaFluxo(l)}</td>
                      <td style={{ color: fgSec }}>{l.caixa_nome || '—'}</td>
                      {/* Num grupo o vendedor e o da VENDA original, e cada titulo pode ter o seu —
                          mostrar so o do primeiro afirmaria que a baixa foi de um vendedor so. */}
                      <td style={{ color: fgSec }}>
                        {varios
                          ? ([...new Set(r.linhas.map((x) => x.vendedor).filter(Boolean))].length > 1
                            ? 'varios' : (l.vendedor || '—'))
                          : (l.vendedor || '—')}
                      </td>
                      <td className="cel-num" style={{ textAlign: 'right', color: fg ?? '#10b981', fontWeight: 600 }}>{formatMoeda(bruto)}</td>
                      <td className="cel-num" style={{ textAlign: 'right' }}>
                        {taxaGrupo > 0.005 ? (
                          <span style={{ color: fg ?? '#dc2626' }}>− {formatMoeda(taxaGrupo)}</span>
                        ) : semMed ? (
                          // Sobre o amarelo o `#c2410c` continua legivel e e o unico sinal de que a
                          // linha nao esta medida — por isso ele NAO vira preto na selecao.
                          <span
                            style={{ color: '#c2410c', fontWeight: 600, fontSize: 11 }}
                            title="Cartao sem captura da maquininha — a taxa existe, o sistema nao mediu quanto. O Liquido ao lado esta MAIOR que o que caiu de fato na conta."
                          >
                            nao medido
                          </span>
                        ) : (
                          <span style={{ color: fgSec }} title="Esta forma nao tem taxa de maquininha">—</span>
                        )}
                      </td>
                      <td
                        className="cel-num"
                        style={{ textAlign: 'right', fontWeight: 700, ...(semMed ? { color: '#c2410c' } : { color: fg }) }}
                        title={semMed ? 'Valor cheio: a taxa deste cartao nao foi medida, entao o que caiu na conta e menor' : undefined}
                      >
                        {formatMoeda(liquido)}
                      </td>
                    </tr>
                    {/* Os titulos que o grupo somou. Nao e' detalhe decorativo: e' o unico lugar em
                        que da pra conferir de que parcelas veio o valor conciliado. */}
                    {varios && aberto && r.linhas.map((x, i) => (
                      <tr key={`${r.chave}-${x.documento}-${i}`} style={{ backgroundColor: 'var(--panel-bg)', fontSize: 12 }}>
                        <td />
                        <td style={{ paddingLeft: 18, color: 'var(--text-muted)' }}>↳ {x.documento}</td>
                        <td />
                        <td style={{ color: 'var(--text-muted)' }}>{x.vendedor || '—'}</td>
                        <td />
                        <td />
                        <td />
                        <td className="cel-num" style={{ textAlign: 'right', color: '#10b981' }}>{formatMoeda(x.entrada)}</td>
                        <td />
                        <td />
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                <td colSpan={7} style={{ textAlign: 'right' }}>Totais</td>
                <td className="cel-num" style={{ textAlign: 'right', color: '#10b981' }}>{formatMoeda(t.bruto)}</td>
                <td className="cel-num" style={{ textAlign: 'right', color: t.taxa > 0.005 ? '#dc2626' : 'var(--text-muted)' }}>
                  {t.taxa > 0.005 ? <>− {formatMoeda(t.taxa)}</> : '—'}
                </td>
                <td className="cel-num" style={{ textAlign: 'right' }}>{formatMoeda(t.liquido)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// A baixa de crediario guarda `data_pagamento` como DATA (sem hora), entao o `ordem_ts` dela chega
// 00:00 — hora que ninguem digitou. Exibir "00:00" afirmaria um horario inexistente, pela mesma
// razao que a taxa nao medida nao vira traco: a coluna so mostra o que a fonte sabe.
function horaRecebimento(l: FluxoLinha): string {
  if (l.origem === 'cr') return '—';
  const m = String(l.ordem_ts).match(/[T ](\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '—';
}

// O historico do crediario ja vem "CREDIARIO - <cliente>" do backend. Repetir a palavra em toda
// linha rouba a leitura da coluna; ela vira etiqueta pequena e o nome fica com o peso normal.
//
// A etiqueta vem COLADA no nome, entre parenteses — nao antes dele (empurrava os nomes de crediario
// pra dentro e a coluna perdia a margem unica que o olho usa pra descer) e nao solta na direita da
// celula (ali ela lia como coluna propria, longe do nome que qualifica).
function nomeClienteRecebimento(l: FluxoLinha) {
  const h = l.historico || '';
  if (!h.startsWith('CREDIARIO - ')) return <>{h}</>;
  return (
    <>
      {h.slice('CREDIARIO - '.length)}{' '}
      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>(CREDIARIO)</span>
    </>
  );
}

// Pagamento que caiu num turno — devolvido por GET /turno/:id/pagamentos. E a lista que faz a
// correcao de caixa alcancar QUALQUER dia; a "Vendas hoje" e fixa em hoje no backend.
interface PagamentoDoTurno {
  id: number;
  valor: string;
  caixa_id: number | null;
  venda_id: number;
  venda_numero: string;
  venda_status: string;
  finalizada_em: string;
  cliente_nome: string;
  forma_pagamento_id: number;
  forma_nome: string;
  forma_tipo: string;
  vendedor_nome: string | null;
  operador_nome: string | null;
  ja_corrigido: string | number;
}

function TurnosTab({ inicio, fim, caixaId, caixas, rel, loading, onInicio, onFim, onCaixaId, onAtualizar, onHoje, podeCorrigir, onCorrigirCaixa }: {
  inicio: string; fim: string; caixaId: number | ''; caixas: CaixaConta[];
  rel: TurnosResp | null; loading: boolean;
  onInicio: (v: string) => void; onFim: (v: string) => void; onCaixaId: (v: number | '') => void;
  onAtualizar: () => void; onHoje: () => void;
  podeCorrigir: boolean; onCorrigirCaixa: (p: PagamentoMovivel[]) => void;
}) {
  // Vendas do turno, buscadas SOB DEMANDA (undefined = nao buscou, null = carregando).
  const [expandido, setExpandido] = useState<Set<number>>(() => new Set());
  const [pagsPorTurno, setPagsPorTurno] = useState<Record<number, PagamentoDoTurno[] | null>>({});

  async function alternar(t: { id: number; caixa_nome: string }) {
    const novo = new Set(expandido);
    if (novo.has(t.id)) { novo.delete(t.id); setExpandido(novo); return; }
    novo.add(t.id);
    setExpandido(novo);
    if (pagsPorTurno[t.id] !== undefined) return;
    setPagsPorTurno((p) => ({ ...p, [t.id]: null }));
    try {
      const r = await api.get<PagamentoDoTurno[]>(`/api/caixa/turno/${t.id}/pagamentos`);
      setPagsPorTurno((p) => ({ ...p, [t.id]: r }));
    } catch {
      setPagsPorTurno((p) => ({ ...p, [t.id]: [] }));
    }
  }

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>De</label>
          <input type="date" value={inicio} onChange={(e) => onInicio(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>Ate</label>
          <input type="date" value={fim} onChange={(e) => onFim(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>Caixa</label>
          <select value={String(caixaId)} onChange={(e) => onCaixaId(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">Todos</option>
            {caixas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        <button type="button" className="btn-secundario" onClick={onHoje}>Hoje</button>
        <button type="button" className="btn-primario" onClick={onAtualizar}>
          <RefreshCw size={14} style={{ marginRight: 4, marginBottom: -2 }} /> Atualizar
        </button>
      </div>

      {/* Totais */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
        <Kpi label="Turnos" valor={rel?.totais.qtd ?? 0} unidade="qtd" />
        <Kpi label="Com diferenca" valor={rel?.totais.qtd_com_divergencia ?? 0} unidade="qtd" />
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: '4px solid #f59e0b', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Faltou (total)</div>
          <div className="cel-num" style={{ fontSize: 22, fontWeight: 700 }}>{formatMoeda(rel?.totais.faltou ?? 0)}</div>
        </div>
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderLeft: '4px solid var(--primary)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Sobrou (total)</div>
          <div className="cel-num" style={{ fontSize: 22, fontWeight: 700 }}>{formatMoeda(rel?.totais.sobrou ?? 0)}</div>
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando turnos...</div>
      ) : !rel || rel.turnos.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum turno no periodo</div>
      ) : (
        <table className="tabela" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ width: 36, textAlign: 'center' }}>#</th>
              <th style={{ width: 100 }}>Data</th>
              <th>Caixa</th>
              <th>Abriu</th>
              <th>Fechou</th>
              <th style={{ textAlign: 'right', width: 110 }}>Abertura</th>
              <th style={{ textAlign: 'right', width: 110 }}>Esperado</th>
              <th style={{ textAlign: 'right', width: 110 }}>Contado</th>
              <th style={{ textAlign: 'right', width: 110 }}>Diferenca</th>
              <th>Passou para</th>
            </tr>
          </thead>
          <tbody>
            {rel.turnos.map((t) => {
              const aberto = t.status === 'aberto';
              const div = t.divergencia != null ? Number(t.divergencia) : null;
              const divAb = t.divergencia_abertura != null ? Number(t.divergencia_abertura) : null;
              const aguardandoConf = aberto && !t.abertura_confirmada_em;
              const aberta = expandido.has(t.id);
              const pags = pagsPorTurno[t.id];
              return (
                <Fragment key={t.id}>
                <tr>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="btn-icon"
                      title={aberta ? 'Esconder as vendas do turno' : 'Ver as vendas que cairam neste turno'}
                      onClick={() => void alternar(t)}
                    >
                      {aberta ? '−' : '+'}
                    </button>
                  </td>
                  <td>{fmtDataFluxo(t.data)}</td>
                  <td style={{ fontWeight: 600 }}>{t.caixa_nome}</td>
                  <td>
                    {t.operador_abertura_nome || '—'}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatHoraSP(t.aberto_em)}</div>
                    {aguardandoConf && (
                      <div style={{ fontSize: 11, color: '#000', backgroundColor: '#fde68a', borderRadius: 4, padding: '0 6px', display: 'inline-block', marginTop: 2 }}>aguardando conferência</div>
                    )}
                    {divAb != null && Math.abs(divAb) >= 0.005 && (
                      <div style={{ fontSize: 11, color: '#000', backgroundColor: '#f59e0b', borderRadius: 4, padding: '0 6px', display: 'inline-block', marginTop: 2 }}>
                        passagem: {divAb > 0 ? 'sobrou' : 'faltou'} {formatMoeda(Math.abs(divAb))}
                      </div>
                    )}
                  </td>
                  <td>
                    {aberto
                      ? <span className="badge-verde">Aberto</span>
                      : t.operador_fechamento_nome
                        ? <>{t.operador_fechamento_nome}
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.fechado_em ? formatHoraSP(t.fechado_em) : '—'}</div>
                          </>
                        : <><span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>automático</span>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>sem conferência{t.fechado_em ? ` · ${formatHoraSP(t.fechado_em)}` : ''}</div>
                          </>}
                  </td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>{formatMoeda(t.valor_abertura)}</td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>{t.valor_esperado_fechamento != null ? formatMoeda(t.valor_esperado_fechamento) : '—'}</td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>{t.valor_fechamento != null ? formatMoeda(t.valor_fechamento) : '—'}</td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>
                    {div == null ? '—' : Math.abs(div) < 0.005
                      ? <span style={{ color: 'var(--text-muted)' }}>ok</span>
                      : <span style={{ backgroundColor: '#f59e0b', color: '#000', fontWeight: 700, padding: '1px 6px', borderRadius: 4 }}>
                          {div > 0 ? '+' : '−'}{formatMoeda(Math.abs(div))}
                        </span>}
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>{t.sucessor_nome || '—'}</td>
                </tr>
                {aberta && (
                  <tr style={{ background: 'rgba(59, 130, 246, 0.05)' }}>
                    <td colSpan={10} style={{ padding: 0 }}>
                      <div style={{ padding: '12px 32px' }}>
                        {pags === null || pags === undefined ? (
                          <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Carregando vendas...</div>
                        ) : pags.length === 0 ? (
                          <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Nenhuma venda caiu neste turno</div>
                        ) : (
                          <table style={{ width: '100%', fontSize: 13, background: 'var(--panel-bg, #fff)', borderRadius: 6, border: '1px solid var(--input-border, #e5e7eb)' }}>
                            <thead>
                              <tr style={{ background: 'var(--grid-header-bg, #f3f4f6)', color: 'var(--grid-header-fg, #4b5563)' }}>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Venda</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Cliente</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Hora</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Forma</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Operador</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Vendedor</th>
                                <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 600, fontSize: 11 }}>Valor</th>
                                <th style={{ textAlign: 'right', padding: '6px 12px', width: 80, fontWeight: 600, fontSize: 11 }}>Acoes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pags.map((p, idx) => (
                                <tr key={p.id} style={{ borderTop: idx > 0 ? '1px solid var(--input-border, #f3f4f6)' : undefined }}>
                                  <td style={{ padding: '6px 12px', fontWeight: 600 }}>
                                    {p.venda_numero}
                                    {Number(p.ja_corrigido) > 0 && (
                                      <span style={{ marginLeft: 6, fontSize: 10, backgroundColor: '#fde68a', color: '#000', borderRadius: 4, padding: '0 5px' }}>corrigida</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '6px 12px' }}>{p.cliente_nome}</td>
                                  <td style={{ padding: '6px 12px', fontSize: 11 }}>{formatHoraSP(p.finalizada_em)}</td>
                                  <td style={{ padding: '6px 12px' }}>{p.forma_nome}</td>
                                  <td style={{ padding: '6px 12px' }}>{p.operador_nome || '—'}</td>
                                  <td style={{ padding: '6px 12px' }}>{p.vendedor_nome || '—'}</td>
                                  <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>{formatMoeda(p.valor)}</td>
                                  <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                                    {podeCorrigir && p.venda_status === 'finalizada' && (
                                      <button
                                        type="button"
                                        className="btn-icon"
                                        title="Esta venda nao e deste caixa — mover pro certo"
                                        style={{ color: 'var(--primary)' }}
                                        onClick={() => onCorrigirCaixa([{
                                          id: p.id,
                                          venda_numero: p.venda_numero,
                                          cliente_nome: p.cliente_nome,
                                          forma_nome: p.forma_nome,
                                          forma_pagamento_id: p.forma_pagamento_id,
                                          valor: Number(p.valor || 0),
                                          caixa_id: p.caixa_id,
                                          caixa_nome: t.caixa_nome,
                                          finalizada_em: p.finalizada_em,
                                        }])}
                                      >
                                        <ArrowLeftRight size={14} />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Reexports pra ModalFinalizarVenda
export type { PedidoCaixa, CaixaConta };
